/**
 * OTSmartDetail · /ot-smart/:odooTaskId
 * Detalle de una OT SMART (project_task de Odoo + smart_ot interno).
 * Incluye: datos del cliente, GPS en mapa, suscripción, equipo, historial,
 * y drawer de asignación de técnico.
 *
 * v2.0.0 — Sprint 1
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import CancelReasonPicker from "../components/CancelReasonPicker";   // v101.188.0
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import BoxPolesLayer from "../components/BoxPolesLayer";   // v101.114.0 · cajas como postes
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import OTActionMenu from "../components/OTActionMenu";
import FibiOTCard from "../components/FibiOTCard";   // v101.196.0
import FibiWhatsAppBoton from "../components/FibiWhatsAppBoton";   // v101.200.0
import OTWhatsAppPanel from "../components/OTWhatsAppPanel";   // v101.267.0 · WhatsApp a la derecha
import { CloseReasonDialog, CloseReasonCard, fetchCloseReasons } from "../components/CloseReasonPicker";   // v101.101.0
import CloseProgressModal from "../components/CloseProgressModal";   // v101.246.0
import {
  ArrowLeft, User, MapPin, Building2, Package, Phone, Mail, IdCard,
  Wifi, WifiOff, Loader2, RefreshCw, Calendar, UserPlus, PlayCircle,
  CheckCircle2, XCircle, MoreHorizontal, History, Zap, Server,
  Printer, MessageSquare, Workflow, RotateCcw, Cpu,
  Edit3, Clock, ExternalLink, Save,
  AlertTriangle,
  Search,
  Check,
  Trash2,
  PackagePlus,
  Plus,
  X,
  ChevronLeft, ChevronRight, CloudDownload,
  Info,
  FileText,   // v101.128.0
} from "lucide-react";

/** v101.46.1 · Estado visual del sync de cierre/aprovisionamiento hacia Odoo */
function odooSyncVisual(ot) {
  if (!ot) return null;
  const close = String(ot.close_event_status || "").toLowerCase();
  const sync = String(ot.odoo_sync_status || "").toLowerCase();
  const ws = String(ot.odoo_ws_event_status || "").toLowerCase();
  const msg = (ot.close_event_message || "").trim();
  const st = String(ot.master_internal_status || ot.smart?.internal_status || "").toLowerCase();
  // v101.162.0 · Cancelación / etapa de Odoo (mismas reglas que Integraciones › Sincronización con Odoo)
  const odooCancel = [20, 21].includes(Number(ot.stage_id_odoo || 0)) || String(ot.stage_name || "").toLowerCase().includes("cancel");
  const cst = String(ot.cancel_event_status || "").toLowerCase();
  if (["cancelada", "cancelled"].includes(st)) {
    if (odooCancel || cst === "completado") return { kind: "ok", label: "Odoo · cancelada", title: "Cancelada también en Odoo" };
    if (cst.startsWith("error")) return { kind: "error", label: "Odoo · cancelación rechazada", title: ot.cancel_event_message || cst };
    if (cst === "pendiente_sync") return { kind: "pending", label: "Odoo · cancelación pendiente", title: ot.cancel_event_message || "Pendiente de enviar" };
    return { kind: "warn", label: "Odoo · cancelación no enviada", title: "En Odoo la tarea sigue abierta" };
  }
  if (odooCancel) return { kind: "error", label: "Odoo · cancelada (aquí abierta)", title: "Odoo canceló la tarea; en AlfaField sigue abierta" };
  const inCloseLane = ["completada", "completed", "finalizada", "solucionada",
    "fiscalizada_pendiente_cierre", "pendiente_fiscalizacion", "solucionada_pendiente_fiscalizacion"].includes(st);

  if (close === "completado" || sync === "sincronizada") {
    return { kind: "ok", label: "Odoo · cierre OK", title: msg || "Cierre confirmado por Odoo" };
  }
  if (["error_negocio", "error_validacion"].includes(close)) {
    return { kind: "error", label: "Odoo · cierre falló", title: msg || `Error: ${close}` };
  }
  if (close === "pendiente_sync" || sync === "pendiente_sync") {
    return { kind: "pending", label: "Odoo · pendiente", title: msg || "Cierre pendiente de sincronizar" };
  }
  if (inCloseLane && !close && !sync) {
    return { kind: "warn", label: "Sin envío a Odoo", title: "No hay registro de cierre enviado a Odoo" };
  }
  if (ws === "sync_ok") {
    return { kind: "ok", label: "Odoo · aprovisionado", title: "Aprovisionamiento OK en Odoo" };
  }
  if (["error", "error_negocio", "failed", "fallido"].includes(ws)) {
    return { kind: "error", label: "Odoo · aprovisionamiento ✗", title: "Fallo al enviar aprovisionamiento" };
  }
  return null;
}

const clientIcon = L.divIcon({
  className: "",
  html: `<div style="background:#0A3747;color:#E0E621;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.4)"><span style="transform:rotate(45deg);font-weight:bold;font-size:14px">C</span></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
});

const STATUS_STYLES = {
  pending:     { label: "Pendiente",   bg: "bg-slate-100",  fg: "text-slate-700",   dot: "bg-slate-400" },
  assigned:    { label: "Asignada",    bg: "bg-blue-100",   fg: "text-blue-800",    dot: "bg-blue-500" },
  manual_assignment_required: { label: "En revisión", bg: "bg-amber-100", fg: "text-amber-800", dot: "bg-amber-500" }, // v101.14.0
  in_progress: { label: "En progreso", bg: "bg-amber-100",  fg: "text-amber-800",   dot: "bg-amber-500" },
  on_site:     { label: "En sitio",    bg: "bg-cyan-100",   fg: "text-cyan-800",    dot: "bg-cyan-500" },
  // v101.55.0 · Carril Fiscalización (terminada en app; pendiente de validar/cerrar o de reenviar a Odoo)
  fiscalizada_pendiente_cierre: { label: "Fiscalización", bg: "bg-orange-100", fg: "text-orange-800", dot: "bg-orange-500" },
  completed:   { label: "Completada",  bg: "bg-emerald-100",fg: "text-emerald-800", dot: "bg-emerald-500" },
  cancelled:   { label: "Cancelada",   bg: "bg-red-100",    fg: "text-red-800",     dot: "bg-red-500" },
};

// v101.18.0 · Origen legible de cada cambio de estado (master_ot_status_log.source)
const ORIGEN_LABEL = (src) => {
  const s = String(src || "");
  const map = {
    odoo_sync: "Sync Odoo (alta)", migracion_v101_18: "Unificación de estados", estado_al_activar: "Estado inicial",
    web_asignar: "Web · Asignar/Reasignar", web_estado: "Web · Botón de estado", web_backfill: "Web · Rescate",
    web_agenda_forzada: "Web · Agenda forzada", web_enviar_revision: "Web · Enviar a revisión", web_limpiar_infra: "Web · Limpiar infra",
    app_tecnico: "App del técnico", app_fiscalizacion: "App · Fiscalización", fiscalizacion: "Fiscalización",
    A2_autoagenda: "A2 · Autoagendamiento", A2_rechazo: "A2 · Sin técnico (a Revisión)", A2_agenda_manual: "A2 · Agenda manual",
    A2_aplicar: "A2 · Propuesta aplicada", A3_evento_masivo: "A3 · Evento masivo", A5_sla_vencido: "A5 · SLA vencido",
    A5_solicitud_cambio: "A5 · Solicitud de cambio", A5_reprogramada: "A5 · Reprogramada", A5_sin_cupo: "A5 · Sin cupo",
    evento_masivo: "Evento masivo", evento_masivo_fin: "Fin de evento masivo", arbol_soluciones: "Árbol de soluciones",
    script_requeue: "Script · Devolver a cola", sin_origen: "Sin origen registrado",
  };
  if (map[s]) return map[s];
  if (s.startsWith("web_carril:")) return `Web · Cambio de carril (${s.split(":")[1]})`;
  if (s.startsWith("agente_")) return `Agente ${s.slice(7)}`;
  return s || "—";
};

// v101.19.1 · Tarea hija: Nuevo + asignada/ejecución + fiscalización/validación (master_ot o EN del detalle)
function otAllowsChildTask(smart) {
  const raw = (smart?.master_internal_status || smart?.internal_status || "").trim().toLowerCase();
  return new Set([
    "pendiente_agendamiento", "pending", "pendiente", "nueva",
    "asignada", "assigned", "agendada", "confirmada",
    "en_ruta", "en_ejecucion", "in_progress", "on_site", "en_sitio", "en_camino",
    "solucionada_pendiente_fiscalizacion", "pendiente_fiscalizacion", "fiscalizada_pendiente_cierre",
    "validada",
  ]).has(raw);
}

const NEXT_ACTIONS = {
  pending:     [{ to: "assigned", label: "Asignar técnico", icon: UserPlus }, { to: "cancelled", label: "Cancelar", icon: XCircle }],
  // v101.45.1 · En revisión: backend ya permite cancelled (STATE_MACHINE); sin esta clave el botón no se pintaba
  manual_assignment_required: [{ to: "cancelled", label: "Cancelar", icon: XCircle }],
  assigned:    [{ to: "in_progress", label: "Iniciar", icon: PlayCircle }, { to: "cancelled", label: "Cancelar", icon: XCircle }],
  in_progress: [{ to: "on_site", label: "En sitio", icon: MapPin }, { to: "completed", label: "Completar", icon: CheckCircle2 }, { to: "cancelled", label: "Cancelar", icon: XCircle }],
  on_site:     [{ to: "completed", label: "Completar", icon: CheckCircle2 }, { to: "cancelled", label: "Cancelar", icon: XCircle }],
  // v101.55.0 · Fiscalización: Validar/Cerrar (botón verde) envía el cierre a Odoo; aquí solo Cancelar
  fiscalizada_pendiente_cierre: [{ to: "cancelled", label: "Cancelar", icon: XCircle }],
  completed:   [],
  cancelled:   [],   // v101.260.0 · sin «Reabrir»
};

/* ═════════════════════════════════════════════════════════════════════
 * v4.2.2 · Helpers Ficha 360°
 * ═════════════════════════════════════════════════════════════════════ */

/** Transforma HTML de Odoo a texto legible (elimina tags, preserva saltos). */
function stripHtml(html) {
  if (!html) return "";
  if (typeof html !== "string") return String(html);
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/* v101.93.0 · Informe técnico (PDF): ver en pestaña nueva o DESCARGAR (la descarga es solo desde la web) */
async function openInformePdf(sid, download = false) {
  const win = download ? null : window.open("", "_blank");
  try {
    const { data } = await api.get(`/v1/fiscalizacion/submissions/${sid}/pdf-link`, { params: download ? { download: 1 } : {} });
    const url = data?.url;
    if (!url) throw new Error("sin enlace");
    if (download) {
      const a = document.createElement("a");
      a.href = url; a.rel = "noreferrer"; document.body.appendChild(a); a.click(); a.remove();
      toast.success("Descargando informe técnico…");
    } else if (win) win.location.href = url; else window.open(url, "_blank");
  } catch (e) {
    if (win) win.close();
    toast.error(e?.response?.data?.detail || "No se pudo abrir el informe PDF");
  }
}

/** Panel de auditoría de fiscalización · muestra fotos + OCR/IA + checklist + aprobar/rechazar. */
function AuditFiscalizationModal({ ot, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // v101.31.0 · Endpoint real: lo que el técnico envió desde la app (fotos firmadas + IA + campos)
        const { data } = await api.get(`/v1/ot_smart/${ot.odoo_task_id}/fiscalization`);
        setPayload(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar la fiscalización");
        setPayload({ photos: [], checklist: [], ai_verdict: null });
      } finally { setLoading(false); }
    })();
  }, [ot.odoo_task_id]);

  const decide = async (verdict) => {
    if (!note.trim() && verdict === "rejected") {
      toast.error("Escribí una nota explicando por qué se rechaza"); return;
    }
    setSubmitting(true);
    try {
      await api.post(`/v1/ot_smart/${ot.odoo_task_id}/fiscalization/decision`, { verdict, note });
      toast.success(verdict === "approved" ? "Fiscalización aprobada" : "Fiscalización rechazada");
      onDone && onDone(verdict);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la decisión");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 bg-amber-50 flex items-center justify-between rounded-t-2xl">
          <div>
            <div className="text-[10px] font-mono text-amber-800 uppercase tracking-widest">AUDITAR FISCALIZACIÓN</div>
            <div className="text-sm font-bold text-amber-900">OT {ot.afc_code || ot.odoo_task_id} · {ot.partner_name}</div>
          </div>
          <div className="flex items-center gap-2">
            {/* v101.93.0 · Informe técnico (PDF) */}
            {payload?.submission_id && (
              <>
                <button onClick={() => openInformePdf(payload.submission_id, false)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold border border-slate-300 bg-white hover:bg-slate-50"
                        data-testid="audit-pdf-view">Ver informe PDF</button>
                <button onClick={() => openInformePdf(payload.submission_id, true)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-[#0A3747] hover:opacity-90"
                        data-testid="audit-pdf-download">Descargar PDF</button>
              </>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl px-2">×</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-slate-400 text-sm">Cargando fiscalización…</div>
          ) : (
            <>
              {/* v101.31.0 · Estado del envío y última decisión */}
              {payload && payload.exists === false && (
                <div className="rounded-xl border p-3 bg-slate-50 border-slate-200 text-xs text-slate-600">
                  El técnico todavía no envió el formulario de autofiscalización desde la app para esta OT.
                </div>
              )}
              {payload?.submitted_at && (
                <div className="text-[11px] text-slate-500">
                  Enviado desde la app: {new Date(payload.submitted_at).toLocaleString("es-EC")} · estado {payload.status || "—"}
                </div>
              )}
              {payload?.last_decision && (
                <div className={`rounded-xl border p-3 text-xs ${payload.last_decision.verdict === "approved" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`}>
                  Última decisión: <b>{payload.last_decision.verdict === "approved" ? "Aprobada" : "Rechazada"}</b>
                  {" · "}{payload.last_decision.decided_by_email}{" · "}{payload.last_decision.decided_at ? new Date(payload.last_decision.decided_at).toLocaleString("es-EC") : ""}
                  {payload.last_decision.note ? <div className="mt-1 italic">«{payload.last_decision.note}»</div> : null}
                </div>
              )}
              {payload?.missing_photos?.length > 0 && (
                <div className="rounded-xl border p-3 bg-red-50 border-red-200 text-xs text-red-900">
                  <b>Fotos obligatorias que no llegaron ({payload.missing_photos.length}):</b>{" "}
                  {payload.missing_photos.map((m) => `${m.label}${m.section ? ` (${m.section})` : ""}`).join(" · ")}
                </div>
              )}
              {/* Veredicto IA */}
              {payload?.ai_verdict && (
                <div className="rounded-xl border p-3 bg-purple-50 border-purple-200">
                  <div className="text-[10px] font-mono text-purple-700 uppercase tracking-widest mb-1">Veredicto IA (Nano Banana)</div>
                  <div className="text-sm text-purple-900 font-semibold">
                    {payload.ai_verdict.status || "—"}: {payload.ai_verdict.summary || payload.ai_verdict.verdict}
                  </div>
                  {payload.ai_verdict.confidence != null && (
                    <div className="text-[11px] text-purple-700 mt-0.5">Confianza: {(payload.ai_verdict.confidence * 100).toFixed(0)}%</div>
                  )}
                </div>
              )}
              {/* Checklist */}
              {payload?.checklist?.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">Checklist ({payload.checklist.length})</div>
                  <div className="space-y-1.5">
                    {payload.checklist.map((c, i) => (
                      <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded border ${c.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-red-50 border-red-200 text-red-900"}`}>
                        <span className="mt-0.5">{c.ok ? "✓" : "✗"}</span>
                        <span className="flex-1">{c.label || c.name}</span>
                        {c.value && <span className="font-mono text-[10px] opacity-70">{String(c.value).slice(0, 40)}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Fotos + OCR */}
              <div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mb-2">
                  Fotos ({(payload?.photos || []).length})
                </div>
                {(payload?.photos || []).length === 0 ? (
                  <div className="text-xs text-slate-400 italic">Sin fotos registradas.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {payload.photos.map((p, i) => (
                      <div key={i} className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                        <a href={p.url || p.uri || p.image_url} target="_blank" rel="noreferrer"><img
                          src={p.url || p.uri || p.image_url}
                          alt={p.label || `Foto ${i + 1}`}
                          className="w-full h-40 object-cover"
                          onError={(e) => { e.target.style.display = "none"; }}
                        /></a>
                        <div className="p-2">
                          <div className="text-[11px] font-semibold text-slate-800 truncate">{p.label || p.field_key || `Foto ${i + 1}`}</div>
                          {p.ocr_text && (
                            <div className="text-[10px] text-slate-500 mt-0.5 font-mono line-clamp-2" title={p.ocr_text}>
                              OCR: {p.ocr_text}
                            </div>
                          )}
                          {p.ai_status && (
                            <div className={`text-[10px] font-bold mt-0.5 ${p.ai_status === "OK" ? "text-emerald-600" : "text-amber-600"}`}>
                              IA: {p.ai_status}
                            </div>
                          )}
                          {p.ai_reason && (
                            <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2" title={p.ai_reason}>{p.ai_reason}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {/* Footer decisión */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl space-y-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota del coordinador (obligatoria si rechazás)…"
            rows={2}
            className="w-full text-xs px-3 py-2 border border-slate-300 rounded-lg focus:border-[#0A3747] focus:outline-none"
            data-testid="audit-note"
          />
          <div className="flex gap-2">
            <button
              onClick={() => decide("approved")}
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="audit-approve"
            >
              ✓ Aprobar fiscalización
            </button>
            <button
              onClick={() => decide("rejected")}
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="audit-reject"
            >
              ✗ Rechazar
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-white">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// v101.165.0 · Traslado: ¿la serie elegida es el MISMO equipo del cliente? Compara PON (16 hex) y serie de fábrica
//              contra todas las formas conocidas del origen. Si ninguna forma del origen es convertible a PON (p. ej.
//              solo la etiqueta del router) no se puede comparar: no se bloquea (lo decide el backend).
const _trHex = (x) => /^[0-9A-F]{16}$/.test(x);
const _trToPon = (raw) => {
  const x = String(raw || "").replace(/\s/g, "").toUpperCase();
  if (_trHex(x)) return x;
  const m = x.replace(/-/g, "").match(/^([A-Z0-9]{4})([0-9A-F]{8})$/);
  if (!m) return null;
  return m[1].split("").map((c) => c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")).join("") + m[2];
};
const _trCompact = (raw) => String(raw || "").replace(/[\s-]/g, "").toUpperCase();
function trasladoSameEquipment(originAliases, candidates, strict = false) {
  const origin = (originAliases || []).filter(Boolean);
  const cands = (candidates || []).filter((c) => String(c || "").trim());
  if (!origin.length || !cands.length) return !strict;
  for (const o of origin) {
    for (const c of cands) {
      if (_trCompact(o) === _trCompact(c)) return true;
      const po = _trToPon(o), pc = _trToPon(c);
      if (po && pc && po === pc) return true;
    }
  }
  if (!strict && !origin.some((o) => _trToPon(o)) && cands.some((c) => _trToPon(c))) return true;
  return false;
}

export default function OTSmartDetail() {
  const { odooTaskId } = useParams();
  const navigate = useNavigate();
  const { hasPerm, user: authUser } = useAuth();
  const canEdit = hasPerm ? hasPerm("M1.edit") : false;
  const canAssign = hasPerm ? hasPerm("M1.assign") : false;
  const canValidate = (hasPerm ? hasPerm("M1.validate") : false) || ["agendador", "scheduler"].includes(authUser?.role);
  const [pushingOdoo, setPushingOdoo] = useState(false); // v101.54.0
  const [validatingClose, setValidatingClose] = useState(false); // v101.50.2 · spinner Validar/Cerrar (espera Odoo)
  // v101.101.0 · Motivo de cierre: se pide antes de Validar/Cerrar si falta
  const [showCloseReason, setShowCloseReason] = useState(false);
  const [closeReasonKey, setCloseReasonKey] = useState(0);
  const canPrint = hasPerm ? hasPerm("M1.print") : false;
  // v101.128.0 · ¿Hay informe de autofiscalización? (botón «Informe PDF», solo lectura)
  const [informe, setInforme] = useState(null);
  useEffect(() => {
    let alive = true;
    setInforme(null);
    if (!odooTaskId) return undefined;
    api.get(`/v1/ot_smart/${odooTaskId}/informe-pdf`)
      .then(({ data }) => { if (alive) setInforme(data); })
      .catch(() => { if (alive) setInforme(null); });
    return () => { alive = false; };
  }, [odooTaskId]);
  const canExport = hasPerm ? hasPerm("M1.export") : false;
  const canComment = hasPerm ? hasPerm("M1.comment") : false;
  const canChangeSolutionType = ["super_admin", "supervisor", "admin", "admin_zone", "agendador"].includes(authUser?.role);
  // v101.7.0 · Agendador/supervisor puede escribir GPS del cliente en Odoo
  const canUpdateOdooCoords = ["super_admin", "supervisor", "admin", "admin_zone", "agendador", "scheduler", "soporte_tecnico"].includes(authUser?.role);
  // v101.14.0 · Crear tarea hija en Odoo (rol agendador/admin)
  const canCreateChildRole = (hasPerm ? hasPerm("M1.edit") : false) &&
    ["super_admin", "supervisor", "admin", "admin_zone", "agendador", "scheduler"].includes(authUser?.role);
  const [loading, setLoading] = useState(true);
  const [ot, setOt] = useState(null);
  const [showAudit, setShowAudit] = useState(false);   // v4.2.2 · Panel auditoría fiscalización
  const [activeTab, setActiveTab] = useState("general"); // v4.2.2 · Tabs de la ficha 360

  const [showAssign, setShowAssign] = useState(false);
  const [odooRefreshing, setOdooRefreshing] = useState(false); // v101.13.0
  const [outsideShiftData, setOutsideShiftData] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false); // v101.30.0 · Cancelar OT: solo pide comentario
  const [showChangeSolution, setShowChangeSolution] = useState(false);
  const [techs, setTechs] = useState([]);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  // v101.4.0 · Edición inline del nombre de tarea (agendador / M1.edit)
  const [editingTaskName, setEditingTaskName] = useState(false);
  const [taskNameDraft, setTaskNameDraft] = useState("");
  const [taskNameSaving, setTaskNameSaving] = useState(false);
  // v4.2.4 · Historial de agentes IA
  const [agentHistory, setAgentHistory] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  // v101.7.0 · Formulario actualizar coords → Odoo
  const [coordLat, setCoordLat] = useState("");
  const [coordLon, setCoordLon] = useState("");
  const [coordReason, setCoordReason] = useState("");
  const [coordSaving, setCoordSaving] = useState(false);
  // v101.14.0 · Modal crear tarea hija
  const [showCreateChild, setShowCreateChild] = useState(false);
  // v101.16.0 · Wizard GPS→Odoo (antes card bajo el mapa)
  const [showGpsWizard, setShowGpsWizard] = useState(false);
  // v101.96.0 · Wizard Dirección→Odoo (mismo proceso que el GPS)
  const [showAddrWizard, setShowAddrWizard] = useState(false);
  const [addrStreet, setAddrStreet] = useState("");
  const [addrStreet2, setAddrStreet2] = useState("");
  const [addrReason, setAddrReason] = useState("");
  const [addrSaving, setAddrSaving] = useState(false);
  // v101.46.1 · Navegación prev/next dentro del mismo carril Kanban
  const [laneNav, setLaneNav] = useState({ prev: null, next: null, lane: null, lane_name: null });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/ot_smart/${odooTaskId}`);
      setOt(data);
      // v101.7.0 · Prefill del formulario GPS→Odoo
      if (data?.partner_lat != null && data?.partner_lon != null) {
        setCoordLat(String(data.partner_lat));
        setCoordLon(String(data.partner_lon));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OT no encontrada");
      if (e?.response?.status === 404) navigate("/ot-smart");
    } finally {
      setLoading(false);
    }
  };
  const reload = load; // v4.2.2 · alias para botones de acción

  const loadTechs = async () => {
    try {
      const { data } = await api.get("/v1/ot_smart/technicians");
      setTechs(data.items || []);
    } catch (e) {
      /* silent */
    }
  };

  const loadLaneNeighbors = async () => {
    if (!odooTaskId) return;
    try {
      const { data } = await api.get(`/v1/ot_smart/${odooTaskId}/lane-neighbors`);
      setLaneNav({
        prev: data?.prev || null,
        next: data?.next || null,
        lane: data?.lane || null,
        lane_name: data?.lane_name || null,
      });
    } catch {
      setLaneNav({ prev: null, next: null, lane: null, lane_name: null });
    }
  };

  useEffect(() => { load(); loadTechs(); loadLaneNeighbors(); /* eslint-disable-next-line */ }, [odooTaskId]);

  // v4.2.4 · Carga historial de agentes IA
  useEffect(() => {
    if (!odooTaskId) return;
    setAgentLoading(true);
    api.get(`/v1/ot_smart/${odooTaskId}/agent-history`)
      .then(({ data }) => setAgentHistory(data))
      .catch(() => setAgentHistory({ events: [], last_action: null, count: 0 }))
      .finally(() => setAgentLoading(false));
  }, [odooTaskId]);

  // v101.4.0 · Bloquear UI solo si no hay solución ni código de actividad (backend aún puede auto-resolver)
  const openAssign = () => {
    if (!ot?.solution_node_id && !ot?.solution_type_name && !(ot?.activity_type_code || "").trim()) {
      toast.error("Asigne un tipo de solución antes de asignar técnico");
      if (canChangeSolutionType) setShowChangeSolution(true);
      return;
    }
    setShowAssign(true);
  };

  const saveTaskName = async () => {
    const next = (taskNameDraft || "").trim();
    if (!next) {
      toast.error("El nombre de la tarea no puede quedar vacío");
      return;
    }
    if (next === (ot?.task_name || "")) {
      setEditingTaskName(false);
      return;
    }
    setTaskNameSaving(true);
    try {
      // v101.4.0 · PUT /edit ya soporta task_name con permiso M1.edit (agendador incluido)
      await api.put(`/v1/ot_smart/${odooTaskId}/edit`, { task_name: next });
      toast.success("Nombre de la tarea actualizado");
      setEditingTaskName(false);
      setOt((prev) => (prev ? { ...prev, task_name: next } : prev));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el nombre");
    } finally {
      setTaskNameSaving(false);
    }
  };

  // v101.13.0 · Releer la OT desde Odoo (solo carriles Nuevas, Infraestructura, Revisión y Asignadas)
  const refreshFromOdoo = async () => {
    if (odooRefreshing) return;
    setOdooRefreshing(true);
    try {
      const { data } = await api.post(`/v1/ot_smart/${odooTaskId}/refresh-from-odoo`);
      const ch = data?.changed || [];
      const extra = (data?.notes || []).join(" · ");
      if (data?.closed_in_odoo) toast.success(`En Odoo la OT está ${data.stage_name || "cerrada"}; sale de los carriles`);
      else if (ch.length) toast.success(`Actualizada desde Odoo · ${ch.join(", ")}${extra ? " · " + extra : ""}`);
      else toast.info(`Sin cambios en Odoo${extra ? " · " + extra : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar desde Odoo");
    } finally {
      setOdooRefreshing(false);
    }
  };

  // v101.9.0 · forceSubSplit: técnico distinto al de hermanas de la misma suscripción
  const doAssign = async (technicianId, priority, scheduledAt, notes, forceOutsideShift = false, forceReason = null, forceSubSplit = false) => {
    try {
      await api.post(`/v1/ot_smart/${odooTaskId}/assign`, {
        technician_id: technicianId,
        internal_priority: priority || null,
        scheduled_at: scheduledAt || null,
        notes: notes || null,
        force_outside_shift: forceOutsideShift,
        force_reason: forceReason || null,
        force_subscription_split: forceSubSplit,
      });
      toast.success(forceOutsideShift
        ? "Técnico asignado (fuera de turno, con motivo registrado en auditoría)"
        : forceSubSplit
          ? "Técnico asignado (split de suscripción forzado)"
          : "Técnico asignado correctamente");
      setShowAssign(false);
      setOutsideShiftData(null);
      load();
    } catch (e) {
      const resp = e?.response?.data;
      // v101.9.0 · OUTSIDE_SHIFT o SUBSCRIPTION_SIBLING_TECH → override con motivo
      if ((resp?.code === "OUTSIDE_SHIFT" || resp?.code === "SUBSCRIPTION_SIBLING_TECH") && resp?.can_force) {
        setOutsideShiftData({
          detail: resp.detail,
          code: resp.code,
          technicianId, priority, scheduledAt, notes,
        });
      } else {
        const detail = resp?.detail || "No se pudo asignar el técnico";
        toast.error(detail);
        // v101.4.0 · Si falta tipo de solución, abrir selector
        if (typeof detail === "string" && detail.toLowerCase().includes("tipo de solución") && canChangeSolutionType) {
          setShowAssign(false);
          setShowChangeSolution(true);
        }
      }
    }
  };

  const doStatus = async (toStatus, notes = null, cancelReasons = null) => {
    if (toStatus === "assigned") {
      openAssign();
      return;
    }
    // v101.50.2 · Validar/Cerrar: deshabilitar + spinner (mismo patrón que Reenviar cierre a Odoo)
    const isClose = toStatus === "completed";
    if (isClose) {
      if (validatingClose) return;
      setValidatingClose(true);
    }
    try {
      // v101.46.0 · Validar/Cerrar espera OK de Odoo; si falla, toast con el motivo y no cambia de carril
      const { data } = await api.post(
        `/v1/ot_smart/${odooTaskId}/status`,
        { to_status: toStatus, notes, ...(cancelReasons && cancelReasons.length ? { cancel_reasons: cancelReasons } : {}) },
        isClose ? { timeout: 90000 } : undefined,
      );
      if (isClose && data?.odoo_close?.ok) {
        toast.success(
          data?.odoo_close?.idempotent
            ? "Cierre ya estaba en Odoo (OK) · OT en Realizadas/Cierre"
            : "Cierre OK en Odoo · OT en Realizadas/Cierre",
        );
        // v101.86.0 · equipo aprovisionado ≠ descargado: solo advertencia
        if (data?.odoo_close?.warning) toast.warning(data.odoo_close.warning, { duration: 15000 });
      } else {
        toast.success("Estado actualizado");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el estado", isClose ? { duration: 12000 } : undefined);
    } finally {
      if (isClose) setValidatingClose(false);
    }
  };

  // v101.101.0 · Validar/Cerrar: si la actividad exige motivo de cierre y aún no está, primero se registra
  const handleValidateClick = async () => {
    if (validatingClose) return;
    try {
      const d = await fetchCloseReasons(odooTaskId);
      if (d?.required && !d?.complete) { setShowCloseReason(true); return; }
    } catch (_) { /* el backend vuelve a validar */ }
    doStatus("completed");
  };

  // v101.7.0 · Escribe partner_latitude/longitude en Odoo (JSON-RPC) + espejo master_ot
  const pushCoordsToOdoo = async () => {
    const lat = Number(coordLat);
    const lon = Number(coordLon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      toast.error("Latitud y longitud numéricas requeridas");
      return;
    }
    setCoordSaving(true);
    try {
      await api.post(`/v1/ot_smart/${odooTaskId}/client-coordinates`, {
        lat,
        lon,
        reason: coordReason || "Corrección GPS desde OT SMART (agendador)",
      });
      toast.success("Coordenadas actualizadas en Odoo");
      setShowGpsWizard(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar Odoo");
    } finally {
      setCoordSaving(false);
    }
  };

  // v101.96.0 · Escribe street/street2 del cliente en Odoo (JSON-RPC) + espejo master_ot
  const pushAddressToOdoo = async () => {
    const street = addrStreet.trim();
    if (street.length < 5) { toast.error("Escribe la dirección (calle principal), mínimo 5 caracteres"); return; }
    setAddrSaving(true);
    try {
      await api.post(`/v1/ot_smart/${odooTaskId}/client-address`, {
        street,
        street2: addrStreet2.trim() || null,
        reason: addrReason || "Corrección de dirección desde OT SMART (agendador)",
      });
      toast.success("Dirección actualizada en Odoo");
      setShowAddrWizard(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar la dirección en Odoo");
    } finally {
      setAddrSaving(false);
    }
  };

  if (loading && !ot) {
    return <Layout><div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-gray-400" size={32} /></div></Layout>;
  }
  if (!ot) return null;

  const smart = ot.smart || { internal_status: "pending" };
  const st = STATUS_STYLES[smart.internal_status] || STATUS_STYLES.pending;
  const gpsValid = ot.partner_lat && ot.partner_lon && ot.partner_lat !== 0 && ot.partner_lon !== 0;
  const actions = NEXT_ACTIONS[smart.internal_status] || [];
  const canCreateChild = canCreateChildRole && otAllowsChildTask(smart);
  const syncVis = odooSyncVisual(ot);
  const syncColors = syncVis ? {
    ok: { bg: "#D1FAE5", fg: "#065F46", Icon: CheckCircle2 },
    error: { bg: "#FEE2E2", fg: "#991B1B", Icon: XCircle },
    pending: { bg: "#FEF3C7", fg: "#92400E", Icon: CloudDownload },
    warn: { bg: "#FFEDD5", fg: "#9A3412", Icon: AlertTriangle },
  }[syncVis.kind] : null;
  const SyncIcon = syncColors?.Icon || null;

  return (
    <Layout>
      <div className="space-y-4" data-testid="ot-smart-detail">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <button
              onClick={() => navigate("/ot-smart")}
              className="p-2 rounded hover:bg-gray-100 mt-1"
              data-testid="btn-back"
            >
              <ArrowLeft size={18} />
            </button>
            {/* v101.46.1 · Flechas: siguiente = OT de abajo en el mismo carril Kanban */}
            <div className="flex items-center gap-0.5 mt-1" data-testid="lane-nav-arrows">
              <button
                type="button"
                disabled={!laneNav.prev}
                onClick={() => laneNav.prev && navigate(`/ot-smart/${laneNav.prev.odoo_task_id}`)}
                title={laneNav.prev ? `Anterior · ${laneNav.prev.code}` : "No hay OT anterior en este carril"}
                className="p-2 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="btn-lane-prev"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                disabled={!laneNav.next}
                onClick={() => laneNav.next && navigate(`/ot-smart/${laneNav.next.odoo_task_id}`)}
                title={laneNav.next ? `Siguiente · ${laneNav.next.code}` : "No hay OT siguiente en este carril"}
                className="p-2 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="btn-lane-next"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-mono mb-1">
                OT SMART · Detalle · Odoo #{ot.odoo_task_id}
                {laneNav.lane_name ? ` · ${laneNav.lane_name}` : ""}
              </div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: "Chivo" }}>
                <Zap className="text-[#E0E621]" fill="#E0E621" strokeWidth={1.5} size={22} />
                <span data-testid="afc-code">{ot.afc_code || ot.task_number || `Tarea #${ot.odoo_task_id}`}</span>
              </h1>
              {ot.task_number && ot.afc_code && (
                <div className="mt-0.5 text-[11px] font-mono text-gray-500">
                  Ref Odoo: <span className="text-gray-700">{ot.task_number}</span>
                </div>
              )}
              {/* v101.4.0 · Nombre editable para agendador (M1.edit) */}
              {editingTaskName ? (
                <div className="mt-1 flex items-center gap-2 max-w-3xl" data-testid="task-name-edit">
                  <input
                    value={taskNameDraft}
                    onChange={(e) => setTaskNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveTaskName();
                      if (e.key === "Escape") setEditingTaskName(false);
                    }}
                    autoFocus
                    disabled={taskNameSaving}
                    className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#0A3747]"
                    data-testid="task-name-input"
                  />
                  <button
                    type="button"
                    onClick={saveTaskName}
                    disabled={taskNameSaving}
                    className="p-1.5 rounded bg-[#E0E621] text-[#0A3747] hover:bg-[#c9cf1e] disabled:opacity-50"
                    title="Guardar nombre"
                    data-testid="task-name-save"
                  >
                    {taskNameSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingTaskName(false)}
                    disabled={taskNameSaving}
                    className="p-1.5 rounded border border-gray-300 hover:bg-gray-50"
                    title="Cancelar"
                    data-testid="task-name-cancel"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="mt-1 flex items-start gap-2 max-w-3xl group">
                  <div className="text-sm text-gray-700" data-testid="task-name">{ot.task_name || "—"}</div>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        setTaskNameDraft(ot.task_name || "");
                        setEditingTaskName(true);
                      }}
                      className="p-1 rounded text-gray-400 hover:text-[#0A3747] hover:bg-gray-100 opacity-70 group-hover:opacity-100"
                      title="Editar nombre de la tarea"
                      data-testid="task-name-edit-btn"
                    >
                      <Edit3 size={13} />
                    </button>
                  )}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${st.bg} ${st.fg}`} data-testid="status-badge">
                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                  {st.label}
                </span>
                {/* v101.46.1 · Icono sync Odoo en ficha */}
                {syncVis && syncColors && SyncIcon && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                    style={{ background: syncColors.bg, color: syncColors.fg }}
                    title={syncVis.title}
                    data-testid="odoo-sync-badge-detail"
                  >
                    <SyncIcon size={12} strokeWidth={2.5} />
                    {syncVis.label}
                  </span>
                )}
                {smart.internal_priority && (
                  <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                    {smart.internal_priority}
                  </span>
                )}
                {ot.last_synced_at && (
                  <span className="text-[10px] font-mono text-gray-500" title={`Materializada desde Odoo: ${ot.last_synced_at}`}>
                    📚 Master: {new Date(ot.last_synced_at).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}
                  </span>
                )}
              </div>
              {/* v101.45.2 · Padre/hija (Odoo main_task_id ↔ AlfaField) */}
              {(ot.main_task_odoo_id || (ot.child_tasks && ot.child_tasks.length > 0)) && (
                <div className="mt-2 flex flex-col gap-1.5 max-w-3xl" data-testid="ot-task-family">
                  {ot.main_task_odoo_id && (
                    <button
                      type="button"
                      onClick={() => navigate(`/ot-smart/${ot.main_task_odoo_id}`)}
                      className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#0A3747] hover:underline w-fit text-left"
                      data-testid="link-parent-task"
                    >
                      <ArrowLeft size={11} /> Tarea padre{" "}
                      {ot.parent_task?.task_number || ot.parent_task?.afc_code || `#${ot.main_task_odoo_id}`}
                      {ot.parent_task?.task_name ? ` · ${String(ot.parent_task.task_name).slice(0, 48)}` : ""}
                    </button>
                  )}
                  {ot.child_tasks?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      <span className="text-[9px] font-bold uppercase text-gray-400 tracking-wide mr-0.5">Hijas</span>
                      {ot.child_tasks.map((c) => (
                        <button
                          key={c.odoo_task_id}
                          type="button"
                          onClick={() => navigate(`/ot-smart/${c.odoo_task_id}`)}
                          className="px-2 py-0.5 rounded-md text-[10px] font-mono bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100"
                          data-testid={`link-child-task-${c.odoo_task_id}`}
                          title={c.task_name || ""}
                        >
                          {c.task_number || c.afc_code || c.odoo_task_id}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && actions.map((a) => (
              <button
                key={a.to}
                onClick={() => (a.to === "cancelled" ? setCancelOpen(true) : doStatus(a.to))}
                data-testid={`action-${a.to}`}
                className={a.to === "cancelled"   /* v101.200.0 · Cancelar en rojo */
                  ? "px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-red-600 text-white border border-red-600 hover:bg-red-700"
                  : "px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50"}
              >
                <a.icon size={13} />
                {a.label}
              </button>
            ))}
            {canAssign && (smart.technician_id ? (
              <button
                onClick={openAssign}
                data-testid="btn-reassign"
                className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-[#0A3747] text-white hover:bg-[#135166]"
              >
                <UserPlus size={13} /> Reasignar
              </button>
            ) : (
              <button
                onClick={openAssign}
                data-testid="btn-assign"
                className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-[#E0E621] text-[#0A3747] hover:bg-[#c9cf1e] font-semibold"
              >
                <UserPlus size={13} /> Asignar técnico
              </button>
            ))}
            {canValidate && ["completed", "solucionada_pendiente_fiscalizacion", "pendiente_fiscalizacion",
              "fiscalizada_pendiente_cierre", "validada", "closed"].includes(smart.internal_status) && (
              <button
                onClick={() => setShowAudit(true)}
                data-testid="btn-audit-fiscal"
                title="Auditar la fiscalización · revisar fotos + IA + checklist"
                className="px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 bg-amber-500 text-white hover:bg-amber-600 font-bold animate-pulse shadow-md"
              >
                🔍 Auditar Fiscalización
              </button>
            )}
            {/* v101.260.0 · Se quitó el botón «Reabrir» (OT realizada / cerrada) */}
            {/* v101.54.0 · (Re)enviar cierre a Odoo: endpoint real /push-to-odoo (antes 404 → no hacía nada).
                Visible en Realizadas/Cierre mientras Odoo NO haya confirmado el cierre (evita duplicados). */}
            {canValidate && ["completed", "closed"].includes(smart.internal_status)
              && String(ot?.close_event_status || "").toLowerCase() !== "completado" && (
              <button
                disabled={pushingOdoo}
                onClick={async () => {
                  const failed = ["error_negocio", "error_validacion", "pendiente_sync"].includes(String(ot?.close_event_status || "").toLowerCase());
                  if (!window.confirm(failed
                    ? "¿Reenviar el cierre a Odoo? El intento anterior falló."
                    : "¿Enviar el cierre final a Odoo? Esta acción es definitiva.")) return;
                  setPushingOdoo(true);
                  try {
                    // v101.50.1 · idempotent = Odoo dijo «ya tiene cierre procesado» → OK
                    const { data } = await api.post(`/v1/ot_smart/${ot.odoo_task_id}/push-to-odoo`, {}, { timeout: 90000 });
                    toast.success(
                      data?.skipped ? "Odoo ya tenía el cierre confirmado"
                        : data?.odoo_close?.idempotent ? "Cierre ya estaba en Odoo (OK)"
                          : "Cierre OK en Odoo"
                    );
                    // v101.86.0 · equipo aprovisionado ≠ descargado: solo advertencia
                    if (data?.odoo_close?.warning) toast.warning(data.odoo_close.warning, { duration: 15000 });
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "No se pudo enviar el cierre a Odoo", { duration: 12000 });
                  } finally {
                    setPushingOdoo(false);
                    reload();
                  }
                }}
                data-testid="btn-push-odoo"
                title="Enviar / reenviar el cierre a Odoo"
                className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
              >
                {pushingOdoo ? <Loader2 size={13} className="animate-spin" /> : "↗"}
                {["error_negocio", "error_validacion", "pendiente_sync"].includes(String(ot?.close_event_status || "").toLowerCase())
                  ? " Reenviar cierre a Odoo" : " Enviar cierre a Odoo"}
              </button>
            )}
            {canValidate && smart.internal_status !== "completed" && smart.internal_status !== "cancelled" && (
              <button
                disabled={validatingClose}
                onClick={handleValidateClick}
                data-testid="btn-validate-ot"
                title="Validar y cerrar la OT (espera respuesta de Odoo)"
                className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
              >
                {validatingClose ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                {validatingClose ? " Cerrando en Odoo…" : " Validar/Cerrar"}
              </button>
            )}
            {/* v101.246.0 · Barra de avance mientras se cierra en Odoo */}
            <CloseProgressModal odooTaskId={odooTaskId} open={validatingClose || pushingOdoo} />
            {/* v101.101.0 · Motivo de cierre antes de Validar/Cerrar */}
            <CloseReasonDialog
              odooTaskId={odooTaskId}
              open={showCloseReason}
              title="Motivo de cierre (requerido para cerrar)"
              onClose={() => setShowCloseReason(false)}
              onSaved={() => { setShowCloseReason(false); setCloseReasonKey((k) => k + 1); doStatus("completed"); }}
            />
            {/* v101.128.0 · Informe de autofiscalización (PDF) en solo lectura: visible para todo rol que ve la OT */}
            {informe?.exists && (
              <button
                onClick={() => openInformePdf(informe.submission_id, false)}
                data-testid="btn-informe-pdf"
                title={informe.final ? "Ver el informe técnico de autofiscalización (PDF)"
                  : "El técnico aún no termina el formulario: el informe puede estar incompleto"}
                className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-white border border-[#0A3747]/40 text-[#0A3747] hover:bg-slate-50"
              >
                <FileText size={13} /> Informe PDF{informe.final ? "" : " (borrador)"}
              </button>
            )}
            {/* v101.200.0 · Imprimir, Actualizar GPS y Actualizar dirección pasan al lápiz; afuera queda WhatsApp */}
            <FibiWhatsAppBoton odooTaskId={odooTaskId} taskNumber={ot?.task_number || ot?.afc_code} />
            {canComment && (
              <button
                onClick={() => { setCommentText(""); setCommentOpen(true); }}
                data-testid="btn-comment-ot"
                title="Agregar comentario a la bitácora"
                className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-white border border-gray-300 hover:bg-gray-50"
              >
                <MessageSquare size={13} /> Comentar
              </button>
            )}
            {(canEdit || canExport || canCreateChild || canPrint || canUpdateOdooCoords) && (
              <OTActionMenu
                ot={ot}
                techs={techs}
                onUpdated={load}
                // v101.47.0 · + on_site / en_sitio (además de pendientes y asignada)
                canRefreshOdoo={canEdit && ["pending", "manual_assignment_required", "assigned", "on_site", "en_sitio",
                  "solucionada_pendiente_fiscalizacion", "pendiente_fiscalizacion", "fiscalizada_pendiente_cierre"].includes(smart.internal_status)}   // v101.272.0 · + Fiscalización
                odooRefreshing={odooRefreshing}
                onRefreshOdoo={refreshFromOdoo}
                canExport={canExport}
                onExport={() => {
                  const data = JSON.stringify(ot, null, 2);
                  const blob = new Blob([data], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url;
                  a.download = `OT_SMART_${ot.afc_code || ot.odoo_task_id}.json`;
                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success("OT exportada");
                }}
                canCreateChild={canCreateChild}
                onCreateChild={() => setShowCreateChild(true)}
                extraItems={[
                  canPrint && { icon: Printer, label: "Imprimir OT", onClick: () => window.print(), testid: "btn-print-ot" },
                  canUpdateOdooCoords && { icon: MapPin, label: "Actualizar GPS en Odoo", testid: "btn-open-gps-odoo-wizard",
                    onClick: () => {
                      if (ot?.partner_lat != null && ot?.partner_lon != null) {
                        setCoordLat(String(ot.partner_lat));
                        setCoordLon(String(ot.partner_lon));
                      }
                      setCoordReason("");
                      setShowGpsWizard(true);
                    } },
                  canUpdateOdooCoords && { icon: MapPin, label: "Actualizar dirección en Odoo", testid: "btn-open-address-odoo-wizard",
                    onClick: () => {
                      setAddrStreet(ot?.address_street || "");
                      setAddrStreet2(ot?.address_street2 || "");
                      setAddrReason("");
                      setShowAddrWizard(true);
                    } },
                ]}
              />
            )}
            {!canEdit && !canAssign && !canValidate && !canPrint && !canExport && !canComment && !canUpdateOdooCoords && (
              <span className="text-[10px] text-gray-400 italic">Modo solo lectura</span>
            )}
          </div>
        </div>

        {/* v101.267.0 · Ficha a la izquierda + WhatsApp del cliente a la derecha */}
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-4 items-start">
        {/* v4.2.3 · Ficha 360° con Tabs · v6.3.2 Light Neumorphic */}
        <div className="rounded-3xl overflow-hidden" style={{ background: NEU.bgLight, boxShadow: NEU.shadowUp, border: `1.5px solid ${NEU.border}` }}>
          {/* Tab bar */}
          <div className="flex items-center gap-0.5 overflow-x-auto px-4" style={{ background: NEU.bgSurface, borderBottom: `1px solid ${NEU.border}` }} data-testid="ot-tabs">
            {[
              { key: "general", label: "General", icon: User },
              { key: "tarea",   label: "Tarea",   icon: Workflow },
              { key: "infra",   label: "Infra Red", icon: Server },
              { key: "historia",label: "Historial",icon: History },
              { key: "agentes", label: "Agentes",  icon: Cpu },
            ].map((tab) => {
              const active = activeTab === tab.key;
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  data-testid={`tab-${tab.key}`}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition whitespace-nowrap"
                  style={{
                    borderColor: active ? NEU.accent : "transparent",
                    color: active ? NEU.accent : NEU.textMuted,
                    background: active ? NEU.accentSoft : "transparent",
                  }}
                >
                  <TabIcon size={13} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab: General */}
          {activeTab === "general" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
              <div className="lg:col-span-1 space-y-4">
                {/* v101.101.0 · Motivo de cierre (lo registra el técnico; editable por supervisión) */}
                <CloseReasonCard odooTaskId={odooTaskId} canEdit={canEdit} refreshKey={closeReasonKey}
                  closed={["completed", "cancelled"].includes(smart.internal_status)} />
                <NeuCard title="Cliente" icon={User} testid="card-client">
                  <NeuKV label="Nombre" value={ot.partner_name} bold />
                  <NeuKV label="RUC/CI" value={ot.partner_vat} mono />
                  <NeuKV label="Móvil" value={ot.partner_mobile} icon={Phone} />
                  <NeuKV label="Email" value={ot.partner_email} icon={Mail} />
                  <NeuKV label="Segmento" value={ot.segment_name} />
                  <NeuKV label="Dirección" value={ot.address_full || ot.address_street} />
                  <NeuKV label="Partner ID" value={ot.partner_id} mono muted />
                </NeuCard>

                <NeuCard title="Sucursal" icon={Building2} testid="card-branch">
                  <NeuKV label="Sucursal" value={ot.branch_name} bold />
                  <NeuKV label="Branch ID" value={ot.branch_office_id} mono muted />
                </NeuCard>

                <NeuCard title="Suscripción" icon={IdCard} testid="card-subscription">
                  {/* v101.45.4 · Label operativo: Suscripción (antes «Código») */}
                  <NeuKV label="Suscripción" value={ot.subscription_code} mono bold />
                  <NeuKV label="Plan" value={ot.plan_name || ot.plan_type} />
                  {/* v95.6.0 · Tipo de plan (sale_subscription.plan_type de Odoo) */}
                  <NeuKV label="Tipo de plan" value={ot.plan_type ? ({
                    optical_fiber: "Fibra óptica",
                    radio_link: "Radio enlace", radio: "Radio enlace", wireless: "Inalámbrico",
                    copper: "Cobre", satellite: "Satelital",
                  }[ot.plan_type] || ot.plan_type) + ` (${ot.plan_type})` : null} />
                  <NeuKV label="Ancho banda" value={ot.bandwidth ? `${ot.bandwidth} Mbps` : null} />
                  <NeuKV label="Precio" value={ot.plan_price ? `$ ${Number(ot.plan_price).toFixed(2)}` : null} mono />
                  <NeuKV label="Código prod." value={ot.plan_code} mono muted />
                  <NeuKV label="Inicio" value={ot.subscription_start} />
                </NeuCard>

                <NeuCard title="Equipo" icon={Package} testid="card-equipment">
                  <NeuKV label="FS ID" value={ot.equipment_fs_id} mono />
                  <NeuKV label="Modelo" value={ot.equipment_model} mono />
                  <NeuKV label="ID Equipo" value={ot.equipment_id} mono />
                </NeuCard>

                {/* v96.2.0 · Equipos en poder del cliente según Alfanet (cédula + suscripción) */}
                <ClientEquipmentCard odooTaskId={ot.odoo_task_id} />

                {(smart.notes || ot.description) && (
                  <NeuCard title="Notas comerciales" testid="card-notes">
                    {smart.notes && (
                      <div className="text-xs text-gray-700 whitespace-pre-wrap mb-2">{smart.notes}</div>
                    )}
                    {ot.description && (
                      <div className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {stripHtml(ot.description)}
                      </div>
                    )}
                  </NeuCard>
                )}

                {/* v4.2.4 · Última Acción del Sistema (agentes IA) */}
                {agentHistory?.last_action && (
                  <NeuCard title="Última Acción del Sistema" icon={Cpu} testid="card-last-agent-action">
                    {(() => {
                      const la = agentHistory.last_action;
                      const isSuccess = ["approved", "scheduled", "assigned", "ok", "coverage_full_waiting_client"].includes((la.result || "").toLowerCase());
                      const icon = isSuccess ? "text-emerald-600" : "text-amber-600";
                      const bgc = isSuccess ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200";
                      const resultLabel = {
                        approved: "Aprobado",
                        rejected: "Rechazado",
                        scheduled: "Agendado",
                        assigned: "Asignado",
                        error: "Error",
                        coverage_full_waiting_client: "En espera (cobertura completa)",
                        no_slots: "Sin slots disponibles",
                        no_techs: "Sin técnicos en zona",
                      }[(la.result || "").toLowerCase()] || la.result;

                      return (
                        <div className={`rounded-lg border px-3 py-2.5 ${bgc}`} data-testid="last-agent-summary">
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${icon}`}>
                              {isSuccess ? "✅" : "⚠️"}
                            </span>
                            <span className="text-xs font-bold text-slate-800">
                              {isSuccess
                                ? `${la.agent_name || la.agent} automáticamente por ${la.agent}`
                                : `Problema de ${la.agent_name || la.agent}: ${resultLabel}`}
                            </span>
                          </div>
                          {la.ts && (
                            <div className="text-[10px] text-slate-500 mt-1 font-mono">
                              {new Date(la.ts).toLocaleString("es-EC")}
                            </div>
                          )}
                          {la.details?.notes && (
                            <div className="text-[11px] text-slate-600 mt-1">{la.details.notes}</div>
                          )}
                        </div>
                      );
                    })()}
                  </NeuCard>
                )}
              </div>

              <div className="lg:col-span-2 space-y-4">

                {/* v101.16.0 · isolate+z-0: panes Leaflet (400–1000) no deben tapar ModuleTopBar (z-30) */}
                <div
                  className="rounded-3xl overflow-hidden relative isolate"
                  style={{ height: 380, border: `1.5px solid ${NEU.border}`, boxShadow: NEU.shadowUp, zIndex: 0 }}
                  data-testid="ot-detail-map"
                >
                  {gpsValid ? (
                    <MapContainer
                      center={[ot.partner_lat, ot.partner_lon]}
                      zoom={15}
                      style={{ height: "100%", width: "100%", zIndex: 0 }}
                      className="ot-detail-leaflet h-full w-full"
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                      <Marker position={[ot.partner_lat, ot.partner_lon]} icon={clientIcon}>
                        <Popup>
                          <div className="text-xs">
                            <div className="font-bold">{ot.partner_name}</div>
                            <div className="font-mono text-[10px]">{ot.partner_lat.toFixed(6)}, {ot.partner_lon.toFixed(6)}</div>
                          </div>
                        </Popup>
                      </Marker>
                      {/* v101.114.0 · Cajas cercanas al cliente (500 m) y al acercar; la caja de la OT resaltada */}
                      <BoxPolesLayer focus={[{ lat: ot.partner_lat, lon: ot.partner_lon }]} radiusM={500}
                        highlight={[ot.network_infrastructure?.distribution_box, ot.network_infrastructure?.port_box]} />
                    </MapContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center" style={{ background: NEU.bgSurface, color: NEU.textMuted }}>
                      <WifiOff size={40} />
                      <div className="text-sm font-semibold mt-2">Sin coordenadas GPS</div>
                      <div className="text-xs mt-1 text-gray-500">
                        Este cliente no tiene <code>partner_latitude/longitude</code> en Odoo
                        ({stats_gps_msg(ot)}).
                      </div>
                    </div>
                  )}
                </div>

                {(smart.technician_id || ot.assigned_tech_id || ot.assigned_tech_email) && (
                  <NeuCard title="Técnico Asignado" icon={User} testid="card-assignment">
                    <NeuKV label="Nombre"     value={smart.technician_name || ot.master_technician_name || ot.assigned_tech_email} bold />
                    <NeuKV label="Email"      value={smart.technician_email || ot.technician_email || ot.assigned_tech_email} />
                    <NeuKV label="Teléfono"   value={smart.technician_phone} mono />
                    <NeuKV label="Sucursal"   value={smart.technician_branch || ot.branch_name} />
                    <NeuKV label="Bodega"     value={smart.warehouse_location_name} />
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <NeuKV label="Asignado el" value={smart.assigned_at ? new Date(smart.assigned_at).toLocaleString("es-EC") : null} />
                      <NeuKV label="Prioridad" value={smart.internal_priority?.toUpperCase()} mono />
                      <NeuKV label="Programada" value={smart.scheduled_at ? new Date(smart.scheduled_at).toLocaleString("es-EC") : null} icon={Calendar} />
                    </div>
                  </NeuCard>
                )}

                {/* v101.22.0 · Timeline suscripción (scroll fijo; no rompe layout) */}
                <SubscriptionAiTimeline
                  odooTaskId={parseInt(odooTaskId, 10)}
                  subscriptionCode={ot.subscription_code}
                  canRebuild={canEdit}
                />
              </div>
            </div>
          )}

          {/* Tab: Tarea */}
          {activeTab === "tarea" && (
            <div className="p-4 space-y-4 max-w-5xl">
              <NeuCard title="Descripción de la tarea" testid="card-description">
                {ot.task_description ? (
                  <div className="text-xs whitespace-pre-wrap leading-relaxed rounded-2xl p-3" style={{ background: NEU.bgSurface, color: NEU.text }}>
                    {stripHtml(ot.task_description)}
                  </div>
                ) : (
                  <div className="text-xs italic" style={{ color: NEU.textMuted }}>Sin descripción</div>
                )}
                <div className="text-[10px] mt-2" style={{ color: NEU.textMuted }}>
                  Creada en Odoo: {ot.odoo_created_at ? new Date(ot.odoo_created_at).toLocaleString("es-EC") : "—"}
                  {ot.helpdesk_ticket_id ? ` · Ticket helpdesk #${ot.helpdesk_ticket_id}` : ""}
                </div>
              </NeuCard>

              <NeuCard
                title="Tipo de Solución"
                icon={Workflow}
                testid="card-solution-type"
                variant={ot.solution_type_name ? "success" : "warning"}
              >
                {ot.solution_type_name ? (
                  <>
                    <NeuKV label="Solución" value={ot.solution_type_name} bold accent={NEU.green} />
                    <NeuKV label="Actividad" value={ot.activity_type} mono />
                    <NeuKV label="Código" value={ot.activity_type_code} mono accent={NEU.accent} />
                    {ot.solution_node_path && ot.solution_node_path.length > 0 && (
                      <div className="text-[10px] mt-1 font-mono" style={{ color: NEU.textMuted }} data-testid="solution-path">
                        {ot.solution_node_path.join(" › ")}
                      </div>
                    )}
                    {/* v99.1.0 · Badge "predeterminado" + botón Cambiar tipo */}
                    {ot.solution_node_source === "default" && (
                      <div className="mt-1 flex items-center gap-1" data-testid="default-badge">
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          Tipo predeterminado · verificar
                        </span>
                      </div>
                    )}
                    {canChangeSolutionType && (
                      <button
                        className="mt-1.5 text-[10px] text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        onClick={() => setShowChangeSolution(true)}
                        data-testid="change-solution-btn"
                      >
                        Cambiar tipo de solución
                      </button>
                    )}
                    <NeuKV label="Modo" value={ot.solution_scheduling_mode === "automatic" ? "Automático" : ot.solution_scheduling_mode} />
                    {/* SLA Badge */}
                    {ot.solution_sla && (
                      <div className="mt-2 pt-2" style={{ borderTop: `1px solid ${NEU.border}` }} data-testid="sla-section">
                        <div className="text-[10px] font-mono uppercase tracking-widest mb-1.5 flex items-center gap-1" style={{ color: NEU.textLabel }}>
                          <Clock size={10} /> SLA de Resolución
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {["p1", "p2", "p3"].map((p) => {
                            const hrs = ot.solution_sla[p];
                            if (hrs == null) return null;
                            const isEffective = (ot.smart?.internal_priority || "p3") === p;
                            return (
                              <span
                                key={p}
                                data-testid={`sla-${p}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold"
                                style={{
                                  background: isEffective ? NEU.accent : NEU.bgSurface,
                                  color: isEffective ? NEU.bg : NEU.textMuted,
                                  border: `1px solid ${isEffective ? NEU.accent : NEU.border}`,
                                  boxShadow: isEffective ? `0 0 8px ${NEU.accent}40` : "none",
                                }}
                              >
                                <span className="uppercase text-[9px] font-black">{p}</span>
                                <span>{hrs}h</span>
                              </span>
                            );
                          })}
                        </div>
                        {ot.solution_sla_effective_hours != null && (
                          <div className="mt-1.5 text-xs font-semibold" style={{ color: NEU.accent }} data-testid="sla-effective">
                            SLA efectivo: {ot.solution_sla_effective_hours}h ({ot.smart?.internal_priority?.toUpperCase() || "P3"})
                          </div>
                        )}
                      </div>
                    )}
                    {/* Link al formulario */}
                    {ot.solution_form_id && (
                      <div className="mt-2">
                        <a
                          href={`/fiscalizacion/formularios/${ot.solution_form_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="link-form"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold hover:underline"
                          style={{ color: NEU.accent }}
                        >
                          <ExternalLink size={11} /> Ver formulario de fiscalización
                        </a>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <NeuKV label="Actividad" value={ot.activity_type} mono />
                    <NeuKV label="Código" value={ot.activity_type_code || "Sin código"} mono accent={NEU.amber} />
                    <div className="text-[11px] italic mt-2 px-3 py-2 rounded-2xl" style={{ color: NEU.amber, background: NEU.amberBg }} data-testid="no-solution-match">
                      Sin nodo mapeado para código "{ot.activity_type_code || 'N/A'}" — usa Auto-Link o mapea manualmente en Tipos de Solución.
                      {/* v101.4.0 · Sin tipo de solución no se puede asignar técnico */}
                      {" "}Debe asignar tipo de solución antes de asignar técnico.
                    </div>
                    {canChangeSolutionType && (
                      <button
                        className="mt-1.5 text-[10px] text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        onClick={() => setShowChangeSolution(true)}
                        data-testid="assign-solution-btn"
                      >
                        Asignar tipo de solución
                      </button>
                    )}
                  </>
                )}
              </NeuCard>

              <StockCheckCard otId={odooTaskId} />
            </div>
          )}

          {/* v6.3.4 · Tab Infra + v101.22.16 · Wizards montados siempre (ocultos fuera de Infra) para no perder progreso */}
          {ot && (() => {
            const ni = ot.network_infrastructure || {};
            const pd = ot.provisioning_data || {};
            const hasInfra = ni.cpe_serial || ni.distribution_box || ni.transmission_box || ni.olt_card || ni.olt || ni.ip_address || ni.equipment_name;
            const hasProv = pd.contractor_name || pd.assigned_users || pd.salesperson_name || pd.team_name || pd.verdict_percentage != null || pd.state_rds || pd.equipment_display_name;
            const canProvision = !!ni.ip_address;
            // v101.103.0 · Explicación de cada dato (aparece al pasar el mouse)
            const INFRA_HELP = {
              equipo: "Nombre del equipo del cliente registrado en Odoo (ficha del CPE de la suscripción). Debe ser el mismo equipo que «CPE (Serie)»; si no coincide, confirme con la etiqueta del equipo instalado.",
              cpe: "Serie del equipo instalado donde el cliente (ONT / ONU). Es la que usa la OLT para ubicarlo (formato GPON: HWTC-…, ZTEG…).",
              dist: "Caja de distribución (NAP) donde llega la acometida del cliente, según la ficha del equipo en Odoo o el aprovisionamiento. Debe ser de la sucursal del cliente.",
              trans: "Caja de la que salen los puertos: en Odoo los puertos se llaman <caja>-NN. Al cerrar, Odoo recibe la caja DUEÑA del puerto y el número de puerto.",
              tarjeta: "Tarjeta (slot) de la OLT donde está el puerto PON que alimenta la caja.",
              olt: "Equipo central de la red de fibra que da servicio a la caja. La VLAN del cliente debe estar habilitada en esta OLT.",
              ipolt: "Dirección con la que AlfaField se conecta a la OLT para aprovisionar (crear, cambiar o borrar la ONT). No es la IP del cliente.",
              puerto: "Puerto de la caja donde se conecta el cliente (<caja>-número). Al cerrar se envía a Odoo la caja dueña del puerto + el número.",
              vlan: "Red lógica del cliente en la OLT (según sucursal, tipo de cliente y plan). Debe estar habilitada en la OLT.",
              ipcli: "IP asignada al servicio del cliente dentro de la VLAN. Si dice «Distinta a Odoo», AlfaField y Odoo tienen IPs diferentes.",
            };
            const infraFields = [
              ...(ni.equipment_name ? [{ label: "Equipo", value: ni.equipment_name, icon: Cpu, color: "#0891B2", help: INFRA_HELP.equipo }] : []),
              { label: "CPE (Serie)", value: ni.cpe_serial, icon: Wifi, color: "#3B82F6", help: INFRA_HELP.cpe },
              { label: "Caja de Distribución", value: ni.distribution_box, icon: Package, color: "#8B5CF6", help: INFRA_HELP.dist },
              { label: "Caja de Transmisión", value: ni.transmission_box, icon: Package, color: "#6366F1", help: INFRA_HELP.trans },
              { label: "Tarjeta OLTs", value: ni.olt_card, icon: Server, color: "#059669", help: INFRA_HELP.tarjeta },
              { label: "OLT", value: ni.olt, icon: Server, color: "#0D9488", help: INFRA_HELP.olt },
              { label: "IP de gestión OLT", value: ni.ip_address, icon: Wifi, color: "#D97706", help: INFRA_HELP.ipolt },
              // v101.22.16 · Puerto visible en ficha Infra Red
              ...(ni.port_name || ot.pe_port_name ? [{ label: "Puerto", value: ni.port_name || ot.pe_port_name, icon: Server, color: "#64748B", help: INFRA_HELP.puerto }] : []),
            ];
            // v101.103.0 · Avisos de datos que no se corresponden (vienen así de Odoo o de un aprovisionamiento anterior)
            const _snk = (x) => String(x || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
            const infraWarnings = [];
            if (ni.port_box && (ni.distribution_box || ni.transmission_box)
                && ![_snk(ni.distribution_box), _snk(ni.transmission_box)].includes(_snk(ni.port_box))) {
              infraWarnings.push(`El puerto ${ni.port_name} es de la caja ${ni.port_box}${ni.port_box_olt ? ` (OLT ${ni.port_box_olt})` : ""}, que no es ninguna de las cajas registradas. Al cerrar se envía la caja dueña del puerto si es de la misma sucursal y OLT; si no, el cierre se detiene.`);
            } else if (ni.port_box && ni.distribution_box && _snk(ni.port_box) !== _snk(ni.distribution_box)) {
              infraWarnings.push(`La caja de distribución (${ni.distribution_box}) no es la dueña del puerto ${ni.port_name} (es ${ni.port_box}). Para Odoo vale la caja dueña del puerto.`);
            }
            if (ni.distribution_box && ni.transmission_box) {
              const _pre = (b) => (String(b).includes("_") ? String(b).split("_")[0].toUpperCase() : "");
              if (_pre(ni.distribution_box) && _pre(ni.transmission_box) && _pre(ni.distribution_box) !== _pre(ni.transmission_box)) {
                infraWarnings.push(`La caja de distribución (${ni.distribution_box}) y la de transmisión (${ni.transmission_box}) son de sucursales distintas: el dato de Odoo de este cliente está inconsistente.`);
              }
            }
            // v101.241.0 · Misma ONT escrita en PON (5A544547DE8DFA7E) y de fábrica (ZTEGDE8DFA7E), o con «(G)»: no es
            //              otro equipo → sin aviso
            const _nuc = (x) => {
              const n = _snk(String(x || "").replace(/^\s*\(\s*G\s*\)\s*/i, "")).slice(0, 20);
              const m = n.match(/^([0-9A-F]{8})([0-9A-F]{8})$/);
              if (m) {
                const pre = (m[1].match(/../g) || []).map((h) => String.fromCharCode(parseInt(h, 16))).join("");
                if (/^[A-Z]{4}$/.test(pre)) return pre + m[2];
              }
              return n;
            };
            if (ni.equipment_name && ni.cpe_serial && _nuc(ni.equipment_name) !== _nuc(ni.cpe_serial)) {
              infraWarnings.push(`El equipo registrado en Odoo (${ni.equipment_name}) no es el mismo que la serie CPE (${ni.cpe_serial}). Confirme con la etiqueta cuál está instalado.`);
            }
            // v101.17.0 · VLAN seleccionada en aprovisionamiento
            if (ni.vlan_name || ni.vlan_id) {
              infraFields.push({
                label: "VLAN",
                value: ni.vlan_name || String(ni.vlan_id),
                icon: Wifi,
                color: "#7C3AED",
                help: INFRA_HELP.vlan,
              });
            }
            // v99.4.2 / v101.17.0 · IP cliente: pe_client_ip | ni.client_ip | pe_cpe_ip_odoo
            const clientIp = ot?.pe_client_ip || ni.client_ip || ni.cpe_ip_odoo || null;
            const ipsDiffer = (ot?.pe_client_ip || ni.client_ip) && ni.cpe_ip_odoo
              && (ot?.pe_client_ip || ni.client_ip) !== ni.cpe_ip_odoo;
            if (clientIp) {
              infraFields.push({
                label: `IP del cliente${ipsDiffer ? " ⚠ Distinta a Odoo" : ""}`,
                value: clientIp,
                icon: Wifi,
                color: ipsDiffer ? "#DC2626" : "#F59E0B",
                help: INFRA_HELP.ipcli,
              });
            }
            const provFields = [
              { label: "Contratista", value: pd.contractor_name, icon: User, color: "#DC2626" },
              { label: "Asignados", value: pd.assigned_users, icon: UserPlus, color: "#7C3AED" },
              { label: "Vendedor", value: pd.salesperson_name, icon: IdCard, color: "#2563EB" },
              { label: "Equipo de ventas", value: pd.team_name, icon: IdCard, color: "#0F766E" },   // v101.248.0
              { label: "Equipo", value: pd.equipment_display_name, icon: Cpu, color: "#0891B2" },
              { label: "Veredictos IA", value: pd.verdict_percentage != null ? `${pd.verdict_percentage}%` : null, icon: Zap, color: "#F59E0B" },
              { label: "Estado RDS", value: pd.state_rds, icon: CheckCircle2, color: pd.state_rds === "synced" ? "#059669" : "#6B7280" },
            ];
            const renderFieldGrid = (fields, testidPrefix) => (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {fields.map((f) => (
                  <div
                    key={f.label}
                    className={`flex items-start gap-3 p-3 transition ${f.help ? "relative group cursor-help" : ""}`}
                    style={{
                      background: f.value ? "#FFFFFF" : NEU.bgSurface,
                      borderRadius: 16,
                      border: `1.5px solid ${f.value ? f.color + "20" : NEU.border}`,
                      boxShadow: f.value ? `0 2px 8px ${f.color}10, -2px -2px 6px rgba(255,255,255,0.8), 2px 2px 6px rgba(163,177,198,0.25)` : "none",
                    }}
                    data-testid={`${testidPrefix}-${f.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${f.color}12` }}>
                      <f.icon size={16} style={{ color: f.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] uppercase tracking-widest font-semibold flex items-center gap-1" style={{ color: NEU.textMuted }}>
                        {f.label}
                        {f.help && <Info size={10} className="opacity-60" />}
                      </div>
                      {/* v101.103.0 · Explicación al pasar el mouse */}
                      {f.help && (
                        <div role="tooltip"
                          className="pointer-events-none absolute left-3 right-3 bottom-full mb-2 z-50 hidden group-hover:block rounded-lg px-3 py-2 text-[11px] leading-snug font-normal normal-case tracking-normal shadow-lg"
                          style={{ background: "#0F172A", color: "#F8FAFC" }}>
                          {f.help}
                        </div>
                      )}
                      <div className="text-sm font-bold mt-0.5 font-mono truncate" style={{ color: f.value ? NEU.text : NEU.textMuted }} title={f.value || "—"}>
                        {f.value || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
            return (
              <div
                className="p-4 space-y-5"
                data-testid="tab-infra-content"
                style={{ display: activeTab === "infra" ? undefined : "none" }}
                aria-hidden={activeTab !== "infra"}
              >
                <NeuCard title="Infraestructura de Red" icon={Server} testid="card-infra-red">
                  {!hasInfra ? (
                    <div className="text-center py-8">
                      <Server size={28} className="mx-auto mb-2 opacity-30" style={{ color: NEU.textMuted }} />
                      <p className="text-xs" style={{ color: NEU.textMuted }}>Sin datos de infraestructura</p>
                    </div>
                  ) : (
                    <>
                      {renderFieldGrid(infraFields, "infra-field")}
                      {infraWarnings.length > 0 && (
                        <div className="mt-3 space-y-1.5" data-testid="infra-warnings">
                          {infraWarnings.map((w, i) => (
                            <div key={i} className="flex items-start gap-2 px-3 py-2 rounded-xl text-[11px]"
                              style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}>
                              <AlertTriangle size={13} className="shrink-0 mt-0.5" /> <span>{w}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </NeuCard>

                {/* v7.0.0 · Botón "Aprovisionar Equipo" + Discovery Wizard */}
                {/* v98.0.0 · UI Aprovisionamiento Dinámico: Tipo de Solución mandatorio */}
                {(() => {
                  const pf = ot.provisioning_flow || {};
                  const flujoNull = pf.flujo == null;
                  const flujo = pf.flujo;
                  const motivo = pf.motivo || "sin_flujo_configurado";
                  const motivoTextos = {
                    sin_flujo_configurado: "Este tipo de solución no tiene flujo de aprovisionamiento asignado (configurar en Tipos de Solución).",
                    segmentos_no_configurados: "El flujo asignado no tiene segmentos configurados.",
                    segmento_no_permitido: "Segmento del cliente fuera de alcance.",
                    red_no_coincide_con_plan: "El flujo asignado no corresponde a la red del cliente.",
                    corporativo_fuera_alcance: "Cliente corporativo: fuera de alcance.",
                    error_resolviendo_flujo: "Error al resolver el flujo de aprovisionamiento.",
                    flujo_no_coincide_con_tecnologia: "El flujo de aprovisionamiento no coincide con la tecnología (fibra / radio) del tipo de solución.",
                    tipo_cliente_no_permitido: "El tipo de cliente del contrato no está en «Aplica a» del tipo de solución de la OT (cambie el tipo de solución o corrija «Aplica a» en Tipos de Solución).",   // v101.141.0
                  };
                  const motivoBase = motivo.split(":")[0];
                  const motivoMsg = motivoTextos[motivoBase] || motivo;
                  // v101.130.0 · El tipo de solución SÍ pide aprovisionamiento pero la configuración lo impide:
                  //             aviso visible para todos (antes se ocultaba el botón sin explicación)
                  const configBlocked = ["segmento_no_permitido", "segmentos_no_configurados", "red_no_coincide_con_plan",
                    "flujo_no_coincide_con_tecnologia", "tipo_cliente_no_permitido"].includes(motivoBase);   // v101.135.0
                  if (flujoNull && configBlocked) {
                    return (
                      <div className="rounded-2xl p-4 text-xs" data-testid="flow-config-blocked"
                        style={{ background: "#FFFBEB", border: "1px solid #FCD34D", color: "#92400E" }}>
                        <b>No se puede aprovisionar esta OT:</b> {motivoMsg}
                        {motivoBase === "segmento_no_permitido" && <> (segmento «{motivo.split(":")[1] || "—"}»)</>}.
                        <div className="mt-1">La OT no podrá finalizarse ni cerrarse hasta corregir la configuración del
                          tipo de aprovisionamiento o cambiar el tipo de solución.</div>
                      </div>
                    );
                  }

                  // v98.0.0 · flujo null → ocultar todo; solo admin ve motivo discreto
                  if (flujoNull) {
                    const isAdmin = hasPerm && (hasPerm("M1.edit") || hasPerm("admin"));
                    return isAdmin ? (
                      <p data-testid="flow-blocked-hint" className="text-[10px] mt-1 opacity-50" style={{ color: "#94A3B8" }}>
                        Diagnóstico: {motivoMsg}
                      </p>
                    ) : null;
                  }

                  // v101.119.0 · Cliente corporativo: solo aprovisionamiento MANUAL desde oficina
                  const corpManual = !!pf.manual_only;
                  const _role = String(authUser?.role || "").toLowerCase();
                  const isFieldRole = ["technician", "tecnico", "contractor", "contratista"].includes(_role);
                  const alreadyProv = !!ot.provisioning_data?.cpe_supply_completed || !!ot.has_successful_provision;
                  if (corpManual && isFieldRole && !alreadyProv) {
                    return (
                      <div className="rounded-2xl p-4 text-xs" data-testid="corp-manual-only"
                        style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", color: "#1E3A8A" }}>
                        <b>Cliente corporativo:</b> el aprovisionamiento lo hace la oficina (Soporte Técnico) en modo
                        manual. Avise a Soporte Técnico con el equipo instalado.
                      </div>
                    );
                  }

                  // v98.0.0 · Renderizar SOLO el wizard correspondiente al flujo
                  return (
                    <>
                      {(flujo === 1 || flujo === 2) && (
                        <ProvisioningWizard
                          odooTaskId={parseInt(odooTaskId)}
                          provisioned={!!ot.provisioning_data?.cpe_supply_completed || !!ot.has_successful_provision}
                          canProvision={canProvision}
                          oltIp={ni.ip_address}
                          oltName={ni.olt}
                          oltId={ni.olt_id}
                          cpeSerial={ni.cpe_serial}
                          configuredRadiusM={ot.provisioning_flow?.config?.radius_m}
                          subscriptionCode={ot.subscription_code}
                          planName={ot.plan_name || ot.plan_type}
                          clientName={ot.client_name || ot.partner_name}
                          branchOffice={ot.branch_name}
                          branchOfficeId={ot.branch_office_id}
                          clientType={ot.segment_name}
                          subscriptionClientType={ot.client_type}
                          planType={ot.plan_type}
                          internalStatus={ot.smart?.internal_status}
                          onDone={load}
                          onGoToMaterials={() => setActiveTab("tarea")}
                          manualOnly={corpManual}
                        />
                      )}
                      {flujo === 3 && (
                        <CambioEquipoWizard
                          odooTaskId={parseInt(odooTaskId)}
                          provisioned={!!ot.provisioning_data?.cpe_supply_completed || !!ot.has_successful_provision}
                          canEdit={canEdit}
                          internalStatus={ot.smart?.internal_status}
                          clientType={ot.segment_name}
                          subscriptionClientType={ot.client_type}
                          onDone={load}
                          manualOnly={corpManual}
                        />
                      )}
                      {(flujo === 4 || flujo === 5 || flujo === 6) && (
                        <RadioEnlaceWizard
                          odooTaskId={parseInt(odooTaskId)}
                          provisioned={!!ot.provisioning_data?.cpe_supply_completed || !!ot.has_successful_provision}
                          canEdit={canEdit}
                          internalStatus={ot.smart?.internal_status}
                          onDone={load}
                        />
                      )}
                    </>
                  );
                })()}

                {/* v7.0.0 · Historial de Aprovisionamiento (Timeline) */}
                <ProvisioningTimeline odooTaskId={parseInt(odooTaskId)} />

                {/* FASE 2 · Tickets + Acciones de Cancel/Change IP — solo si hay flujo */}
                {ot.provisioning_flow?.flujo != null && (
                  <ProvisioningFase2Actions
                    odooTaskId={parseInt(odooTaskId)}
                    ot={ot}
                    canEdit={canEdit}
                    onRefresh={load}
                  />
                )}

                {/* v97.4.0 · Devolución de equipos: visible en toda OT (editable mientras está en ejecución) */}
                <EquipmentReturnsCard
                  odooTaskId={parseInt(odooTaskId)}
                  canEdit={canEdit && ["en_ruta", "on_site", "en_sitio", "in_progress", "en_ejecucion",
                    "fiscalizada_pendiente_cierre", "pendiente_fiscalizacion", "solucionada_pendiente_fiscalizacion"]
                    .includes((ot.smart?.internal_status || "").toLowerCase())}
                />

                {/* FASE 7 · Verificación técnica + RN15 — solo si flujo === 7 */}
                {ot.provisioning_flow?.flujo === 7 && (
                  <VerificacionTecnica
                    odooTaskId={parseInt(odooTaskId)}
                    ot={ot}
                    canEdit={canEdit}
                    onRefresh={load}
                  />
                )}

                <NeuCard title="Datos de Aprovisionamiento" icon={Cpu} testid="card-provisioning">
                  {!hasProv ? (
                    <div className="text-center py-8">
                      <Cpu size={28} className="mx-auto mb-2 opacity-30" style={{ color: NEU.textMuted }} />
                      <p className="text-xs" style={{ color: NEU.textMuted }}>Sin datos de aprovisionamiento</p>
                    </div>
                  ) : renderFieldGrid(provFields, "prov-field")}
                </NeuCard>
              </div>
            );
          })()}

          {/* Tab: Historial (Timeline 360°) */}
          {activeTab === "historia" && (
            <div className="p-4 max-w-4xl">
              {/* v101.18.0 · Historial ÚNICO de estados (trigger en master_ot: registra todo cambio de estado, técnico o fecha) */}
              <NeuCard title="Estados de la OT · trazabilidad" icon={History} testid="card-status-log">
                {(!ot.status_log || ot.status_log.length === 0) ? (
                  <div className="text-xs italic py-4 text-center" style={{ color: NEU.textMuted }}>
                    Sin registros todavía (se activa al ejecutar scripts/estado_unico_trazabilidad.py --apply).
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]" data-testid="status-log-table">
                      <thead>
                        <tr className="text-left" style={{ color: NEU.textMuted }}>
                          <th className="py-1 pr-2 font-semibold">Fecha</th>
                          <th className="py-1 pr-2 font-semibold">Estado</th>
                          <th className="py-1 pr-2 font-semibold">Técnico</th>
                          <th className="py-1 pr-2 font-semibold">Agenda</th>
                          <th className="py-1 pr-2 font-semibold">Origen</th>
                          <th className="py-1 font-semibold">Quién / nota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ot.status_log.map((l) => {
                          const fmt = (v) => v ? new Date(v).toLocaleString("es-EC", { timeZone: "America/Guayaquil", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
                          const techChanged = (l.from_tech_id || "") !== (l.to_tech_id || "") || (l.from_tech_name || "") !== (l.to_tech_name || "");
                          const schedChanged = (l.from_scheduled_at || "") !== (l.to_scheduled_at || "");
                          return (
                            <tr key={l.id} className="border-t align-top" style={{ borderColor: NEU.border, color: NEU.text }}>
                              <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{fmt(l.changed_at)}</td>
                              <td className="py-1.5 pr-2 whitespace-nowrap">
                                {l.from_label && l.from_label !== l.to_label ? <>{l.from_label} → </> : null}
                                <span className="font-semibold">{l.to_label}</span>
                              </td>
                              <td className="py-1.5 pr-2">
                                {techChanged && l.from_tech_name ? <>{l.from_tech_name} → </> : null}
                                {l.to_tech_name || (techChanged ? "Sin técnico" : "—")}
                              </td>
                              <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{schedChanged ? fmt(l.to_scheduled_at) : "—"}</td>
                              <td className="py-1.5 pr-2 whitespace-nowrap">{ORIGEN_LABEL(l.source)}</td>
                              <td className="py-1.5">
                                {l.actor || ""}{l.note ? <span style={{ color: NEU.textMuted }}>{l.actor ? " · " : ""}{l.note}</span> : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </NeuCard>
              <div className="h-4" />
              <FibiOTCard odooTaskId={odooTaskId} />   {/* v101.196.0 · conversaciones WhatsApp (evidencia) */}
              <div className="h-4" />
              <NeuCard title="Historial · Timeline" icon={History} testid="card-history">
                {(!ot.history || ot.history.length === 0) ? (
                  <div className="text-xs italic py-6 text-center" style={{ color: NEU.textMuted }}>Sin eventos registrados aún.</div>
                ) : (
                  <div className="relative pl-6 py-2">
                    <div className="absolute left-2.5 top-1 bottom-1 w-0.5" style={{ background: `linear-gradient(to bottom, ${NEU.accent}, ${NEU.border}, transparent)` }} />
                    {ot.history.map((h, i) => {
                      const isAssign = h.action === "assign";
                      const isStatus = h.action === "status_change";
                      const tone = isAssign ? "bg-blue-500" : isStatus ? "bg-emerald-500" : "bg-slate-400";
                      return (
                        <div key={h.id || i} className="relative mb-4 last:mb-0">
                          <div className={`absolute -left-4 top-2 w-3.5 h-3.5 rounded-full ${tone} shadow-md`} style={{ borderWidth: 2, borderColor: NEU.bgLight }} />
                          <div className="rounded-2xl px-4 py-3 transition" style={{ background: NEU.bgSurface, border: `1.5px solid ${NEU.border}`, boxShadow: NEU.shadowUp }}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-xs font-bold" style={{ color: NEU.text }}>
                                {isAssign ? "Asignación de técnico" :
                                 isStatus ? `${STATUS_STYLES[h.from_status]?.label || h.from_status || "—"} → ${STATUS_STYLES[h.to_status]?.label || h.to_status}` :
                                 h.action}
                              </div>
                              <div className="text-[10px] font-mono flex-shrink-0" style={{ color: NEU.textMuted }}>
                                {h.performed_at ? new Date(h.performed_at).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                              </div>
                            </div>
                            {h.performed_by_name && (
                              <div className="text-[10px] mt-0.5" style={{ color: NEU.textMuted }}>por <span className="font-semibold" style={{ color: NEU.text }}>{h.performed_by_name}</span></div>
                            )}
                            {h.notes && (
                              <div className="text-[11px] mt-1.5 rounded-lg p-2 leading-relaxed" style={{ background: NEU.bg, color: NEU.text, border: `1px solid ${NEU.border}` }}>
                                {h.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </NeuCard>
            </div>
          )}

          {/* v4.2.4 · Tab: Agentes (Inteligencia de Auditoría) */}
          {activeTab === "agentes" && (
            <div className="p-4 max-w-5xl">
              {agentLoading ? (
                <div className="text-center py-12">
                  <Loader2 size={24} className="animate-spin mx-auto text-slate-400" />
                  <div className="text-xs text-slate-400 mt-2">Cargando historial de agentes...</div>
                </div>
              ) : !agentHistory || agentHistory.events.length === 0 ? (
                <NeuCard title="Agentes IA · Historial" icon={Cpu} testid="card-agents-empty">
                  <div className="text-xs text-gray-400 italic py-8 text-center">
                    Ningún agente ha procesado esta OT aún.
                  </div>
                </NeuCard>
              ) : (
                <div className="space-y-3">
                  {/* Resumen rápido */}
                  <div className="flex items-center gap-3 flex-wrap mb-2" data-testid="agents-summary">
                    {["A1", "A2", "A5", "A6"].map((a) => {
                      const count = agentHistory.events.filter((e) => e.agent === a).length;
                      if (!count) return null;
                      return (
                        <div key={a} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-xs font-bold text-slate-700 border border-slate-200">
                          <span className="w-5 h-5 rounded-full bg-[#0A3747] text-white flex items-center justify-center text-[9px] font-black">{a}</span>
                          <span>{count} evento{count > 1 ? "s" : ""}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Timeline de agentes */}
                  <div className="relative pl-8">
                    <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-purple-400 via-blue-300 to-slate-200" />
                    {agentHistory.events.map((ev, idx) => {
                      const agentColors = {
                        A1: { bg: "bg-purple-500", ring: "ring-purple-200", label: "bg-purple-50 text-purple-700 border-purple-200" },
                        A2: { bg: "bg-blue-500", ring: "ring-blue-200", label: "bg-blue-50 text-blue-700 border-blue-200" },
                        A5: { bg: "bg-amber-500", ring: "ring-amber-200", label: "bg-amber-50 text-amber-700 border-amber-200" },
                        A6: { bg: "bg-emerald-500", ring: "ring-emerald-200", label: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                      };
                      const ac = agentColors[ev.agent] || agentColors.A1;
                      const isSuccess = ["approved", "scheduled", "assigned", "ok", "coverage_full_waiting_client"].includes((ev.result || "").toLowerCase());
                      const isError = ["rejected", "error", "no_slots", "no_techs", "failed"].includes((ev.result || "").toLowerCase());

                      return (
                        <div key={idx} className="relative mb-4 last:mb-0" data-testid={`agent-event-${idx}`}>
                          {/* Dot */}
                          <div className={`absolute -left-5 top-2.5 w-4 h-4 rounded-full ${ac.bg} border-2 border-white shadow-md ring-2 ${ac.ring}`} />

                          <div className="bg-white rounded-xl border border-slate-150 px-4 py-3 shadow-sm hover:shadow transition">
                            {/* Header */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black border ${ac.label}`}>
                                {ev.agent}
                              </span>
                              <span className="text-xs font-bold text-slate-800">
                                {ev.agent_name}
                              </span>
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                                isSuccess ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                isError ? "bg-red-50 text-red-700 border-red-200" :
                                "bg-slate-50 text-slate-600 border-slate-200"
                              }`}>
                                {isSuccess ? "✅" : isError ? "❌" : "⏳"} {ev.result}
                              </span>
                              <span className="ml-auto text-[10px] font-mono text-slate-400">
                                {ev.ts ? new Date(ev.ts).toLocaleString("es-EC", {
                                  day: "2-digit", month: "2-digit", year: "2-digit",
                                  hour: "2-digit", minute: "2-digit",
                                }) : ""}
                              </span>
                            </div>

                            {/* Detalles inline legibles */}
                            {ev.details && typeof ev.details === "object" && (
                              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
                                {Object.entries(ev.details).filter(([, v]) => v != null && v !== "").map(([k, v]) => (
                                  <div key={k} className="text-[11px]">
                                    <span className="text-slate-400 font-semibold">{k}: </span>
                                    <span className="text-slate-700 font-mono">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* JSON raw colapsable para auditoría profunda */}
                            {(ev.context || ev.details) && (
                              <details className="mt-2">
                                <summary className="text-[10px] text-slate-400 cursor-pointer hover:text-slate-600 font-semibold select-none">
                                  Ver JSON de diagnóstico
                                </summary>
                                <pre className="mt-1 text-[10px] bg-slate-50 rounded-lg p-2 border border-slate-100 overflow-x-auto max-h-40 font-mono text-slate-600 leading-relaxed">
                                  {JSON.stringify(ev.context || ev.details, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
          {/* v101.267.0 · WhatsApp del cliente a la DERECHA: atender (tomar, devolver a Fibi, cerrar, chatear) sin salir de la OT */}
          <aside className="xl:sticky xl:top-4" data-testid="ot-whatsapp-aside">
            <OTWhatsAppPanel odooTaskId={odooTaskId} />
          </aside>
        </div>
      </div>


      {/* v99.1.0 · Modal de cambio de tipo de solución */}
      {showChangeSolution && ot && (
        <ChangeSolutionModal
          ot={ot}
          onClose={() => setShowChangeSolution(false)}
          onDone={load}
        />
      )}

      {showAssign && (
        <AssignDrawer
          techs={techs}
          current={smart.technician_id}
          onClose={() => { setShowAssign(false); setOutsideShiftData(null); }}
          onSubmit={doAssign}
        />
      )}

      {/* v101.14.0 · Crear tarea hija en Odoo */}
      {showCreateChild && ot && (
        <CreateChildTaskModal
          ot={ot}
          onClose={() => setShowCreateChild(false)}
          onDone={(childId) => {
            setShowCreateChild(false);
            load();
            if (childId) {
              toast.success(`Hija #${childId} creada. Abriendo…`, { duration: 2500 });
              setTimeout(() => navigate(`/ot-smart/${childId}`), 600);
            }
          }}
        />
      )}

      {/* v101.16.0 · Wizard: actualizar GPS del cliente en Odoo */}
      {showGpsWizard && ot && (
        <UpdateGpsOdooWizard
          ot={ot}
          coordLat={coordLat}
          coordLon={coordLon}
          coordReason={coordReason}
          coordSaving={coordSaving}
          setCoordLat={setCoordLat}
          setCoordLon={setCoordLon}
          setCoordReason={setCoordReason}
          onSave={pushCoordsToOdoo}
          onClose={() => setShowGpsWizard(false)}
        />
      )}

      {/* v101.96.0 · Wizard: actualizar dirección del cliente en Odoo */}
      {showAddrWizard && ot && (
        <UpdateAddressOdooWizard
          ot={ot}
          street={addrStreet} street2={addrStreet2} reason={addrReason} saving={addrSaving}
          setStreet={setAddrStreet} setStreet2={setAddrStreet2} setReason={setAddrReason}
          onSave={pushAddressToOdoo}
          onClose={() => setShowAddrWizard(false)}
        />
      )}

      {/* v101.30.0 · Cancelar OT: solo comentario */}
      {cancelOpen && (
        <CancelOtModal
          odooTaskId={odooTaskId}
          onClose={() => setCancelOpen(false)}
          onConfirm={async (comment, reasons) => { await doStatus("cancelled", comment, reasons); setCancelOpen(false); }}
        />
      )}
      {/* v99.0.0 · Modal de override: Asignar fuera de turno */}
      {/* v99.0.0 / v101.9.0 · Override: fuera de turno o split de suscripción */}
      {outsideShiftData && (
        <OutsideShiftOverrideModal
          data={outsideShiftData}
          onCancel={() => setOutsideShiftData(null)}
          onConfirm={(reason) => {
            const isSub = outsideShiftData.code === "SUBSCRIPTION_SIBLING_TECH";
            doAssign(
              outsideShiftData.technicianId,
              outsideShiftData.priority,
              outsideShiftData.scheduledAt,
              outsideShiftData.notes,
              !isSub,  // forceOutsideShift
              reason,
              isSub,   // forceSubSplit · v101.9.0
            );
          }}
        />
      )}

      {/* v4.2.2 · Panel de auditoría de fiscalización */}
      {showAudit && (
        <AuditFiscalizationModal
          ot={ot}
          onClose={() => setShowAudit(false)}
          onDone={() => reload()}
        />
      )}

      {/* Modal Comentar · v2.8.8.9 */}
      {commentOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !commentSaving && setCommentOpen(false)}
          data-testid="comment-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-lg w-full max-w-md p-5 shadow-xl"
            data-testid="comment-modal"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-[#0A3747] flex items-center gap-2" style={{ fontFamily: "Space Grotesk" }}>
                <MessageSquare size={16} /> Comentar bitácora OT SMART
              </h3>
              <button
                onClick={() => setCommentOpen(false)}
                disabled={commentSaving}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none disabled:opacity-40"
                data-testid="btn-close-comment-modal"
              >×</button>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              El comentario quedará en el histórico de la OT <span className="font-mono">{ot.afc_code || `#${ot.odoo_task_id}`}</span>.
            </p>
            <textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={4}
              placeholder="Escribe el comentario o nota interna…"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              data-testid="input-comment-text"
              autoFocus
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setCommentOpen(false)}
                disabled={commentSaving}
                className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50 disabled:opacity-40"
                data-testid="btn-cancel-comment"
              >Cancelar</button>
              <button
                type="button"
                onClick={async () => {
                  const note = commentText.trim();
                  if (!note) { toast.error("Escribe algún texto"); return; }
                  setCommentSaving(true);
                  try {
                    await api.post(`/v1/ot_smart/${ot.odoo_task_id}/comment`, { note });
                    toast.success("Comentario agregado a la bitácora");
                    setCommentOpen(false);
                    setCommentText("");
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "No se pudo guardar el comentario");
                  } finally { setCommentSaving(false); }
                }}
                disabled={commentSaving || !commentText.trim()}
                className="px-4 py-2 bg-[#0A3747] text-white text-sm rounded hover:bg-[#135166] disabled:opacity-40 flex items-center gap-1.5"
                data-testid="btn-submit-comment"
              >
                {commentSaving ? <Loader2 size={13} className="animate-spin" /> : <MessageSquare size={13} />}
                Guardar comentario
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}

function stats_gps_msg(ot) {
  if (ot.partner_lat === 0 && ot.partner_lon === 0) return "coordenadas 0,0";
  return "coordenadas nulas";
}

/* ═══════════════════════════════════════════════════════════════════
 * Light Neumorphic theme · v6.3.2
 * ═══════════════════════════════════════════════════════════════════ */
const NEU = {
  bg: "#F0F2F5",           // Page bg - soft gray
  bgLight: "#FFFFFF",      // Card bg
  bgSurface: "#F7F8FA",   // Elevated surface / inset bg
  border: "#E2E6EB",      // Subtle border
  text: "#1A2B3C",        // Primary text - dark navy
  textMuted: "#6B7B8D",   // Secondary text
  textLabel: "#8896A4",   // Label text
  accent: "#0A3747",      // Brand accent (dark teal)
  accentSoft: "rgba(10,55,71,0.08)",
  shadowUp: "4px 4px 12px rgba(0,0,0,0.06), -2px -2px 8px rgba(255,255,255,0.8)",
  shadowInset: "inset 2px 2px 5px rgba(0,0,0,0.05), inset -1px -1px 3px rgba(255,255,255,0.7)",
  green: "#059669",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  amber: "#D97706",
  amberBg: "#FFFBEB",
  amberBorder: "#FDE68A",
  red: "#DC2626",
  redBg: "#FEF2F2",
  redBorder: "#FECACA",
};

// v96.4.0 · SSID con prefijo obligatorio ALFANET_ (el usuario define solo el sufijo)
const SSID_PREFIX = "ALFANET_";
const ssidSuffix = (full) => (full || "").startsWith(SSID_PREFIX) ? full.slice(SSID_PREFIX.length) : (full || "");
const cleanSuffix = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9_-]/g, "").slice(0, 24);
const ssidValid = (full) => (full || "").startsWith(SSID_PREFIX) && ssidSuffix(full).length >= 1;
function SsidSuffixInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-semibold text-gray-500">{label}</label>
      <div className="flex items-stretch rounded-lg overflow-hidden border border-gray-300 bg-white">
        <span className="px-2 flex items-center text-xs font-mono font-bold bg-gray-100 text-[#0A3747] select-none">{SSID_PREFIX}</span>
        <input value={ssidSuffix(value)} onChange={(e) => onChange(SSID_PREFIX + cleanSuffix(e.target.value))}
          placeholder="SUFIJO (ej. FAMILIA_PEREZ)" className="flex-1 px-2 py-2 text-xs font-mono outline-none"
          data-testid={testid} />
      </div>
    </div>
  );
}

// v101.19.0 · Unificar redes (APP + web, incl. traslado): 1 SSID UI → API ssid24/ssid5 iguales
function UnifyNetworksToggle({ checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer select-none" data-testid={testid}>
      <span className="min-w-0">
        <span className="block text-[11px] font-bold" style={{ color: "#0A3747" }}>Unificar redes</span>
        <span className="block text-[10px]" style={{ color: "#64748B" }}>Un solo nombre para 2.4 y 5 GHz</span>
      </span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-cyan-600 shrink-0" />
    </label>
  );
}

// v96.2.0 · Consulta al Web Service de Alfanet: equipos que siguen en poder del cliente
/* ── v97.4.0 · Devolución de equipos (web) · misma tabla que la app y cuenta para el cierre ── */
const RET_CONDITIONS = [
  { v: "working", l: "Funcionando" },
  { v: "damaged", l: "Dañado" },
  { v: "obsolete", l: "Obsoleto" },
  { v: "lost", l: "Extraviado" },
];

function EquipmentReturnsCard({ odooTaskId, canEdit, prefillSerial = "", title = "Devolución de equipos", defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen || !!prefillSerial);
  const [data, setData] = useState(null);
  const [clientEq, setClientEq] = useState(null);
  const [loadingEq, setLoadingEq] = useState(false);
  const [serial, setSerial] = useState(prefillSerial || "");
  const [condition, setCondition] = useState("working");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/v1/provisioning/equipment-returns/${odooTaskId}`);
      setData(d);
    } catch { setData({ items: [], count: 0 }); }
  };
  useEffect(() => { if (odooTaskId) load(); /* eslint-disable-next-line */ }, [odooTaskId]);
  useEffect(() => { if (prefillSerial) setSerial(prefillSerial); }, [prefillSerial]);

  const loadClientEq = async () => {
    setLoadingEq(true);
    try {
      const { data: d } = await api.get(`/v1/ot_smart/${odooTaskId}/client-equipment`);
      setClientEq(d);
      if (!d.ok) toast.error(d.message || "No se pudo consultar los equipos del cliente");
    } catch (e) {
      setClientEq({ ok: false, message: e?.response?.data?.detail || "Error al consultar" });
    } finally { setLoadingEq(false); }
  };

  const register = async (sn) => {
    const s = (sn || serial || "").trim();
    if (s.length < 3) { toast.error("Ingrese la serie del equipo que se devuelve"); return; }
    setSaving(true);
    try {
      await api.post(`/v1/provisioning/equipment-returns/${odooTaskId}`, { serial_number: s, condition, notes: notes || null });
      toast.success(`Devolución de ${s} registrada`);
      setSerial(""); setNotes("");
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo registrar la devolución");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/v1/provisioning/equipment-returns/${odooTaskId}/${id}`);
      toast.success("Devolución eliminada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  const items = data?.items || [];
  const returned = new Set(items.map((x) => (x.serial_number || "").toUpperCase()));
  const condLabel = (c) => (RET_CONDITIONS.find((x) => x.v === c) || {}).l || c;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "#F8FAFC", border: "1.5px solid #CBD5E1" }}
      data-testid="equipment-returns-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 px-4 py-3 text-left"
        data-testid="btn-toggle-equipment-returns">
        <RotateCcw size={15} style={{ color: "#0A3747" }} />
        <span className="text-xs font-bold uppercase tracking-wider flex-1" style={{ color: "#0A3747" }}>{title}</span>
        {data?.requires_return && (
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${data.count ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}>
            {data.count ? "Obligatoria · cumplida" : "Obligatoria"}
          </span>
        )}
        {!data?.requires_return && data?.suggested && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Sugerida</span>
        )}
        <span className="text-[10px] font-mono text-gray-500">{items.length} registrada(s)</span>
        <span className="text-gray-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {canEdit && (
            <>
              <div>
                <button onClick={loadClientEq} disabled={loadingEq}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50"
                  style={{ background: "#0A3747", color: "#E0E621" }} data-testid="btn-returns-client-eq">
                  {loadingEq ? <Loader2 size={12} className="animate-spin" /> : <Cpu size={12} />}
                  {clientEq ? "Volver a consultar equipos del cliente" : "Ver equipos del cliente (Alfanet)"}
                </button>
                {clientEq && !clientEq.ok && <div className="text-[11px] text-red-600 mt-1">{clientEq.message}</div>}
                {clientEq?.ok && (clientEq.equipment || []).length === 0 && (
                  <div className="text-[11px] text-gray-500 mt-1">El cliente no tiene equipos registrados en esta suscripción.</div>
                )}
                {clientEq?.ok && (clientEq.equipment || []).map((e, i) => {
                  const sn = e.serial_number || "";
                  const done = returned.has(sn.toUpperCase());
                  return (
                    <div key={`${sn}-${i}`} className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0"
                      data-testid={`returns-client-eq-${i}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-semibold text-gray-800 truncate">{e.product}</div>
                        <div className="text-[10px] text-gray-500 font-mono">
                          Serie: {sn || "—"} · {e.is_already_invoiced ? "Facturado" : "No facturado"}
                        </div>
                      </div>
                      {done ? (
                        <span className="text-[10px] font-bold text-emerald-700 flex items-center gap-1"><Check size={12} /> Devuelto</span>
                      ) : (
                        <button onClick={() => register(sn)} disabled={saving || !sn}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold text-white disabled:opacity-40"
                          style={{ background: "#16A34A" }} data-testid={`btn-return-client-eq-${i}`}>
                          <RotateCcw size={11} /> Devolver
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input value={serial} onChange={(e) => setSerial(e.target.value.toUpperCase())}
                  placeholder="Serie del equipo devuelto" data-testid="input-return-serial"
                  className="px-3 py-2 rounded-lg text-xs font-mono" style={{ border: "1px solid #CBD5E1", background: "#fff" }} />
                <select value={condition} onChange={(e) => setCondition(e.target.value)} data-testid="select-return-condition"
                  className="px-3 py-2 rounded-lg text-xs" style={{ border: "1px solid #CBD5E1", background: "#fff" }}>
                  {RET_CONDITIONS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones (opcional)"
                  className="px-3 py-2 rounded-lg text-xs" style={{ border: "1px solid #CBD5E1", background: "#fff" }} />
              </div>
              <button onClick={() => register()} disabled={saving || serial.trim().length < 3}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-40"
                style={{ background: "#16A34A" }} data-testid="btn-register-return">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Registrar devolución a bodega
              </button>
            </>
          )}

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Devoluciones registradas</div>
            {items.length === 0 && <div className="text-[11px] text-gray-500">Aún no hay devoluciones en esta OT.</div>}
            {items.map((r) => (
              <div key={r.id} className="flex items-center gap-2 py-1.5 border-b border-gray-100 last:border-0" data-testid={`return-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-mono font-bold text-gray-800">{r.serial_number}</div>
                  <div className="text-[10px] text-gray-500">
                    {condLabel(r.condition)}{r.notes ? ` · ${r.notes}` : ""}{r.returned_by_name ? ` · ${r.returned_by_name}` : ""}
                  </div>
                </div>
                {canEdit && (
                  <button onClick={() => remove(r.id)} className="p-1.5 rounded-lg hover:bg-red-50" title="Eliminar"
                    data-testid={`btn-delete-return-${r.id}`}>
                    <Trash2 size={13} style={{ color: "#DC2626" }} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── v97.4.0 · Gate de materiales: primero la descarga de materiales, luego el aprovisionamiento ── */
function MaterialsBlockedBanner({ odooTaskId, onReady }) {
  const [checking, setChecking] = useState(false);
  const recheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.get(`/v1/provisioning/check-materials/${odooTaskId}`);
      if (data?.blocked) toast.error("Aún no hay materiales registrados en la OT.");
      else { toast.success("Materiales registrados. Puede continuar."); onReady && onReady(data); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo verificar los materiales");
    } finally { setChecking(false); }
  };
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl mb-4" style={{ background: "#FEF2F2", border: "1.5px solid #FECACA" }}
      data-testid="materials-gate-banner">
      <AlertTriangle size={18} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
      <div className="flex-1">
        <div className="text-xs font-bold" style={{ color: "#991B1B" }}>Primero la descarga de materiales</div>
        <div className="text-[11px] mt-1" style={{ color: "#B91C1C" }}>
          Sin materiales registrados en la OT no se puede aprovisionar. El técnico debe registrarlos en la app
          (Centro de Trabajo → Materiales) y luego continuar aquí.
        </div>
        <button onClick={recheck} disabled={checking}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50"
          style={{ background: "#0A3747", color: "#E0E621" }} data-testid="btn-materials-recheck">
          {checking ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Volver a verificar
        </button>
      </div>
    </div>
  );
}

function useMaterialsGate(odooTaskId) {
  const [mc, setMc] = useState(null);
  const check = async () => {
    try {
      const { data } = await api.get(`/v1/provisioning/check-materials/${odooTaskId}`);
      setMc(data);
      return data;
    } catch { setMc(null); return null; }
  };
  return { mc, setMc, check, blocked: !!mc?.blocked };
}

function ClientEquipmentCard({ odooTaskId, autoLoad = false, hint = null }) {
  const [state, setState] = useState({ loading: false, data: null });
  const consult = async () => {
    setState({ loading: true, data: null });
    try {
      const { data } = await api.get(`/v1/ot_smart/${odooTaskId}/client-equipment`);
      setState({ loading: false, data });
      if (!data.ok) toast.error(data.message || "No se pudo consultar los equipos");
    } catch (e) {
      setState({ loading: false, data: { ok: false, message: e?.response?.data?.detail || "Error al consultar" } });
    }
  };
  useEffect(() => {
    if (autoLoad && odooTaskId) consult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, odooTaskId]);
  const d = state.data;
  return (
    <NeuCard title="Equipos del cliente (Alfanet)" icon={Cpu} testid="card-client-equipment">
      {hint && <div className="text-[10px] text-gray-500 mb-1.5">{hint}</div>}
      <button onClick={consult} disabled={state.loading || !odooTaskId}
        className="mb-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50"
        style={{ background: "#0A3747", color: "#E0E621" }} data-testid="btn-client-equipment">
        {state.loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        {d ? "Volver a consultar" : "Consultar en Alfanet"}
      </button>
      {d && !d.ok && <div className="text-[11px] text-red-600">{d.message}</div>}
      {d && d.ok && (d.equipment || []).length === 0 && (
        <div className="text-[11px] text-gray-500">El cliente no tiene equipos registrados en esta suscripción.</div>
      )}
      {d && d.ok && (d.equipment || []).map((e, i) => (
        <div key={`${e.serial_number}-${i}`} className="py-1.5 border-b border-gray-100 last:border-0" data-testid={`client-eq-${i}`}>
          <div className="text-[11px] font-semibold text-gray-800">{e.product}</div>
          <div className="flex items-center gap-2 text-[10px] text-gray-500 font-mono">
            <span>Serie: {e.serial_number || "—"}</span>
            <span className={`px-1.5 py-0.5 rounded font-sans font-bold ${e.is_already_invoiced ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
              {e.is_already_invoiced ? "Facturado" : "No facturado"}
            </span>
          </div>
        </div>
      ))}
      {d && d.ok && <div className="text-[9px] text-gray-400 mt-1">Cédula/RUC {d.vat} · suscripción {d.subscription_code}</div>}
    </NeuCard>
  );
}

function NeuCard({ title, icon: Icon, children, testid, accent, variant }) {
  const isSuccess = variant === "success";
  const isWarning = variant === "warning";
  const isError = variant === "error";
  const borderColor = isSuccess ? NEU.greenBorder : isWarning ? NEU.amberBorder : isError ? NEU.redBorder : NEU.border;
  const bgColor = isSuccess ? NEU.greenBg : isWarning ? NEU.amberBg : isError ? NEU.redBg : NEU.bgLight;
  return (
    <div
      className="rounded-3xl p-4 transition-all duration-200"
      style={{
        background: bgColor,
        border: `1.5px solid ${borderColor}`,
        boxShadow: NEU.shadowUp,
      }}
      data-testid={testid}
    >
      {title && (
        <div className="flex items-center gap-1.5 mb-3">
          {Icon && <Icon size={12} style={{ color: accent || NEU.accent }} />}
          <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: accent || NEU.textLabel }}>
            {title}
          </span>
        </div>
      )}
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function NeuKV({ label, value, mono, bold, icon: Icon, accent }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <div className="flex-shrink-0 w-24 flex items-center gap-1" style={{ color: NEU.textLabel }}>
        {Icon && <Icon size={10} />}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <div
        className={`flex-1 min-w-0 truncate ${mono ? "font-mono" : ""} ${bold ? "font-semibold" : ""}`}
        style={{ color: accent || NEU.text }}
      >
        {value}
      </div>
    </div>
  );
}

/* ── v99.1.0 · Modal de cambio de tipo de solución ── */
function ChangeSolutionModal({ ot, onClose, onDone }) {
  const [nodes, setNodes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // v101.61.0 · Permitir tipos de OTRA actividad de Odoo: el mismo botón actualiza la actividad en Odoo (JSON-RPC)
  const [allNodes, setAllNodes] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [q, setQ] = useState("");
  // v101.82.0 · No se puede cambiar si la OT está aprovisionada o tiene materiales cargados
  const [block, setBlock] = useState(null);
  useEffect(() => {
    api.get(`/v1/ot_smart/${ot.odoo_task_id}/change-solution-node/check`)
      .then(({ data }) => setBlock(data && data.allowed === false ? data : null))
      .catch(() => setBlock(null));
  }, [ot.odoo_task_id]);

  // v101.137.0 · Mismo origen que «Reasignar tipo de solución»: tipos que aplican al tipo de cliente (contrato) y al
  //              plan de la OT; «Mostrar todos» para todos los roles.
  const canSeeAllTypes = true;
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/v1/ot_smart/${ot.odoo_task_id}/compatible-nodes${showAll ? "?all=true" : ""}`);
        if (!alive) return;
        if (showAll) setAllNodes(data.nodes || []); else setNodes(data.nodes || []);
      } catch {
        if (alive) { if (showAll) setAllNodes([]); else setNodes([]); }
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [ot.odoo_task_id, showAll]);

  const visibleNodes = (showAll ? allNodes : nodes)
    .filter(n => !q.trim() || `${n.name} ${n.odoo_activity_type}`.toLowerCase().includes(q.trim().toLowerCase()))
    .slice(0, 200);

  const submit = async () => {
    if (!selected || reason.length < 5) return;
    setSubmitting(true);
    try {
      const sel = (showAll ? allNodes : nodes).find(n => n.id === selected);
      const mismatch = !!sel?.code_mismatch;
      if (mismatch && !window.confirm(
        `Se cambiará la ACTIVIDAD de la tarea en Odoo:\n${ot.activity_type_code} → ${sel.odoo_activity_type}\n\n¿Continuar?`)) {
        setSubmitting(false);
        return;
      }
      const { data } = await api.put(`/v1/ot_smart/${ot.odoo_task_id}/change-solution-node`, {
        new_node_id: selected,
        reason,
        force: !!mismatch,
        all_types: showAll,   // v101.137.0
      }, { timeout: 90000 });
      toast.success(data?.message || "Tipo de solución cambiado correctamente");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cambiar tipo de solución");
    }
    setSubmitting(false);
  };

  const selectedNode = (showAll ? allNodes : nodes).find(n => n.id === selected);
  const willChangeOdoo = !!selectedNode?.code_mismatch;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="change-solution-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <h3 className="font-bold text-lg text-gray-900 mb-1" style={{ fontFamily: "Chivo" }}>Cambiar tipo de solución</h3>
        <p className="text-xs text-gray-500 mb-4">
          Código Odoo: <code className="bg-gray-100 px-1 rounded">{ot.activity_type_code}</code> · OT #{ot.odoo_task_id}
        </p>
        {block && (
          <div className="mb-3 p-3 rounded-lg text-sm bg-red-50 border border-red-300 text-red-800 font-medium" data-testid="change-solution-blocked">
            {block.message}
          </div>
        )}
        {block ? (
          <div className="flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cerrar</button>
          </div>
        ) : loading ? (
          <p className="text-sm text-gray-500">Cargando nodos…</p>
        ) : (
          <>
            {canSeeAllTypes && (
            <label className="flex items-center gap-2 text-xs text-gray-700 mb-2" data-testid="change-solution-show-all">
              <input type="checkbox" checked={showAll} onChange={(e) => { setShowAll(e.target.checked); setSelected(null); }} />
              Ver todos los tipos de solución (si cambia la actividad, se actualiza en Odoo)
            </label>
            )}
            {showAll && (
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar tipo o código…"
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs mb-2" data-testid="change-solution-search" />
            )}
            <div className="space-y-1 mb-4 max-h-48 overflow-auto">
              {visibleNodes.length === 0 ? (
                <p className="text-sm text-gray-500">No hay tipos de solución para el tipo de cliente y plan de esta OT. Marque «Ver todos los tipos de solución».</p>
              ) : visibleNodes.map(n => (
                <label key={n.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border text-sm ${selected === n.id ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"}`}>
                  <input type="radio" name="node" value={n.id} checked={selected === n.id} onChange={() => setSelected(n.id)} />
                  <span className="font-medium">{n.name}</span>
                  {n.has_children && <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 text-slate-600">nivel con subtipos</span>}
                  {n.is_default_for_code && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700">★ DEFAULT</span>}
                  {n.pft_name && <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-100 text-indigo-700">{n.pft_name}</span>}
                  {showAll && <code className="ml-auto text-[9px] px-1 py-0.5 rounded bg-gray-100 text-gray-600">{n.odoo_activity_type}</code>}
                </label>
              ))}
            </div>
            {willChangeOdoo && (
              <div className="mb-3 p-2 rounded-lg text-xs bg-amber-50 border border-amber-200 text-amber-800" data-testid="change-solution-odoo-warning">
                Se actualizará la actividad de la tarea en Odoo: <b>{ot.activity_type_code}</b> → <b>{selectedNode.odoo_activity_type}</b>.
                Si Odoo no lo acepta, no se cambia nada.
              </div>
            )}
            <div className="mb-4">
              <label className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1 block">Motivo del cambio *</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Motivo (mín. 5 caracteres)…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" rows={2}
                data-testid="change-solution-reason" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button disabled={!selected || reason.trim().length < 5 || submitting} onClick={submit}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="change-solution-confirm">
                {submitting ? (willChangeOdoo ? "Actualizando Odoo…" : "Guardando…") : (willChangeOdoo ? "Cambiar tipo y actualizar Odoo" : "Cambiar tipo de solución")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── v101.16.0 · Wizard: actualizar GPS del cliente en Odoo ── */
function UpdateGpsOdooWizard({
  ot, coordLat, coordLon, coordReason, coordSaving,
  setCoordLat, setCoordLon, setCoordReason, onSave, onClose,
}) {
  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="gps-odoo-wizard">
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
            <MapPin size={18} className="text-teal-700" /> Actualizar GPS en Odoo
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="gps-odoo-wizard-close">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Escribe <code>partner_latitude/longitude</code> en el cliente de Odoo
          ({ot.partner_name || ot.client_name || "—"}) y actualiza el espejo local.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Latitud</label>
            <input
              type="number"
              step="any"
              value={coordLat}
              onChange={(e) => setCoordLat(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-xs font-mono border border-slate-200 rounded-lg"
              data-testid="input-odoo-coord-lat"
              placeholder="-2.170998"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Longitud</label>
            <input
              type="number"
              step="any"
              value={coordLon}
              onChange={(e) => setCoordLon(e.target.value)}
              className="w-full mt-0.5 px-2 py-1.5 text-xs font-mono border border-slate-200 rounded-lg"
              data-testid="input-odoo-coord-lon"
              placeholder="-79.922359"
            />
          </div>
        </div>
        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Motivo</label>
          <input
            type="text"
            value={coordReason}
            onChange={(e) => setCoordReason(e.target.value)}
            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg"
            data-testid="input-odoo-coord-reason"
            placeholder="Cliente se mudó / GPS incorrecto en Odoo"
          />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            onClick={() => {
              if (ot?.partner_lat != null && ot?.partner_lon != null) {
                setCoordLat(String(ot.partner_lat));
                setCoordLon(String(ot.partner_lon));
              }
            }}
            className="px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid="btn-odoo-coord-fill-current"
          >
            Usar actuales
          </button>
          <button
            type="button"
            onClick={() => {
              if (!navigator.geolocation) {
                toast.error("Geolocalización no disponible en este navegador");
                return;
              }
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  setCoordLat(String(pos.coords.latitude));
                  setCoordLon(String(pos.coords.longitude));
                },
                () => toast.error("No se pudo obtener tu ubicación"),
                { enableHighAccuracy: true, timeout: 15000 },
              );
            }}
            className="px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid="btn-odoo-coord-browser-gps"
          >
            Mi GPS
          </button>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              data-testid="btn-odoo-coord-cancel"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={coordSaving}
              onClick={onSave}
              className="px-3 py-1.5 text-[11px] font-bold rounded-lg bg-[#0A3747] text-[#E0E621] disabled:opacity-60 flex items-center gap-1.5"
              data-testid="btn-odoo-coord-save"
            >
              {coordSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Guardar en Odoo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── v101.96.0 · Wizard: actualizar dirección del cliente en Odoo (mismo proceso que el GPS) ── */
function UpdateAddressOdooWizard({ ot, street, street2, reason, saving, setStreet, setStreet2, setReason, onSave, onClose }) {
  const current = ot.address_full || [ot.address_street, ot.address_street2].filter(Boolean).join(" · ") || "—";
  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="address-odoo-wizard">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
            <MapPin size={18} className="text-teal-700" /> Actualizar dirección en Odoo
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="address-odoo-wizard-close">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-1">
          Escribe la dirección en el cliente de Odoo ({ot.partner_name || ot.client_name || "—"}) y actualiza la OT.
        </p>
        <p className="text-[11px] text-slate-500 mb-4">Actual: <span className="font-medium text-slate-700">{current}</span></p>
        <div className="mb-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Dirección (calle principal)</label>
          <input
            type="text" value={street} maxLength={250}
            onChange={(e) => setStreet(e.target.value)}
            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg"
            data-testid="input-odoo-address-street"
            placeholder="Av. Amazonas N34-120 y Atahualpa"
          />
        </div>
        <div className="mb-2">
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Referencia / calle secundaria (opcional)</label>
          <input
            type="text" value={street2} maxLength={250}
            onChange={(e) => setStreet2(e.target.value)}
            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg"
            data-testid="input-odoo-address-street2"
            placeholder="Edificio azul, 3er piso, frente al parque"
          />
        </div>
        <div className="mb-4">
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Motivo</label>
          <input
            type="text" value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full mt-0.5 px-2 py-1.5 text-xs border border-slate-200 rounded-lg"
            data-testid="input-odoo-address-reason"
            placeholder="Dirección incompleta / cliente se mudó"
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-[11px] font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            data-testid="btn-odoo-address-cancel">Cancelar</button>
          <button type="button" disabled={saving || street.trim().length < 5} onClick={onSave}
            className="px-3 py-1.5 text-[11px] font-bold rounded-lg bg-[#0A3747] text-[#E0E621] disabled:opacity-60 flex items-center gap-1.5"
            data-testid="btn-odoo-address-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Guardar en Odoo
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── v101.14.0 · Modal: crear tarea hija (elige activity type Odoo) ── */
function CreateChildTaskModal({ ot, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [childName, setChildName] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/v1/ot_smart/activity-types-for-child");
        if (!cancelled) setItems(data?.items || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudieron cargar los tipos de actividad");
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      `${it.name || ""} ${it.code || ""}`.toLowerCase().includes(s)
    );
  }, [items, q]);

  const selected = items.find((it) => String(it.id) === String(selectedId));

  const submit = async () => {
    if (!selectedId) return toast.error("Seleccione un tipo de actividad");
    setSaving(true);
    try {
      const body = { activity_type_id: Number(selectedId) };
      if (childName.trim()) body.name = childName.trim();
      const { data } = await api.post(`/v1/ot_smart/${ot.odoo_task_id}/create-child`, body);
      const childId = data?.child_odoo_task_id;
      const num = data?.child_number;
      const imported = data?.imported_to_master;
      toast.success(
        imported
          ? `Tarea hija creada${num ? ` (${num})` : ""} e importada a AlfaField`
          : `Tarea hija creada en Odoo${num ? ` (${num})` : ""}; import pendiente`
      );
      onDone?.(childId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la tarea hija");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="create-child-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-bold text-lg text-gray-900" style={{ fontFamily: "Chivo" }}>
            Crear tarea hija
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" data-testid="create-child-close">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Se creará en Odoo como hija de #{ot.task_number || ot.odoo_task_id} (pestaña Tareas Hijas),
          heredando cliente/suscripción/proyecto. Elija el tipo de actividad
          (sin instalación ni infraestructura).
        </p>

        <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo de actividad *</label>
        <div className="relative mb-2">
          <Search size={13} className="absolute left-2.5 top-2.5 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o código…"
            className="w-full border rounded-lg pl-8 pr-2 py-1.5 text-xs"
            data-testid="create-child-search"
          />
        </div>
        <div className="border rounded-lg max-h-52 overflow-y-auto mb-3" data-testid="create-child-type-list">
          {loading ? (
            <div className="p-6 text-center text-xs text-gray-400">
              <Loader2 size={16} className="animate-spin inline mr-1" /> Cargando tipos…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-xs text-gray-400 text-center">Sin tipos disponibles</div>
          ) : (
            filtered.map((it) => {
              const active = String(it.id) === String(selectedId);
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => {
                    setSelectedId(String(it.id));
                    if (!childName.trim()) setChildName(`HIJA/${(it.name || it.code || "").toUpperCase()}`);
                  }}
                  className={`w-full text-left px-3 py-2 text-xs border-b last:border-b-0 flex items-center justify-between gap-2 ${
                    active ? "bg-indigo-50 text-indigo-900" : "hover:bg-gray-50"
                  }`}
                  data-testid={`create-child-type-${it.code}`}
                >
                  <span>
                    <span className="font-semibold">{it.name}</span>
                    <span className="text-gray-400 ml-1.5 font-mono">{it.code}</span>
                  </span>
                  {active && <Check size={14} className="text-indigo-600 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
        {selected && (
          <div className="text-[10px] text-gray-500 mb-3 font-mono">
            Odoo: activity_type={selected.id} · activity_type_code={selected.code}
          </div>
        )}

        <label className="block text-xs font-semibold text-gray-600 mb-1">Nombre de la hija</label>
        <input
          value={childName}
          onChange={(e) => setChildName(e.target.value)}
          placeholder="HIJA/PRODUCTO EXTRA"
          className="w-full border rounded-lg px-3 py-1.5 text-xs mb-4"
          data-testid="create-child-name"
        />

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50">Cancelar</button>
          <button
            onClick={submit}
            disabled={saving || !selectedId}
            className="px-3 py-1.5 text-xs rounded bg-indigo-700 text-white hover:bg-indigo-800 disabled:opacity-50 flex items-center gap-1.5"
            data-testid="btn-confirm-create-child"
          >
            {saving && <Loader2 size={12} className="animate-spin" />} Crear en Odoo
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── v99.0.0 · Modal de override: asignar fuera de turno ── */
/* ── v99.0.0 / v101.9.0 · Modal override: fuera de turno o split suscripción ── */
// v101.30.0 · Cancelar OT: un solo campo (comentario obligatorio) y un botón
function CancelOtModal({ odooTaskId, onClose, onConfirm }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState([]);           // v101.188.0 · motivos de cancelación
  const [reasonsRequired, setReasonsRequired] = useState(false);
  const ok = comment.trim().length >= 10 && (!reasonsRequired || reasons.length > 0);   // v101.97.0 · comentario (mín. 10)
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" data-testid="cancel-ot-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold text-sm text-red-700">
          <XCircle size={16} /> Cancelar OT
        </div>
        {/* v101.46.2 · Aviso de prerrequisitos (el backend valida y bloquea) */}
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 leading-snug">
          No se puede cancelar si hay <b>aprovisionamiento</b> o <b>materiales</b> registrados.
          Primero: Cancelar aprovisionamiento y/o Liberar materiales.
        </p>
        <CancelReasonPicker odooTaskId={odooTaskId} value={reasons}
          onChange={(codes, req) => { setReasons(codes); setReasonsRequired(req); }} />
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          maxLength={1000}
          autoFocus
          placeholder="Comentario obligatorio (mínimo 10 caracteres): motivo de la cancelación"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          data-testid="cancel-ot-comment"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded border hover:bg-gray-50">Volver</button>
          <button
            disabled={!ok || busy}
            onClick={async () => { setBusy(true); try { await onConfirm(comment.trim(), reasons); } finally { setBusy(false); } }}
            className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 flex items-center gap-1.5"
            data-testid="cancel-ot-confirm"
          >
            {busy && <Loader2 size={12} className="animate-spin" />} Cancelar OT
          </button>
        </div>
      </div>
    </div>
  );
}

function OutsideShiftOverrideModal({ data, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isSubSplit = data?.code === "SUBSCRIPTION_SIBLING_TECH";
  return (
    <div className="fixed inset-0 z-[10000] bg-black/60 flex items-center justify-center p-4" data-testid="outside-shift-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle size={20} className="text-amber-600" />
          </div>
          <h3 className="font-bold text-lg text-gray-900" style={{ fontFamily: "Chivo" }}>
            {isSubSplit ? "Misma suscripción · otro técnico" : "Fuera de turno"}
          </h3>
        </div>
        <p className="text-sm text-gray-600 mb-4" data-testid="outside-shift-detail">{data.detail}</p>
        <div className="mb-4">
          <label className="text-xs font-mono uppercase tracking-wider text-gray-500 mb-1 block">
            {isSubSplit ? "Motivo para usar otro técnico *" : "Motivo de asignación fuera de turno *"}
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Explica por qué se asigna fuera del turno (mín. 5 caracteres)…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
            rows={3}
            data-testid="outside-shift-reason"
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            data-testid="outside-shift-cancel"
          >
            Cancelar
          </button>
          <button
            disabled={reason.trim().length < 5 || submitting}
            onClick={async () => {
              setSubmitting(true);
              try { await onConfirm(reason.trim()); } finally { setSubmitting(false); }
            }}
            className="px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="outside-shift-confirm"
          >
            {submitting ? "Asignando…" : "Asignar de todos modos"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignDrawer({ techs, current, onClose, onSubmit }) {
  const [techId, setTechId] = useState(current || "");
  const [priority, setPriority] = useState("p3");
  const [sched, setSched] = useState("");
  const [notes, setNotes] = useState("");
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return techs;
    const q = search.toLowerCase();
    return techs.filter((t) => (t.name || "").toLowerCase().includes(q) || (t.email || "").toLowerCase().includes(q));
  }, [techs, search]);

  const handleSubmit = async () => {
    if (!techId) {
      toast.error("Selecciona un técnico");
      return;
    }
    setSubmitting(true);
    try {
      const iso = sched ? new Date(sched).toISOString() : null;
      await onSubmit(techId, priority, iso, notes);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end" data-testid="assign-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white shadow-2xl h-full overflow-y-auto animate-slide-in-right">
        <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-[#0A3747]" style={{ fontFamily: "Chivo" }}>Asignar técnico</h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="drawer-close">
              <XCircle size={18} />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1 block">Técnico</label>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filtrar por nombre o email…"
              data-testid="tech-search"
              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded mb-2"
            />
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded divide-y divide-gray-100">
              {filtered.length === 0 && (
                <div className="p-4 text-center text-xs text-gray-400">Sin técnicos</div>
              )}
              {filtered.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTechId(t.id)}
                  data-testid={`tech-${t.id}`}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 ${techId === t.id ? "bg-[#E0E621]/20" : ""}`}
                >
                  <div className={`w-2 h-2 rounded-full ${techId === t.id ? "bg-emerald-500" : "bg-gray-300"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{t.name}</div>
                    <div className="text-[10px] font-mono text-gray-500 truncate">{t.email}</div>
                  </div>
                  <div className="text-[9px] uppercase tracking-wider text-gray-400">{t.role}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1 block">Prioridad</label>
            <div className="grid grid-cols-4 gap-1">
              {["p1", "p2", "p3", "p4"].map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  data-testid={`priority-${p}`}
                  className={`py-1.5 text-xs rounded border ${priority === p ? "bg-[#0A3747] text-white border-[#0A3747]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                >
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1 block">Programar (opcional)</label>
            <input
              type="datetime-local"
              value={sched}
              onChange={(e) => setSched(e.target.value)}
              data-testid="scheduled-at"
              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1 block">Notas</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instrucciones para el técnico…"
              data-testid="notes"
              className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded resize-none"
            />
          </div>

          <button
            onClick={handleSubmit}
            disabled={!techId || submitting}
            data-testid="btn-submit-assign"
            className="w-full py-2 rounded bg-[#E0E621] text-[#0A3747] font-semibold text-sm hover:bg-[#c9cf1e] disabled:opacity-50"
          >
            {submitting ? "Asignando…" : "Confirmar asignación"}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * v3.1.0 · StockCheckCard — valida la receta del tipo de solución contra la
 * bodega asignada al técnico. Muestra rojo/amarillo/verde por material.
 */
function StockCheckCard({ otId }) {
  const [check, setCheck] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/ot/${otId}/stock-check`);
        if (!cancel) setCheck(data);
      } catch (e) {
        if (!cancel) setCheck({ status: "error", message: "Error consultando stock" });
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [otId]);

  if (loading) return null;
  if (!check) return null;
  const level = check.alert_level;
  const variant = level === "red" ? "error" : level === "yellow" ? "warning" : "success";
  const fg = level === "red" ? NEU.red : level === "yellow" ? NEU.amber : NEU.green;
  const label = level === "red" ? "STOCK INSUFICIENTE" : level === "yellow" ? "PENDIENTE VALIDAR" : "STOCK OK";
  return (
    <NeuCard testid="ot-stock-check" variant={variant}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[9px] uppercase tracking-widest font-bold" style={{ color: fg }}>Receta · Stock del técnico</div>
          <div className="text-sm font-bold mt-0.5" style={{ color: fg }}>{label}</div>
        </div>
        {check.warehouse && (
          <div className="text-right text-[10px]" style={{ color: NEU.textMuted }}>
            <div className="font-semibold">{check.warehouse.name}</div>
            <div>Sucursal {check.warehouse.branch}</div>
          </div>
        )}
      </div>
      <div className="text-xs mb-2" style={{ color: NEU.text }}>{check.message}</div>
      {check.items && check.items.length > 0 && (
        <table className="w-full text-xs">
          <thead className="text-[9px] uppercase" style={{ color: fg }}>
            <tr>
              <th className="text-left py-1">Material</th>
              <th className="text-center py-1 w-16">Mín</th>
              <th className="text-center py-1 w-20">Disponible</th>
              <th className="text-center py-1 w-6"></th>
            </tr>
          </thead>
          <tbody>
            {check.items.map(m => (
              <tr key={m.product_id} style={{ borderTop: `1px solid ${NEU.border}` }}>
                <td className="py-1" style={{ color: NEU.text }}><span className="font-mono text-[10px] opacity-60">{m.product_code}</span> · {m.product_name}</td>
                <td className="py-1 text-center tabular-nums" style={{ color: NEU.textMuted }}>{m.qty_min}</td>
                <td className="py-1 text-center tabular-nums font-semibold" style={{ color: NEU.text }}>{m.qty_available === null ? "—" : m.qty_available}</td>
                <td className="py-1 text-center">
                  {m.is_ok === true && <span title="OK" style={{ color: NEU.green }}>✓</span>}
                  {m.is_ok === false && <span title="Insuficiente" style={{ color: NEU.red }}>✗</span>}
                  {m.is_ok === null && <span title="Sin info" className="opacity-40">?</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </NeuCard>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   v7.0.0 · ProvisioningWizard — Discover ONTs + Selección + Ejecución
   ═══════════════════════════════════════════════════════════════════════════ */
// VLAN: canónico = sale_subscription.client_type (residential|corporate).
// Fallback segment solo si aún no hay sync de client_type.
// v101.83.0 · WiFi (Axxess) solo clientes residenciales (Residencial / Mi Negocio) de fibra; nunca Radio Enlace.
//            Misma regla que el backend (odoo_close_v2.axxess_applies). Sin dato de cliente → aplica.
// v101.202.0 · Corporativo NUNCA pide WiFi (Axxess): misma regla que el backend (odoo_close_v2.es_corporativo).
function isCorporateClient(subscriptionClientType, segmentName, planName) {
  const seg = String(segmentName || "").trim().toLowerCase();
  const plan = String(planName || "").trim().toLowerCase();
  if (/corporativ|corporate/.test(plan) || /corporativ/.test(seg)) return true;
  const ct = String(subscriptionClientType || "").trim().toLowerCase();
  if (["corporate", "corporativo", "empresarial", "empresa", "business"].includes(ct)) return true;
  if (ct === "residential" || ct === "residencial") return false;
  if (!seg) return false;
  return seg !== "residencial" && seg !== "mi negocio";
}
function needsAxxessForClient(tipoEquipo, subscriptionClientType, segmentName, planName) {
  if (!tipoEquipo || tipoEquipo === "ONU") return false;
  return !isCorporateClient(subscriptionClientType, segmentName, planName);
}

function resolveVlanClientType(subscriptionClientType, segmentName, _planName) {
  const ct = (subscriptionClientType || "").trim().toLowerCase();
  if (ct === "residential" || ct === "corporate") return ct;
  const seg = (segmentName || "").trim().toLowerCase();
  if (!seg) return null;
  if (seg === "residencial" || seg === "mi negocio") return "residential";
  return "corporate";
}

// v101.45.3 · Descargo manual: prellenar SKU único y validar con mensajes (no botón mudo)
function buildDischargeItemsFromRecipe(recipeItems) {
  return (recipeItems || []).map((m) => {
    let product_id = m.product_id ?? null;
    let product_name = m.product_name || m.category || "Material";
    let serial = "";
    const skus = m.skus_candidatos || [];
    if (m.match_mode === "category" && skus.length === 1) {
      product_id = skus[0].product_id;
      product_name = skus[0].product_name || product_name;
      const sns = skus[0].serial_numbers || [];
      if (sns.length === 1) serial = sns[0];
    }
    return { product_id, product_name, qty: Math.max(m.qty_min || 1, 1), serial };
  });
}

// v101.139.0 · Excedentes sobre lo gratuito de la receta (renglón i de la receta ↔ renglón i del descargo)
function excessLinesForDischarge(recipeItems, dischargeItems) {
  const out = [];
  (recipeItems || []).forEach((r, i) => {
    const it = (dischargeItems || [])[i];
    if (!r || !it) return;
    const total = Number(it.qty) || 0;
    const free = Number(r.qty_free) || 0;
    if (total > free) {
      const n = (x) => String(Math.round(x * 100) / 100);
      out.push({ name: (it.product_name || r.product_name || r.category || "Material").split(" / ").pop(),
                 free: n(free), total: n(total), excess: n(total - free) });
    }
  });
  return out;
}

function validateManualDischarge(recipeItems, dischargeItems) {
  const items = recipeItems || [];
  const msgs = [];
  const validItems = [];
  if (items.length === 0) {
    const manual = dischargeItems.filter((i) => (i.product_name || "").trim() && (i.qty || 0) > 0);
    if (manual.length === 0) msgs.push("Agregue al menos un material con nombre y cantidad.");
    else validItems.push(...manual);
    return { ok: msgs.length === 0, msgs, validItems };
  }
  dischargeItems.forEach((it, i) => {
    const r = items[i];
    if (!r || (r.qty_min || 0) === 0) return;
    const label = (it.product_name || r.product_name || "Material").split(" / ").pop();
    if (r.qty_available <= 0) {
      msgs.push(`${label}: sin stock en bodega del técnico`);
      return;
    }
    const hasSku = r.match_mode === "category" && (r.skus_candidatos || []).length > 0;
    if (hasSku && !it.product_id) {
      msgs.push(`${label}: seleccione SKU`);
      return;
    }
    const ns = r.has_serial || (r.skus_candidatos || []).some((sk) => sk.has_serial);
    if (ns && !(it.serial || "").trim()) {
      msgs.push(`${label}: seleccione la serie de la bodega del técnico`);
      return;
    }
    if ((it.qty || 0) < (r.qty_min || 1)) {
      msgs.push(`${label}: cantidad mínima ${r.qty_min || 1}`);
      return;
    }
    validItems.push(it);
  });
  if (validItems.length === 0 && msgs.length === 0) {
    msgs.push("No hay materiales obligatorios listos para descargar (revise Min > 0).");
  }
  // v101.65.0 · Mismo equipo en dos renglones (fábrica o PON) → Odoo rechaza el cierre («serial repetido»)
  const _snKey = (sn) => {
    const n = String(sn || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    const m = n.match(/^([A-Z0-9]{4})([0-9A-F]{8})$/);
    return m ? m[1].split("").map((c) => c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")).join("") + m[2] : n;
  };
  const _seen = {};
  (dischargeItems || []).forEach((it) => {
    const sn = (it.serial || "").trim();
    if (!sn) return;
    const k = _snKey(sn);
    if (_seen[k]) msgs.push(`El equipo ${sn} está repetido (${_seen[k]} y ${(it.product_name || "otro renglón").split(" / ").pop()}). Déjelo en un solo renglón.`);
    else _seen[k] = (it.product_name || "otro renglón").split(" / ").pop();
  });
  return { ok: msgs.length === 0 && validItems.length > 0, msgs, validItems };
}

function ProvisioningWizard({ odooTaskId, canProvision, oltIp, oltName, oltId, cpeSerial, configuredRadiusM, subscriptionCode, planName, clientName, branchOffice, branchOfficeId, clientType, subscriptionClientType, planType, internalStatus, onDone, provisioned, onGoToMaterials, manualOnly = false }) {
  const hasDirectOlt = !!oltIp;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [justProvisioned, setJustProvisioned] = useState(false); // v94.0.2 · local guard post-éxito
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [error, setError] = useState(null);
  const [selectedOnt, setSelectedOnt] = useState(null);
  const [vlan, setVlan] = useState("");
  const [provisioning, setProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null);
  const [provisionError, setProvisionError] = useState(null);
  // v80.0 · SN engagement check + materials block
  const [snEngaged, setSnEngaged] = useState(null);
  const [snChecking, setSnChecking] = useState(false);
  const [materialsCheck, setMaterialsCheck] = useState(null);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materialsGateModal, setMaterialsGateModal] = useState(false);
  // v97.6.0 · Equipment gate: sin descargo (equipment vacío) → bloqueo total
  // v100.3.0 · Reemplazado: usar check-materials como gate (no client-equipment)
  const [eqGate, setEqGate] = useState({ loading: true, equipment: null, blocked: true });
  const [dischargeModal, setDischargeModal] = useState(false);
  const [recipeMaterials, setRecipeMaterials] = useState([]);
  const [dischargeItems, setDischargeItems] = useState([]);
  const [dischargeSaving, setDischargeSaving] = useState(false);
  // v101.139.0 · Confirmación de excedente (lo que supera lo gratuito de la receta se factura al cliente)
  const [excessPrompt, setExcessPrompt] = useState(null);   // { lines: [...], items: [...] }
  const saveDischarge = async (validItems) => {
    setDischargeSaving(true);
    try {
      await api.post(`/v1/provisioning/manual-materials-discharge/${odooTaskId}`, { items: validItems });
      toast.success(`Descargo manual registrado: ${validItems.length} materiales.`);
      setDischargeModal(false);
      await _checkMaterialsGate();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Error al registrar descargo");
    } finally { setDischargeSaving(false); }
  };
  const [superAdminBypass, setSuperAdminBypass] = useState(false);

  // v100.3.0 · Poll check-materials cada 30s (fuente: fiscal_form_submissions.answers.materiales)
  const _checkMaterialsGate = useCallback(async () => {
    try {
      const { data } = await api.get(`/v1/provisioning/check-materials/${odooTaskId}`);
      const hasMatls = !!(data?.materials_registered || data?.has_materials);
      setEqGate({ loading: false, equipment: data?.equipment || data?.materials || [], blocked: !hasMatls });
      // v101.17.0 · Guardar payload completo (incluye materials[]) para la UI de tipo de equipo
      setMaterialsCheck(data);
      setMaterialsLoading(false);
    } catch {
      setEqGate(prev => ({ ...prev, loading: false }));
      setMaterialsLoading(false);
    }
  }, [odooTaskId]);

  // v101.17.0 · Al seleccionar router/ONT de bodega: auto-descargo a materiales (solo instalación FO)
  const occupyMaterialSerial = useCallback(async (serial, kind = "Router") => {
    const sn = String(serial || "").trim().toUpperCase();
    if (!sn || !odooTaskId) return;
    try {
      await api.post(`/v1/provisioning/occupy-material-serial/${odooTaskId}`, {
        serial: sn, category_hint: kind, kind,
      });
      await _checkMaterialsGate();
    } catch (_) { /* traslado/cambio u OT no aplicable: no bloquear UI */ }
  }, [odooTaskId, _checkMaterialsGate]);

  useEffect(() => {
    if (!odooTaskId || provisioned || justProvisioned) return;
    _checkMaterialsGate();
    const iv = setInterval(_checkMaterialsGate, 30000);
    return () => clearInterval(iv);
  }, [odooTaskId, provisioned, justProvisioned, _checkMaterialsGate]);

  // v98.3.0 · Pre-fetch traslado origin al montar (para saber si omitir equipment gate)
  useEffect(() => {
    if (!odooTaskId) return;
    api.get(`/v1/provisioning/provision/traslado/origin-data/${odooTaskId}`)
      .then(({ data }) => {
        setTrasladoOrigin(data);
        if (data?.origin_deleted) setTrasladoDeleteDone(true);
      })
      .catch(() => setTrasladoOrigin(null));
  }, [odooTaskId]);
  // v82.0 · VLAN/IP mode selector (auto vs manual)
  const { user } = useAuth();
  // v101.19.0 · Técnico/contratista = solo auto; TODO el resto ve Auto + Manual (sin whitelist)
  const canManualMode = !["technician", "tecnico", "contractor", "contratista"].includes((user?.role || "").toLowerCase());
  const [provMode, setProvMode] = useState(manualOnly ? "manual" : "auto");   // v101.119.0 · corporativo = manual
  // v93.5.0 · Manual: equipo descargado por el técnico (sin consultar la OLT)
  const [manualSerials, setManualSerials] = useState([]);
  const [manualSerialInput, setManualSerialInput] = useState("");
  // v101.22.16 · PON/MAC pedida al usuario en modo manual (≠ serie Receta)
  const [manualPonInput, setManualPonInput] = useState("");
  // v101.22.16 · 2.º intento AUTO: selector VLAN/IP (vlans-permitidas filtradas)
  const [autoVlanPickMode, setAutoVlanPickMode] = useState(false);
  const [autoPermitVlans, setAutoPermitVlans] = useState([]);
  const [autoAttemptCount, setAutoAttemptCount] = useState(0);
  // v93.9.0 · Equipos con serie en la bodega del técnico asignado + búsqueda de IPs (máx. 100)
  const [techOnus, setTechOnus] = useState([]);
  const [techOnusInfo, setTechOnusInfo] = useState(null);
  const [manualIpQuery, setManualIpQuery] = useState("");
  const [manualVlans, setManualVlans] = useState([]);
  const [manualVlansLoading, setManualVlansLoading] = useState(false);
  const [selectedVlan, setSelectedVlan] = useState(null);
  const [manualIps, setManualIps] = useState([]);
  const [manualIpsLoading, setManualIpsLoading] = useState(false);
  const [selectedIp, setSelectedIp] = useState(null);
  const [vlanSearch, setVlanSearch] = useState("");
  const [autoResolved, setAutoResolved] = useState(null);
  const [autoResolving, setAutoResolving] = useState(false);
  // v91.1.0-RN7 · Tipo de equipo
  const [tipoEquipo, setTipoEquipo] = useState("ONU");
  const [routerSerial, setRouterSerial] = useState("");
  // v96.4.0 · Fibra + Residencial → ONU-WiFi u ONU+Router (sin ONU sola)
  const isFiberResidential = /residencial/i.test(clientType || "") && (!planType || /fiber|fibra/i.test(planType || ""));
  const tipoOptions = isFiberResidential ? ["ONU-WiFi", "ONU+Router"] : ["ONU", "ONU-WiFi", "ONU+Router"];
  useEffect(() => { if (isFiberResidential && tipoEquipo === "ONU") setTipoEquipo("ONU-WiFi"); }, [isFiberResidential, tipoEquipo]);
  const [techRouters, setTechRouters] = useState([]);
  // v101.17.0 · Productos que el técnico registró/descargó en la OT (check-materials.materials)
  const downloadedMaterials = useMemo(() => {
    const raw = materialsCheck?.materials || materialsCheck?.equipment || [];
    return Array.isArray(raw) ? raw : [];
  }, [materialsCheck]);
  // v101.22.16 · Residencial/Mi Negocio: CPE por equipment_classification; corp = serie libre
  const isResMiNegocio = /residencial|mi\s*negocio/i.test(clientType || "");
  const _matClass = (m) => String(m?.equipment_classification || "").toLowerCase().trim();
  const _matBlob = (m) => `${m?.product_name || ""} ${m?.product_code || ""} ${m?.category || ""}`.toUpperCase();
  const downloadedOnus = useMemo(() => {
    if (isResMiNegocio) {
      const byClass = downloadedMaterials.filter((m) => ["onu", "onu_wifi"].includes(_matClass(m)) && (m.serial || "").trim());
      if (byClass.length) return byClass;
    }
    // Fallback nombre solo si no hay clasificación (datos viejos)
    return downloadedMaterials.filter((m) => /ONU|ONT|WIFI|WI-FI/.test(_matBlob(m)));
  }, [downloadedMaterials, isResMiNegocio]);
  const downloadedRouters = useMemo(() => {
    const byClass = downloadedMaterials.filter((m) => _matClass(m) === "router" && (m.serial || "").trim());
    if (byClass.length) return byClass;
    return downloadedMaterials.filter((m) => /ROUTER|MIKROTIK/.test(_matBlob(m)) && !/ONU|ONT/.test(_matBlob(m)));
  }, [downloadedMaterials]);
  // v101.22.16 · Detectar reintento (cancel previo / CPE previo / intento fallido)
  useEffect(() => {
    if (!odooTaskId) return;
    let n = 0;
    try {
      n = parseInt(sessionStorage.getItem(`prov_attempts_${odooTaskId}`) || "0", 10) || 0;
    } catch { n = 0; }
    setAutoAttemptCount(n);
    setAutoVlanPickMode(n >= 1);
  }, [odooTaskId]);
  useEffect(() => {
    if (tipoEquipo !== "ONU+Router" || !odooTaskId) return;
    api.get(`/v1/provisioning/provision/tech-onu-serials/${odooTaskId}`, { params: { kind: "router" } })
      .then(({ data }) => {
        // v101.17.0 · Priorizar routers descargados en la OT; completar con inventario bodega
        const fromDl = (downloadedRouters || [])
          .filter((m) => (m.serial || "").trim())
          .map((m) => ({
            serial: String(m.serial).toUpperCase(),
            product_name: m.product_name || m.product_code || "Router",
            product_id: m.product_id,
            from_download: true,
          }));
        const fromInv = (data.items || []).map((r) => ({ ...r, from_download: false }));
        const seen = new Set();
        const merged = [];
        for (const r of [...fromDl, ...fromInv]) {
          const k = (r.serial || "").toUpperCase();
          if (!k || seen.has(k)) continue;
          seen.add(k);
          merged.push(r);
        }
        setTechRouters(merged);
      }).catch(() => {
        const fromDl = (downloadedRouters || [])
          .filter((m) => (m.serial || "").trim())
          .map((m) => ({
            serial: String(m.serial).toUpperCase(),
            product_name: m.product_name || m.product_code || "Router",
            from_download: true,
          }));
        setTechRouters(fromDl);
      });
  }, [tipoEquipo, odooTaskId, downloadedRouters]);
  // v91.1.0 · Modal "ya aprovisionada"
  const [alreadyProvModal, setAlreadyProvModal] = useState(null);
  // v91.1.0 · Paso Axxess post-provisioning
  const [axxessStep, setAxxessStep] = useState(false);
  const [ssid24, setSsid24] = useState("ALFANET_");
  const [ssid5, setSsid5] = useState("ALFANET_");
  // v101.19.0 · Unificar redes (instalación y traslado)
  const [unifyNetworks, setUnifyNetworks] = useState(false);
  const [axxessLoading, setAxxessLoading] = useState(false);
  const [axxessResult, setAxxessResult] = useState(null);
  // v101.76.0 · Mientras el asistente muestra su paso WiFi (Axxess), la tarjeta «WiFi pendiente» de Infra Red se
  //            oculta: antes se veían las dos a la vez y el traslado/instalación pedía el Axxess DOS veces.
  useEffect(() => {
    const pending = axxessStep && !axxessResult?.ok;
    setAxxessWizardOpen(odooTaskId, pending);
    return () => setAxxessWizardOpen(odooTaskId, false);
  }, [axxessStep, axxessResult, odooTaskId]);

  // v91.3.0 · FASE 3 — Traslado
  const [trasladoOrigin, setTrasladoOrigin] = useState(null);
  const [trasladoDeleteDone, setTrasladoDeleteDone] = useState(false);
  const [trasladoDeletingOrigin, setTrasladoDeletingOrigin] = useState(false);
  // v97.4.0 · La app deja la OT en «en ejecución» (in_progress) al llegar/iniciar: también cuenta como en sitio
  const isEnSitio = ["on_site", "en_sitio", "in_progress", "en_ejecucion"].includes((internalStatus || "").toLowerCase());

  const [step, setStep] = useState("idle");

  // v57.0.0 · Residential flow state
  const [boxes, setBoxes] = useState([]);
  // v101.22.4 · Filtro de sucursal aplicado por backend (prefijo QV_/DR_/…)
  const [boxBranchFilter, setBoxBranchFilter] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [ports, setPorts] = useState([]);
  const [selectedPort, setSelectedPort] = useState(null);
  const [resolvedOltIp, setResolvedOltIp] = useState(null);
  const [resolvedOltName, setResolvedOltName] = useState(null);
  const [boxSearch, setBoxSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [boxClient, setBoxClient] = useState(null);   // v101.114.0 · [lat, lon] del cliente para el mapa de cajas
  const MAX_RADIUS = 50000;
  const defaultRadius = configuredRadiusM
    ? Math.min(parseInt(configuredRadiusM), MAX_RADIUS)
    : 1000;
  const [radiusM, setRadiusM] = useState(defaultRadius);

  const effectiveOltIp = resolvedOltIp || oltIp;
  const effectiveOltName = resolvedOltName || oltName;

  // ═══ Box selection (Step 1) ═══
  const handleLoadBoxes = async (radius = radiusM) => {
    setStep("loading_boxes");
    setError(null);
    setBoxes([]);
    setBoxBranchFilter(null);
    try {
      const { data } = await api.get(`/v1/mobile/orders/${odooTaskId}/provisioning/nearby-boxes`, {
        params: { radius_m: radius },
      });
      if (!data.ok) {
        setError(data.message || "No se pudo buscar cajas.");
        setStep("idle");
        return;
      }
      setBoxes(data.boxes || []);
      setBoxBranchFilter(data.branch_filter || null);
      if (data.client_lat != null && data.client_lon != null) setBoxClient([data.client_lat, data.client_lon]);   // v101.114.0
      if ((data.boxes || []).length === 0) {
        setError(data.message || `No se encontraron cajas dentro de ${radius}m.`);
      }
      setStep("select_box");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error buscando cajas.");
      setStep("idle");
    }
  };

  // v72 · Buscar cajas por nombre (sin límite de radio)
  const handleSearchBoxes = async (term) => {
    setBoxSearch(term);
    if (term.trim().length < 2) {
      // Si borra la búsqueda, recargar nearby
      if (term.trim().length === 0 && boxes.length === 0) handleLoadBoxes();
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const { data } = await api.get(`/v1/mobile/orders/${odooTaskId}/provisioning/search-boxes`, {
        params: { q: term.trim() },
      });
      setBoxes(data.boxes || []);
      setBoxBranchFilter(data.branch_filter || null);
      if ((data.boxes || []).length === 0) {
        const bf = data.branch_filter;
        setError(
          bf?.nomenclatura
            ? `No hay cajas ${bf.nomenclatura}_* para "${term}" en ${bf.branch_name || "esta sucursal"}.`
            : `No se encontraron cajas para "${term}".`,
        );
      }
      setStep("select_box");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error buscando cajas.");
    } finally {
      setSearching(false);
    }
  };

  // ═══ Port selection (Step 2) ═══
  const handleSelectBox = async (box) => {
    setSelectedBox(box);
    setStep("loading_ports");
    setError(null);
    try {
      const { data } = await api.get(`/v1/mobile/orders/${odooTaskId}/provisioning/box-ports/${box.id}`);
      setPorts(data.ports || []);
      if ((data.ports || []).length === 0) {
        setError(`Caja ${box.name} sin puertos libres. Seleccione otra.`);
        setStep("select_box");
        return;
      }
      setStep("select_port");
    } catch (e) {
      setError(e?.response?.data?.detail || "Error consultando puertos.");
      setStep("select_box");
    }
  };

  // ═══ v93.5.0 · Caja → Puerto → Tipo de aprovisionamiento ═══
  const handleSelectPort = async (port) => {
    setSelectedPort(port);
    if (!selectedBox) return;
    // Usuarios sin permiso de modo manual siguen directo al automático.
    // v97.1.0 · El traslado también puede ir en Manual (cuando la ONT no aparece en la OLT destino).
    if (manualOnly && canManualMode) { return handleChooseManual(); }   // v101.119.0 · corporativo: solo manual
    if (canManualMode) { setStep("choose_mode"); return; }
    return continueAutoFromPort(port);
  };

  // v93.5.0 · Serie de fábrica → sn_use (misma regla del backend: hex(ascii(prefijo)) + sufijo)
  const fabricToSnUse = (sn) => {
    const s = (sn || "").trim().toUpperCase();
    if (!s.includes("-")) return s;
    const [p, ...rest] = s.split("-");
    return p.split("").map(c => c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")).join("") + rest.join("");
  };
  const handleChooseAuto = () => { setProvMode("auto"); continueAutoFromPort(selectedPort); };
  const handleChooseManual = () => {
    setProvMode("manual");
    setSelectedVlan(null); setSelectedIp(null); setManualIps([]); setAutoResolved(null); setVlan("");
    setManualPonInput("");
    loadManualVlans();
    // v101.22.16 · Manual: solo series Receta CPE (no techOnus / no cliente)
    if (isTraslado && trasladoOrigin?.origin?.sn_use) {
      const originSn = String(trasladoOrigin.origin.sn_use).toUpperCase();
      setManualSerials([]);
      setManualSerialInput(originSn);
    } else {
      // v101.219.0 · Corporativo: el equipo suele ser un ROUTER (MikroTik, Fortigate…), no una ONU: antes solo se
      //              ofrecían materiales con ONU/ONT/WiFi en el nombre y salía «Sin series en Receta» aunque el
      //              técnico ya hubiera descargado el equipo. Ahora, en corporativo, cualquier material CON SERIE de la OT.
      const _src = isResMiNegocio ? (downloadedOnus || [])
        : [...(downloadedOnus || []), ...(downloadedMaterials || []).filter((m) => String(m.serial || "").trim())];
      const fromRecipe = _src
        .map((m) => String(m.serial || "").trim().toUpperCase())
        .filter(Boolean);
      const dl = (materialsCheck?.downloaded_serials || []).map((s) => String(s).toUpperCase());
      // Res/Mi Negocio: solo Receta clasificada; si vacío, downloaded_serials (también en corporativo · v101.219.0)
      setManualSerials([...new Set(fromRecipe.length ? fromRecipe : dl)]);
      setManualSerialInput("");
    }
    setStep("manual_select");
  };
  const handlePickManualSerial = (sn) => {
    const fab = (sn || "").trim().toUpperCase();
    if (!fab) return;
    // v101.22.16 · sn_fabric = Receta; sn_use se completa con PON del usuario en configure
    setSelectedOnt({ sn_fabric: fab, sn_use: "", manual: true });
    setManualPonInput("");
    setProvisionError(null); setProvisionResult(null);
    setStep("configure");
  };

  // ═══ Automático: resolver OLT + discovery (solo en modo automático) ═══
  const continueAutoFromPort = async (port) => {
    setStep("resolving_olt");
    setError(null);
    try {
      const { data: oltData } = await api.get(
        `/v1/mobile/orders/${odooTaskId}/provisioning/resolve-olt/${selectedBox.id}`
      );
      if (!oltData.ok) {
        setError(oltData.message || "No se pudo resolver la OLT.");
        setStep("select_box");
        return;
      }
      setResolvedOltIp(oltData.olt_ssh_ip);
      setResolvedOltName(oltData.olt_name);

      // v91.3.0 · Traslado: si aún no se eliminó de origen, pausar aquí
      if (isTraslado && !trasladoDeleteDone) {
        setStep("select_port"); // Quedarse para que el usuario vea el panel de delete-origin
        return;
      }

      // Auto-discover with residential endpoint
      setStep("discover");
      setLoading(true);
      const { data: disc } = await api.post(
        `/v1/mobile/orders/${odooTaskId}/provisioning/residential-discover`,
        { box_id: selectedBox.id }
      );
      if (!disc.ok) {
        setError(disc.message || "No se pudo conectar con la OLT.");
        setStep("idle");
        return;
      }
      setDiscoveryResult({ ...disc, olt_ip: oltData.olt_ssh_ip, olt_name: oltData.olt_name });
      toast.success("ONTs detectadas correctamente");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || "Error en el flujo.");
      setStep("idle");
    } finally {
      setLoading(false);
    }
  };

  // v91.3.0 · Traslado: continuar discover después del delete-origin
  const handleTrasladoContinueDiscover = async () => {
    if (!selectedBox) return;
    setStep("discover");
    setLoading(true);
    setError(null);
    try {
      const { data: disc } = await api.post(
        `/v1/mobile/orders/${odooTaskId}/provisioning/residential-discover`,
        { box_id: selectedBox.id }
      );
      if (!disc.ok) {
        setError(disc.message || "No se pudo conectar con la OLT destino.");
        setStep("idle");
        return;
      }
      setDiscoveryResult({ ...disc, olt_ip: resolvedOltIp, olt_name: resolvedOltName });
      toast.success("ONTs detectadas en OLT destino");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.response?.data?.message || "Error en discover destino.");
      setStep("idle");
    } finally {
      setLoading(false);
    }
  };

  // ═══ Direct discover (OT already has OLT) ═══
  const handleDiscover = async () => {
    setLoading(true);
    setError(null);
    setDiscoveryResult(null);
    setSelectedOnt(null);
    setProvisionResult(null);
    setProvisionError(null);
    setStep("discover");
    try {
      const { data } = await api.post("/v1/provisioning/discover", {
        odoo_task_id: odooTaskId,
      });
      setDiscoveryResult(data);
      toast.success("ONTs detectadas correctamente");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail || err.message;
      setError(msg);
      toast.error(msg || "Error al descubrir ONTs");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectOnt = async (ont) => {
    setSelectedOnt(ont);
    setVlan(ont.vlan || "");
    setProvisionError(null);
    setProvisionResult(null);
    setStep("configure");
    // v80.0 · Check SN engagement
    const sn = (ont.sn_use || "").trim();
    if (sn) {
      setSnChecking(true);
      setSnEngaged(null);
      try {
        const { data } = await api.get(`/v1/provisioning/check-equipment/${encodeURIComponent(sn)}`, {
          params: { odoo_task_id: odooTaskId },
        });
        setSnEngaged(data);
      } catch {
        setSnEngaged(null);
      } finally {
        setSnChecking(false);
      }
    }
    // v82.0 · Auto-resolve VLAN+IP if automatic mode
    if (provMode === "auto") {
      await doAutoResolve();
    }
  };

  // v82.0 · Auto-resolver VLAN+IP para la OLT resuelta
  const doAutoResolve = async () => {
    setAutoResolving(true);
    setAutoResolved(null);
    try {
      const oltIdNum = selectedBox?.olt_id || oltId || null;
      if (!oltIdNum) {
        setAutoResolved({ error: "No se pudo determinar el ID de la OLT. Intente modo manual." });
        return;
      }

      // v101.90.0 · Traslado: el backend (auto-resolve-vlan) conserva VLAN + IP del origen SOLO si la caja destino es
      //             de la MISMA OLT; en otra OLT asigna VLAN + IP nuevas (al azar). Ya no se decide aquí.

      // v101.22.16 · Reintento ≥2: cargar candidatas para selector (no auto-ciego)
      if (autoVlanPickMode) {
        try {
          const { data: vlansData } = await api.get(`/v1/provisioning/vlans-permitidas/${oltIdNum}`, {
            params: odooTaskId ? { odoo_task_id: odooTaskId } : {},
          });
          setAutoPermitVlans(vlansData.items || []);
        } catch { setAutoPermitVlans([]); }
      }

      const { data } = await api.get(`/v1/provisioning/auto-resolve-vlan/${oltIdNum}`, {
        params: odooTaskId ? { odoo_task_id: odooTaskId } : {},
      });
      if (data.resolved) {
        setAutoResolved(data);
        setVlan(data.vlan_name || String(data.alfanet_id || ""));
        if (data.note) toast.warning(data.note, { duration: 12000 });   // v101.115.0
        if (autoVlanPickMode) {
          setSelectedVlan({
            id: data.vlan_id, name: data.vlan_name, alfanet_id: data.alfanet_id,
          });
          if (data.vlan_id) loadManualIps(data.vlan_id);
        }
      } else {
        setAutoResolved({ error: "No se pudo resolver VLAN+IP automáticamente." });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error auto-resolviendo VLAN+IP";
      setAutoResolved({ error: msg });
    } finally {
      setAutoResolving(false);
    }
  };

  // v101.19.0 · Modo MANUAL: catálogo completo (NO vlans-permitidas OLT).
  // Backend resuelve filtros desde odoo_task_id (sucursal → plan → client_type).
  // Luego /ips-libres solo muestra IPs free de la VLAN elegida.
  const loadManualVlans = async () => {
    setManualVlansLoading(true);
    setManualVlans([]);
    setSelectedVlan(null);
    setManualIps([]);
    setSelectedIp(null);
    try {
      const params = {};
      if (odooTaskId) params.odoo_task_id = odooTaskId;
      else {
        const boId = branchOfficeId != null && branchOfficeId !== "" ? String(branchOfficeId) : null;
        if (boId) params.branch_office = boId;
        if (planType) params.plan_type = planType;
        const vlanCt = resolveVlanClientType(subscriptionClientType, clientType, planName);
        if (vlanCt) params.client_type = vlanCt;
      }
      const { data } = await api.get("/v1/provisioning/vlans-disponibles", { params });
      setManualVlans(data.items || []);
      if (!(data.items || []).length) {
        const f = data.filters || {};
        toast.warning(
          `Sin VLANs activas para sucursal=${f.branch_office || "—"} · plan=${f.plan_type || planType || "—"} · cliente=${f.client_type || resolveVlanClientType(subscriptionClientType, clientType, planName) || "—"}`,
        );
      }
    } catch (e) {
      toast.error("Error cargando VLANs: " + (e?.response?.data?.detail || e.message));
    } finally {
      setManualVlansLoading(false);
    }
  };

  // v82.0 · Cargar IPs libres de una VLAN (modo manual)
  const loadManualIps = async (vlanId, q = "") => {
    setManualIpsLoading(true);
    setManualIps([]);
    setSelectedIp(null);
    try {
      // v93.9.0 · Solo las primeras 100 IPs libres (o las que coinciden con la búsqueda)
      const { data } = await api.get(`/v1/provisioning/ips-libres/${vlanId}`, { params: { limit: 100, q } });
      setManualIps(data.items || []);
      if ((data.items || []).length === 0) {
        toast.warning("No hay IPs libres en esta VLAN.");
      }
    } catch (e) {
      toast.error("Error cargando IPs: " + (e?.response?.data?.detail || e.message));
    } finally {
      setManualIpsLoading(false);
    }
  };

  // v82.0 · Manejar selección VLAN manual
  const handleSelectManualVlan = (v) => {
    setSelectedVlan(v);
    setVlan(v.name || String(v.alfanet_id || ""));
    loadManualIps(v.id);
  };

  // v82.0 · Manejar selección IP manual
  const handleSelectManualIp = (ip) => {
    setSelectedIp(ip);
  };

  // v93.9.0 · "Ya está aprovisionada" justo después de aprovisionar = éxito (doble clic / reintento)
  const isAlreadyProvisioned = (err) => {
    const d = err?.response?.data?.detail;
    const txt = typeof d === "object" ? (d?.message || "") : String(d || "");
    return err?.response?.status === 409 && /ya est[aá] aprovisionad/i.test(txt);
  };

  const handleProvision = async () => {
    if (!selectedOnt || provisioning) return;   // v93.9.0 · sin doble envío

    // v91.1.0-RN6 · Modo manual: no llama a la OLT
    if (provMode === "manual" && canManualMode) {
      const fab = (selectedOnt.sn_fabric || "").trim();
      const pon = (manualPonInput || selectedOnt.sn_use || "").trim();
      if (!fab) { toast.error("Seleccione la serie del equipo (Receta)"); return; }
      if (!pon) { toast.error("Ingrese el PON/MAC del equipo"); return; }
      // v101.58.0 · PON/MAC puede ser igual a la serie (hay equipos así): ya no se bloquea
      setProvisioning(true);
      setProvisionError(null);
      setStep("executing");
      try {
        const { data } = await api.post("/v1/provisioning/provision/manual", {
          odoo_task_id: odooTaskId,
          sn_use: fab,                 // compat: backend usa sn_fabric + pon
          sn_fabric: fab,
          box_name: selectedBox?.name || "",
          port_name: selectedPort?.name || "",
          port_id: selectedPort?.id || null,
          vlan_id: selectedVlan?.alfanet_id?.toString() || vlan || "",
          client_ip: selectedIp ? selectedIp.name : undefined,
          pon,                         // v101.22.16 · PON pedida al usuario
          tipo_equipo: tipoEquipo,
          olt_ip: resolvedOltIp || discoveryResult?.olt_ip || oltIp || undefined,
          olt_name: resolvedOltName || discoveryResult?.olt_name || oltName || undefined,
          router_serial: tipoEquipo === "ONU+Router" && routerSerial ? routerSerial : undefined,
        });
        setProvisionResult({ ...data, tipo_equipo: tipoEquipo });
        setStep("success");
        setJustProvisioned(true);
        toast.success("Aprovisionamiento manual registrado exitosamente");
        // v101.57.0 · También en manual: el cierre de instalación exige el WiFi (bloque axxess)
        if (needsAxxessForClient(tipoEquipo, subscriptionClientType, clientType, planName)) setAxxessStep(true);   // v101.83.0
        try { if (onDone) await onDone(); } catch (_) {}
      } catch (err) {
        if (isAlreadyProvisioned(err)) {
          setStep("success");
          setJustProvisioned(true);
          toast.info("La OT quedó aprovisionada.");
          try { if (onDone) await onDone(); } catch (_) {}
          return;
        }
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "object" ? detail.message : detail || err.message;
        setProvisionError(msg);
        setStep("error");
        try {
          const n = (autoAttemptCount || 0) + 1;
          setAutoAttemptCount(n);
          setAutoVlanPickMode(true);
          sessionStorage.setItem(`prov_attempts_${odooTaskId}`, String(n));
        } catch { /* ignore */ }
        toast.error(msg || "Error al registrar aprovisionamiento manual");
      } finally {
        setProvisioning(false);
      }
      return;
    }

    // v90.3.0 · Guard: abortar si no hay IP de gestión OLT
    const oltMgmtIp = discoveryResult?.olt_ip || resolvedOltIp || "";
    if (!oltMgmtIp) {
      toast.error("Sin IP de OLT: vuelva a seleccionar la caja");
      return;
    }

    setProvisioning(true);
    setProvisionError(null);
    setStep("executing");
    try {
      const { data } = await api.post("/v1/provisioning/provision", {
        odoo_task_id: odooTaskId,
        sn_use: selectedOnt.sn_use || "",
        sn_fabric: selectedOnt.sn_fabric || "",
        // v101.22.16 · En reintento AUTO priorizar VLAN/IP elegidas
        vlan: (selectedVlan?.name || selectedVlan?.alfanet_id || vlan) || vlan,
        number: selectedOnt.number ?? 0,
        frame: selectedOnt.frame ?? 0,
        slot: selectedOnt.slot ?? 0,
        port: selectedOnt.port ?? 0,
        ip_address: oltMgmtIp,
        client_ip: selectedIp?.name || autoResolved?.ip_address || undefined,
        box_name: selectedBox?.name || "",
        port_name: selectedPort?.name || "",
        port_id: selectedPort?.id || null,
        tipo_equipo: tipoEquipo,
        router_serial: tipoEquipo === "ONU+Router" && routerSerial ? routerSerial : undefined,
      });
      setProvisionResult(data);
      setStep("success");
      setJustProvisioned(true);
      toast.success("ONT aprovisionada exitosamente en la OLT");
      if (needsAxxessForClient(tipoEquipo, subscriptionClientType, clientType, planName)) setAxxessStep(true);   // v101.83.0
      try { if (onDone) await onDone(); } catch (_) {}
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail?.code === "already_provisioned") {
        setAlreadyProvModal({
          olt_ip: detail.olt_ip,
          transaction_id: detail.transaction_id,
        });
        setStep("configure");
        setProvisioning(false);
        return;
      }
      if (isAlreadyProvisioned(err)) {
        setStep("success");
        setJustProvisioned(true);
        toast.info("La OT quedó aprovisionada.");
        try { if (onDone) await onDone(); } catch (_) {}
        return;
      }
      const msg = typeof detail === "object" ? detail.message : detail || err.message;
      setProvisionError(msg);
      setStep("error");
      try {
        const n = (autoAttemptCount || 0) + 1;
        setAutoAttemptCount(n);
        setAutoVlanPickMode(true);
        sessionStorage.setItem(`prov_attempts_${odooTaskId}`, String(n));
      } catch { /* ignore */ }
      toast.error(msg || "Error al aprovisionar ONT");
    } finally {
      setProvisioning(false);
    }
  };

  // v91.1.0 · Confirmar equipo ya aprovisionado (usuario dice "Sí, tiene navegación")
  const handleConfirmExisting = async () => {
    try {
      setProvisioning(true);
      const { data } = await api.post("/v1/provisioning/provision/confirm-existing", {
        odoo_task_id: odooTaskId,
        sn_use: selectedOnt?.sn_use || "",
        sn_fabric: selectedOnt?.sn_fabric || "",
        box_name: selectedBox?.name || "",
        port_name: selectedPort?.name || "",
        port_id: selectedPort?.id || null,
        vlan: vlan || "",
        client_ip: provMode === "auto"
          ? (autoResolved?.ip_address || undefined)
          : (selectedIp ? selectedIp.name : undefined),
        olt_ip: discoveryResult?.olt_ip || resolvedOltIp || "",
        tipo_equipo: tipoEquipo,
      });
      setAlreadyProvModal(null);
      setProvisionResult({ ...data, tipo_equipo: tipoEquipo });
      setStep("success");
      setJustProvisioned(true);
      toast.success("APROVISIONAMIENTO EXITOSO: El equipo ya está activo en la red.");
      if (needsAxxessForClient(tipoEquipo, subscriptionClientType, clientType, planName)) setAxxessStep(true);   // v101.83.0
      try { if (onDone) await onDone(); } catch (_) {}
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al confirmar");
    } finally {
      setProvisioning(false);
    }
  };

  // v91.1.0-RN7 · Configurar Axxess (WiFi) · v101.19.0 · igual en instalación y traslado
  const handleAxxessConfigure = async () => {
    const s24 = ssid24;
    const s5 = unifyNetworks ? ssid24 : ssid5;
    if (!ssidValid(s24) || !ssidValid(s5)) {
      toast.error(unifyNetworks ? "Completa el sufijo del SSID (ALFANET_…)" : "Completa el sufijo de ambos SSID (ALFANET_…)");
      return;
    }
    setAxxessLoading(true);
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        // v96.4.0 · Endpoint y campos reales (antes "/v1/axxess/configure" no existía y siempre fallaba)
        const { data } = await api.post("/v1/provisioning/axxess/configure", {
          odoo_task_id: odooTaskId,
          router_serial: tipoEquipo === "ONU+Router" && routerSerial ? routerSerial : (selectedOnt?.sn_fabric || selectedOnt?.sn_use || ""),
          ssid24: s24, ssid5: s5,
        });
        setAxxessResult(data);
        toast.success(unifyNetworks ? "WiFi aplicado (redes unificadas)" : "Configuración WiFi aplicada exitosamente");
        setAxxessLoading(false);
        if (onDone) onDone();   // v101.76.0 · recargar la OT (SSID guardado) → no se vuelve a pedir el WiFi
        return;
      } catch (err) {
        lastErr = err;
        if (attempt < 3) await new Promise(r => setTimeout(r, 2000));
      }
    }
    setAxxessLoading(false);
    const msg = lastErr?.response?.data?.detail || "Error al configurar WiFi";
    toast.error(`${msg}. Puede continuar y configurar después.`);
    setAxxessResult({ ok: false, error: msg });
  };

  // v101.57.0 · WiFi configurado a mano en el equipo (manual / Axxess caído): solo registra los SSID
  const handleAxxessManual = async () => {
    const s24 = ssid24;
    const s5 = unifyNetworks ? ssid24 : ssid5;
    if (!ssidValid(s24) || !ssidValid(s5)) {
      toast.error("Completa el sufijo del SSID (ALFANET_…)");
      return;
    }
    setAxxessLoading(true);
    try {
      await api.post("/v1/provisioning/axxess/register-manual", { odoo_task_id: odooTaskId, ssid24: s24, ssid5: s5 });
      setAxxessResult({ ok: true, manual: true });
      toast.success("SSID registrado (sin Axxess)");
      if (onDone) onDone();   // v101.76.0
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar el SSID");
    } finally { setAxxessLoading(false); }
  };

  const handleBackToList = () => {
    setSelectedOnt(null);
    setProvisionError(null);
    setProvisionResult(null);
    setSnEngaged(null);
    setSnChecking(false);
    setAutoResolved(null);
    setAutoResolving(false);
    setSelectedVlan(null);
    setSelectedIp(null);
    setManualIps([]);
    if (provMode === "manual") { loadManualVlans(); setStep("manual_select"); return; }
    setStep("discover");
  };

  const handleReset = () => {
    setStep("idle");
    setError(null);
    setBoxes([]);
    setSelectedBox(null);
    setPorts([]);
    setSelectedPort(null);
    setResolvedOltIp(null);
    setResolvedOltName(null);
    setDiscoveryResult(null);
    setSelectedOnt(null);
    setProvisionResult(null);
    setProvisionError(null);
    setSnEngaged(null);
    setSnChecking(false);
    setAutoResolved(null);
    setAutoResolving(false);
    setSelectedVlan(null);
    setSelectedIp(null);
    setManualVlans([]);
    setManualIps([]);
    setTrasladoOrigin(null);
    setTrasladoDeleteDone(false);
    setTrasladoDeletingOrigin(false);
  };

  // v91.3.0 · FASE 3 — Traslado helpers
  const isTraslado = trasladoOrigin?.is_traslado && isEnSitio;
  const originSn = trasladoOrigin?.origin?.sn_use || "";
  const originAliases = (trasladoOrigin?.origin?.aliases && trasladoOrigin.origin.aliases.length)
    ? trasladoOrigin.origin.aliases : (originSn ? [originSn] : []);   // v101.165.0

  const fetchTrasladoOrigin = async () => {
    try {
      const { data } = await api.get(`/v1/provisioning/provision/traslado/origin-data/${odooTaskId}`);
      setTrasladoOrigin(data);
      // v97.1.0 · Si el origen ya se procesó (antes de recargar), no volver a pedirlo
      if (data?.origin_deleted) setTrasladoDeleteDone(true);
      return data;
    } catch { setTrasladoOrigin(null); return null; }
  };

  // v101.83.0 · Traslado automático: al procesar el origen se continúa solo con el discover en destino
  //            (modo manual o elección de modo: no, para no saltarse esos pasos).
  const shouldTrasladoAutoDiscover = () =>
    !!selectedBox && !discoveryResult && provMode !== "manual" &&
    !["manual_select", "configure", "choose_mode", "executing", "discover"].includes(step);

  const handleTrasladoDeleteOrigin = async () => {
    // v101.19.0 · Sin serie/OLT: no llamar al API (misma regla APP; evita spinner eterno)
    const _sn = (trasladoOrigin?.origin?.sn_use || "").trim();
    const _olt = (trasladoOrigin?.origin?.olt_ip || "").trim();
    const finishDelete = () => {
      setTrasladoDeleteDone(true);
      if (shouldTrasladoAutoDiscover()) handleTrasladoContinueDiscover();
    };
    if (!_sn || !_olt) {
      toast.warning("Sin serial u OLT de origen. Se continúa con el destino.");
      finishDelete();
      return;
    }
    setTrasladoDeletingOrigin(true);
    setError(null);
    try {
      const { data } = await api.post("/v1/provisioning/provision/traslado/delete-origin", {
        odoo_task_id: odooTaskId,
        // v101.115.0 · En automático, el backend verifica VLAN + IP en la OLT destino ANTES de borrar el origen
        ...(provMode !== "manual" && (selectedBox?.olt_id || oltId) ? { dest_olt_id: selectedBox?.olt_id || oltId } : {}),
      });
      // v96.7.0 · No bloqueante: si la OLT de origen no confirmó, se avisa y se continúa
      if (data?.non_blocking) toast.warning(data.warning || "No se eliminó la ONT del origen; se continúa con el traslado.");
      else toast.success("ONT eliminada de OLT origen. Proceda a conectar en destino.");
      finishDelete();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : detail || err.message;
      // v101.115.0 · 409 = la OLT destino no tiene VLAN + IP para el cliente: NO se borró el origen y NO se continúa
      if (err.response?.status === 409) {
        setError(msg);
        toast.error(msg, { duration: 12000 });
        return;
      }
      // v96.7.0 · Tampoco bloquea ante un error inesperado: se avisa y se continúa en destino
      toast.warning(`No se pudo eliminar la ONT del origen (${msg || "error"}). Se continúa con el traslado.`);
      finishDelete();
    } finally {
      setTrasladoDeletingOrigin(false);
    }
  };

  // Traslado: provision con endpoint específico
  const handleTrasladoProvision = async () => {
    if (!selectedOnt) return;

    // Validar mismo serial · v97.1.0 · comparar en formato PON (la serie de origen puede venir de fábrica sin guion)
    // v101.165.0 · Se compara contra TODAS las formas del equipo del cliente (PON, fábrica, bodega, Odoo)
    const _sameSn = trasladoSameEquipment(originAliases, [selectedOnt.sn_use, selectedOnt.sn_fabric]);
    if (!_sameSn) {
      toast.error(`El serial ${selectedOnt.sn_use} no coincide con el origen (${originSn}). Eso es Cambio de Equipo.`);
      return;
    }

    // Modo manual (traslado)
    if (provMode === "manual" && canManualMode) {
      // v101.50.4 · PON obligatorio → pe_serial_provisioned / mac_number en cierre Odoo
      const fab = (selectedOnt.sn_fabric || selectedOnt.sn_use || "").trim();
      const pon = (manualPonInput || selectedOnt.sn_use || "").trim();
      if (!pon) { toast.error("Ingrese el PON/MAC del equipo (no puede ir vacío)"); return; }
      // v101.64.0 · PON igual a la serie es válido (v101.58.0): no se bloquea
      setProvisioning(true);
      setProvisionError(null);
      setStep("executing");
      try {
        const { data } = await api.post("/v1/provisioning/provision/traslado-manual", {
          odoo_task_id: odooTaskId,
          sn_use: fab || pon,
          sn_fabric: fab || undefined,
          pon,
          box_name: selectedBox?.name || "",
          port_name: selectedPort?.name || "",
          port_id: selectedPort?.id || null,
          vlan_id: selectedVlan?.alfanet_id?.toString() || vlan || "",
          client_ip: selectedIp ? selectedIp.name : undefined,
          tipo_equipo: tipoEquipo,
        });
        setProvisionResult({ ...data, tipo_equipo: tipoEquipo, tipo_solucion: "traslado" });
        setStep("success");
        setJustProvisioned(true);
        toast.success("Traslado manual registrado exitosamente");
        // v101.19.0 · Traslado: mismo paso Axxess que instalación (SSID / Unificar redes)
        if (needsAxxessForClient(tipoEquipo, subscriptionClientType, clientType, planName)) setAxxessStep(true);   // v101.83.0
        if (data.axxess_ticket) {
          toast.warning("Aviso IP Axxess: configure WiFi abajo (mismo flujo que instalación). Ticket de seguimiento creado.");
        }
        try { if (onDone) await onDone(); } catch (_) {}
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "object" ? detail.message : detail || err.message;
        // Req 7: serial mismatch
        if (err.response?.status === 409 && detail?.code === "serial_mismatch") {
          toast.error(detail.message);
          setStep("configure");
        } else {
          setProvisionError(msg);
          setStep("error");
          toast.error(msg || "Error en traslado manual");
        }
      } finally { setProvisioning(false); }
      return;
    }

    // Modo automático (traslado)
    const destOltIp = discoveryResult?.olt_ip || resolvedOltIp || "";
    if (!destOltIp) {
      toast.error("Sin IP de OLT destino");
      return;
    }
    setProvisioning(true);
    setProvisionError(null);
    setStep("executing");
    try {
      const { data } = await api.post("/v1/provisioning/provision/traslado", {
        odoo_task_id: odooTaskId,
        sn_use: selectedOnt.sn_use || "",
        sn_fabric: selectedOnt.sn_fabric || "",
        vlan: vlan,
        number: selectedOnt.number ?? 0,
        frame: selectedOnt.frame ?? 0,
        slot: selectedOnt.slot ?? 0,
        port: selectedOnt.port ?? 0,
        dest_olt_ip: destOltIp,
        client_ip: provMode === "auto"
          ? (autoResolved?.ip_address || undefined)
          : (selectedIp ? selectedIp.name : undefined),
        box_name: selectedBox?.name || "",
        port_name: selectedPort?.name || "",
        port_id: selectedPort?.id || null,
        tipo_equipo: tipoEquipo,
      });
      setProvisionResult({ ...data, tipo_equipo: tipoEquipo, tipo_solucion: "traslado" });
      setStep("success");
      setJustProvisioned(true);
      toast.success("Traslado completado exitosamente");
      // v101.19.0 · Traslado: mismo paso Axxess que instalación (no sustituir por solo ticket)
      if (needsAxxessForClient(tipoEquipo, subscriptionClientType, clientType, planName)) setAxxessStep(true);   // v101.83.0
      if (data.axxess_ticket) {
        toast.warning("Aviso IP Axxess: configure WiFi abajo (mismo flujo que instalación). Ticket de seguimiento creado.");
      }
      try { if (onDone) await onDone(); } catch (_) {}
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "serial_mismatch") {
        toast.error(detail.message);
        setStep("configure");
        setProvisioning(false);
        return;
      }
      const msg = typeof detail === "object" ? detail.message : detail || err.message;
      setProvisionError(msg);
      setStep("error");
      toast.error(msg || "Error en traslado");
    } finally { setProvisioning(false); }
  };

  return (
    <div data-testid="provisioning-wizard">
      {/* v97.6.0 · Equipment gate: sin descargo → banner rojo + botón deshabilitado */}
      {/* v98.3.0 · Traslado omite gate de materiales */}
      {!open && !provisioned && !justProvisioned && eqGate.blocked && !superAdminBypass && !trasladoOrigin?.is_traslado && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: "#FEF2F2", border: "1.5px solid #FECACA" }}
            data-testid="equipment-gate-banner">
            <AlertTriangle size={20} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1">
              <div className="text-xs font-bold" style={{ color: "#991B1B" }}>Sin descargo de materiales</div>
              <div className="text-[11px] mt-1 leading-relaxed" style={{ color: "#B91C1C" }}>
                No es posible aprovisionar hasta que el técnico registre los materiales o se realice un descargo manual desde web.
              </div>
              <div className="text-[10px] mt-1.5 italic" style={{ color: "#9CA3AF" }}>
                Verificación automática cada 30 segundos.
                {eqGate.loading && " Verificando..."}
              </div>
              <button onClick={_checkMaterialsGate}
                className="mt-2 text-[10px] font-semibold underline" style={{ color: "#2563EB" }}
                data-testid="btn-recheck-materials">
                Volver a verificar ahora
              </button>
            </div>
          </div>
          <button disabled
            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-bold text-sm opacity-40 cursor-not-allowed"
            style={{ background: "#6B7280", color: "#D1D5DB", border: "1.5px solid #9CA3AF" }}
            data-testid="btn-provisionar-equipo-disabled"
          >
            <Zap size={18} /> Aprovisionar Equipo — bloqueado
          </button>
          {/* Descargo manual para admin/super_admin */}
          {["super_admin", "admin"].includes(user?.role) && (
            <button
              onClick={async () => {
                setRecipeMaterials({ _loading: true });
                setDischargeModal(true);
                try {
                  const { data } = await api.get(`/v1/provisioning/recipe-materials/${odooTaskId}`);
                  setRecipeMaterials(data || {});
                  const items = buildDischargeItemsFromRecipe(data?.recipe_items || []);
                  setDischargeItems(items.length ? items : [{ product_id: null, product_name: "", qty: 1, serial: "" }]);
                } catch {
                  setRecipeMaterials({});
                  setDischargeItems([{ product_id: null, product_name: "", qty: 1, serial: "" }]);
                }
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold border transition hover:opacity-80"
              style={{ borderColor: "#0A3747", color: "#0A3747" }}
              data-testid="btn-manual-discharge"
            >
              <PackagePlus size={14} /> Registrar descargo manual
            </button>
          )}
          {/* Bypass super_admin con confirmación explícita */}
          {user?.role === "super_admin" && (
            <button
              onClick={() => { if (window.confirm("Aprovisionar sin materiales — bajo mi responsabilidad. ¿Continuar?")) setSuperAdminBypass(true); }}
              className="w-full text-center text-[10px] py-1 rounded-lg underline"
              style={{ color: "#9CA3AF" }}
              data-testid="btn-bypass-gate"
            >
              Aprovisionar sin materiales (super admin)
            </button>
          )}
        </div>
      )}

      {/* Botón principal · visible solo si no hay bloqueo por equipment gate o bypass activo */}
      {/* v98.3.0 · Traslado siempre pasa el gate */}
      {!open && !provisioned && !justProvisioned && (!eqGate.blocked || superAdminBypass || trasladoOrigin?.is_traslado) && (
        <button
          onClick={async () => {
            setOpen(true);
            fetchTrasladoOrigin();
            api.get(`/v1/provisioning/provision/tech-onu-serials/${odooTaskId}`)
              .then(({ data }) => { setTechOnus(data.items || []); setTechOnusInfo(data); })
              .catch(() => { setTechOnus([]); setTechOnusInfo(null); });
            handleLoadBoxes(radiusM);
          }}
          data-testid="btn-provisionar-equipo"
          className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl font-bold text-sm transition-all hover:scale-[1.01] active:scale-[0.99]"
          style={{
            background: `linear-gradient(135deg, #0A3747 0%, #0D4A5E 100%)`,
            color: "#E0E621",
            boxShadow: `-4px -4px 10px rgba(255,255,255,0.6), 4px 4px 12px rgba(163,177,198,0.4), inset 0 1px 0 rgba(255,255,255,0.1)`,
            border: `1.5px solid rgba(224,230,33,0.15)`,
          }}
        >
          <Zap size={18} />
          Aprovisionar Equipo
          {superAdminBypass && <span className="text-[9px] opacity-60 ml-1">(bypass admin)</span>}
          {!superAdminBypass && hasDirectOlt ? (
            <span className="text-[10px] opacity-70 ml-1 font-normal">OLT: {oltName || oltIp}</span>
          ) : !superAdminBypass ? (
            <span className="text-[10px] opacity-70 ml-1 font-normal">Seleccionar Caja</span>
          ) : null}
        </button>
      )}

      {/* v94.0.2 · Mensaje "Ya aprovisionada" */}
      {!open && (provisioned || justProvisioned) && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-[12px] font-semibold"
          style={{ background: "#F0FDF4", color: "#15803D", border: "1.5px solid #BBF7D0" }}
          data-testid="prov-already-done-msg">
          <Check size={15} /> Ya aprovisionada
        </div>
      )}

      {/* v97.6.1 · Modal de descargo manual — receta + stock + seriales */}
      {dischargeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="discharge-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PackagePlus size={20} style={{ color: "#0A3747" }} />
                <div className="text-sm font-bold" style={{ color: "#0A3747" }}>Descargo manual de materiales</div>
              </div>
              <button onClick={() => setDischargeModal(false)} className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
            </div>
            {recipeMaterials._loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin" style={{ color: "#0A3747" }} /></div>
            ) : (
              <>
                {recipeMaterials.warehouse_name && (
                  <div className="flex items-center gap-2 text-[11px] px-3 py-2 rounded-lg" style={{ background: "#F0F9FF", color: "#0369A1" }}>
                    <Building2 size={13} /> Bodega: <span className="font-semibold">{recipeMaterials.warehouse_name}</span>
                    {recipeMaterials.tech_email && <span className="ml-2 opacity-60">({recipeMaterials.tech_email})</span>}
                  </div>
                )}
                {(!recipeMaterials.recipe_items || recipeMaterials.recipe_items.length === 0) ? (
                  <div className="text-xs text-center py-6" style={{ color: "#9CA3AF" }}>
                    Sin receta definida para este tipo de solución. Registre materiales manualmente.
                    <div className="mt-3 space-y-2">
                      {dischargeItems.map((it, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg" style={{ background: "#F9FAFB" }}>
                          <div className="col-span-5">
                            <input value={it.product_name} placeholder="Material"
                              onChange={e => { const n = [...dischargeItems]; n[idx].product_name = e.target.value; setDischargeItems(n); }}
                              className="w-full text-xs px-2 py-1.5 rounded border" style={{ borderColor: "#D1D5DB" }} />
                          </div>
                          <div className="col-span-2">
                            <input type="number" min="1" value={it.qty}
                              onChange={e => { const n = [...dischargeItems]; n[idx].qty = parseInt(e.target.value) || 1; setDischargeItems(n); }}
                              className="w-full text-xs px-2 py-1.5 rounded border text-center" style={{ borderColor: "#D1D5DB" }} />
                          </div>
                          <div className="col-span-4">
                            {/* v101.153.0 · La serie se SELECCIONA de la bodega del técnico (no se digita) */}
                            <select value={it.serial || ""}
                              onChange={e => {
                                const n = [...dischargeItems];
                                const ts = (recipeMaterials.tech_serials || []).find(t => t.serial === e.target.value);
                                n[idx].serial = e.target.value;
                                if (ts) { n[idx].product_id = ts.product_id; n[idx].product_name = ts.product_name || n[idx].product_name; }
                                setDischargeItems(n);
                              }}
                              className="w-full text-xs px-2 py-1.5 rounded border" style={{ borderColor: "#D1D5DB" }}
                              data-testid={`discharge-serial-${idx}`}>
                              <option value="">{(recipeMaterials.tech_serials || []).length ? "Sin serie" : "Sin series en bodega"}</option>
                              {(recipeMaterials.tech_serials || [])
                                .filter(t => t.serial === it.serial || !dischargeItems.some((o, j) => j !== idx && o.serial === t.serial))
                                .map(t => <option key={t.serial} value={t.serial}>{t.serial}{t.product_name ? ` · ${t.product_name.split(" / ").pop()}` : ""}</option>)}
                            </select>
                          </div>
                          <div className="col-span-1"><button onClick={() => setDischargeItems(dischargeItems.filter((_, i) => i !== idx))} className="text-red-400 hover:text-red-600"><X size={14} /></button></div>
                        </div>
                      ))}
                      <button onClick={() => setDischargeItems([...dischargeItems, { product_id: null, product_name: "", qty: 1, serial: "" }])}
                        className="text-xs font-semibold flex items-center gap-1" style={{ color: "#0A3747" }}>
                        <Plus size={13} /> Agregar material
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {dischargeItems.map((it, idx) => {
                      const recipe = recipeMaterials.recipe_items?.[idx];
                      if (!recipe) return null;
                      const noStock = recipe.qty_available <= 0;
                      const qtyMin = recipe.qty_min || 0;
                      const qtyFree = recipe.qty_free || 0;
                      const qtyAvail = recipe.qty_available ?? 0;
                      const needsSerial = recipe.has_serial || (recipe.skus_candidatos || []).some(sk => sk.has_serial);
                      const hasSku = recipe.match_mode === "category" && (recipe.skus_candidatos || []).length > 0;
                      const selectedSku = it.product_id
                        ? (recipe.skus_candidatos || []).find(sk => sk.product_id === it.product_id)
                        : null;
                      const uniqueSerials = [...new Set(
                        selectedSku?.serial_numbers
                        || recipe.serial_numbers
                        || (recipe.skus_candidatos || []).flatMap(sk => sk.serial_numbers || [])
                        || []
                      )];
                      const isRequired = qtyMin > 0;
                      const isComplete = (() => {
                        if (!isRequired) return true;
                        if (noStock) return false;
                        if (hasSku && !it.product_id) return false;
                        if (needsSerial && !it.serial) return false;
                        return it.qty >= (qtyMin || 1);
                      })();
                      const displayName = (it.product_name || "").split(" / ").pop() || it.product_name;
                      return (
                        <div key={idx} className="rounded-xl overflow-hidden" style={{ border: noStock && isRequired ? "1.5px solid #FECACA" : !isRequired ? "1px solid #E5E7EB" : "1px solid #E5E7EB", opacity: !isRequired ? 0.6 : 1 }}
                          data-testid={`discharge-row-${idx}`}>
                          {/* Header del material */}
                          <div className="flex items-center justify-between px-3 py-2"
                            style={{ background: noStock && isRequired ? "#FEF2F2" : "#F9FAFB" }}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs font-bold truncate" style={{ color: noStock && isRequired ? "#DC2626" : "#111827" }}
                                title={it.product_name}>{displayName}</span>
                              {!isRequired && <span className="text-[8px] font-mono px-1 py-0.5 rounded" style={{ background: "#F3F4F6", color: "#9CA3AF" }}>Opcional</span>}
                              {recipe.product_code && <span className="text-[9px] font-mono shrink-0" style={{ color: "#9CA3AF" }}>{recipe.product_code}</span>}
                            </div>
                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0 ml-2"
                              style={{
                                background: isComplete ? "#D1FAE5" : "#F3F4F6",
                                color: isComplete ? "#059669" : "#6B7280",
                              }}>
                              {isComplete ? "Completo" : "Pendiente"}
                            </span>
                          </div>
                          {/* Metricas: Min | Máx | Gratuito | Stock */}
                          <div className="grid grid-cols-4 text-center py-1.5 text-[10px]" style={{ background: "#FAFAFA", borderTop: "1px solid #F3F4F6", borderBottom: "1px solid #F3F4F6" }}>
                            <div><span style={{ color: "#9CA3AF" }}>Min </span><span className="font-bold" style={{ color: "#374151" }}>{qtyMin}</span></div>
                            <div><span style={{ color: "#9CA3AF" }}>Máx </span><span className="font-bold" style={{ color: "#374151" }}>{recipe.qty_max || "—"}</span></div>{/* v101.52.0 */}
                            <div><span style={{ color: "#9CA3AF" }}>Gratuito </span><span className="font-bold" style={{ color: "#374151" }}>{qtyFree}</span></div>
                            <div>
                              <span style={{ color: "#9CA3AF" }}>Stock </span>
                              <span className="font-bold" style={{ color: noStock ? "#DC2626" : "#059669" }}>{qtyAvail}</span>
                            </div>
                          </div>
                          {/* Controles */}
                          <div className="px-3 py-2.5 space-y-2" style={{ background: "#FFF" }}>
                            {hasSku && (
                              noStock ? (
                                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[10px] font-semibold"
                                  style={{ background: "#FEF2F2", color: "#DC2626" }}>
                                  <AlertTriangle size={12} /> Sin SKUs disponibles en bodega
                                </div>
                              ) : (
                                <select value={it.product_id || ""}
                                  onChange={e => {
                                    const n = [...dischargeItems];
                                    const sku = recipe.skus_candidatos.find(s => String(s.product_id) === e.target.value);
                                    n[idx].product_id = sku?.product_id || null;
                                    n[idx].product_name = sku?.product_name || it.product_name;
                                    n[idx].serial = "";
                                    setDischargeItems(n);
                                  }}
                                  className="w-full text-xs px-2 py-1.5 rounded-lg border"
                                  style={{ borderColor: !it.product_id ? "#FCD34D" : "#D1D5DB" }}
                                  data-testid={`discharge-sku-${idx}`}>
                                  <option value="">-- Seleccionar SKU --</option>
                                  {recipe.skus_candidatos.map(sk => (
                                    <option key={sk.product_id} value={sk.product_id}>
                                      {sk.product_name} (stock: {sk.stock_total})
                                    </option>
                                  ))}
                                </select>
                              )
                            )}
                            <div className="flex items-center gap-3">
                              <div className="w-20 shrink-0">
                                <div className="text-[9px] font-semibold mb-0.5" style={{ color: "#6B7280" }}>Cantidad</div>
                                <input type="number" min={0} max={recipe.qty_max || 99} value={it.qty}
                                  onChange={e => { const n = [...dischargeItems]; n[idx].qty = parseInt(e.target.value) || 0; setDischargeItems(n); }}
                                  className="w-full text-xs px-2 py-1.5 rounded-lg border text-center font-bold"
                                  style={{ borderColor: "#D1D5DB" }}
                                  data-testid={`discharge-qty-${idx}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[9px] font-semibold mb-0.5" style={{ color: "#6B7280" }}>Serie</div>
                                {uniqueSerials.length > 0 ? (
                                  <select value={it.serial}
                                    onChange={e => { const n = [...dischargeItems]; n[idx].serial = e.target.value; setDischargeItems(n); }}
                                    className="w-full text-xs px-2 py-1.5 rounded-lg border"
                                    style={{ borderColor: needsSerial && !it.serial ? "#FCD34D" : "#D1D5DB" }}
                                    data-testid={`discharge-serial-${idx}`}>
                                    <option value="">-- Seleccionar serie --</option>
                                    {uniqueSerials.map(sn => (
                                      <option key={sn} value={sn}>{sn}</option>
                                    ))}
                                  </select>
                                ) : needsSerial && !noStock ? (
                                  /* v101.153.0 · Sin digitar: si la bodega no tiene series libres, no se puede registrar */
                                  <div className="text-[10px] font-semibold py-1.5" style={{ color: "#DC2626" }}
                                    data-testid={`discharge-serial-none-${idx}`}>
                                    Sin series disponibles en la bodega del técnico
                                  </div>
                                ) : (
                                  <div className="text-[10px] italic py-1.5" style={{ color: "#9CA3AF" }}>No requiere serie</div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Validacion global */}
                {recipeMaterials.recipe_items?.length > 0 && (() => {
                  const msgs = [];
                  dischargeItems.forEach((it, i) => {
                    const r = recipeMaterials.recipe_items?.[i];
                    if (!r || (r.qty_min || 0) === 0) return; // Opcional si min=0
                    if (r.qty_available <= 0) msgs.push(`${(it.product_name || "").split(" / ").pop()}: sin stock`);
                    else {
                      const hasSku = r.match_mode === "category" && (r.skus_candidatos || []).length > 0;
                      if (hasSku && !it.product_id) msgs.push(`${(it.product_name || "").split(" / ").pop()}: seleccione SKU`);
                      const ns = r.has_serial || (r.skus_candidatos || []).some(sk => sk.has_serial);
                      if (ns && !it.serial) msgs.push(`${(it.product_name || "").split(" / ").pop()}: seleccione serie`);
                    }
                  });
                  if (msgs.length === 0) return null;
                  return (
                    <div className="text-[10px] px-3 py-2 rounded-lg space-y-0.5" style={{ background: "#FEF2F2", color: "#DC2626" }}>
                      <div className="flex items-center gap-1.5 font-bold"><AlertTriangle size={12} /> Pendientes para habilitar descargo:</div>
                      {msgs.map((m, i) => <div key={i} className="ml-5">- {m}</div>)}
                    </div>
                  );
                })()}
              </>
            )}
            <div className="flex items-center justify-end gap-3 pt-2 border-t" style={{ borderColor: "#E5E7EB" }}>
              <button onClick={() => setDischargeModal(false)}
                className="px-4 py-2 rounded-lg text-xs font-semibold border"
                style={{ borderColor: "#D1D5DB", color: "#6B7280" }}
                data-testid="discharge-cancel">
                Cancelar
              </button>
              <button
                type="button"
                disabled={dischargeSaving}
                onClick={async () => {
                  const { ok, msgs, validItems } = validateManualDischarge(
                    recipeMaterials.recipe_items || [],
                    dischargeItems,
                  );
                  if (!ok) {
                    toast.error(msgs.length ? msgs.join(" · ") : "Complete los materiales obligatorios antes de confirmar.");
                    return;
                  }
                  // v101.139.0 · Receta estricta: excedente sobre lo gratuito → confirmar antes de registrar
                  const lines = excessLinesForDischarge(recipeMaterials.recipe_items || [], dischargeItems);
                  if (lines.length > 0) { setExcessPrompt({ lines, items: validItems }); return; }
                  await saveDischarge(validItems);
                }}
                className="px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "#0A3747", color: "#E0E621" }}
                data-testid="discharge-confirm">
                {dischargeSaving ? <Loader2 size={13} className="animate-spin" /> : "Confirmar descargo"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* v101.139.0 · Advertencia de excedente sobre lo cubierto por la receta */}
      {excessPrompt && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" data-testid="excess-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-5 space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              <div className="text-sm font-bold text-slate-900">Materiales por encima de lo cubierto</div>
            </div>
            <p className="text-xs text-slate-700">Los siguientes materiales superan la cantidad cubierta sin costo por la receta de esta OT:</p>
            <ul className="space-y-1">
              {excessPrompt.lines.map((l, i) => (
                <li key={i} className="text-xs rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-amber-900">
                  <b>{l.name}</b>: cubierto {l.free}, registrado {l.total} · excedente <b>{l.excess}</b>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-700">El excedente será facturado al cliente en el cierre de la OT. ¿Desea continuar?</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setExcessPrompt(null)} className="px-4 py-2 rounded-lg text-xs font-semibold border border-gray-300 text-gray-600"
                data-testid="excess-cancel">Cancelar</button>
              <button onClick={async () => { const it = excessPrompt.items; setExcessPrompt(null); await saveDischarge(it); }}
                className="px-4 py-2 rounded-lg text-xs font-bold" style={{ background: "#0A3747", color: "#E0E621" }}
                data-testid="excess-continue">Continuar</button>
            </div>
          </div>
        </div>
      )}

      {/* Panel expandido */}
      {open && (
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: NEU.bgLight,
            boxShadow: NEU.shadowUp,
            border: `1.5px solid ${NEU.border}`,
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{ background: "#0A3747", color: "#E0E621" }}
          >
            <div className="flex items-center gap-2">
              <Zap size={16} />
              <span className="font-bold text-sm">
                {(step === "idle" || step === "loading_boxes" || step === "select_box") && "Seleccionar Caja"}
                {(step === "loading_ports" || step === "select_port") && "Seleccionar Puerto"}
                {step === "resolving_olt" && "Resolviendo OLT..."}
                {step === "choose_mode" && "Tipo de aprovisionamiento"}
                {step === "manual_select" && "Equipo descargado (manual)"}
                {step === "discover" && "Discovery ONTs"}
                {step === "configure" && "Configurar Aprovisionamiento"}
                {step === "executing" && "Ejecutando..."}
                {step === "success" && "Aprovisionamiento completo"}
                {step === "error" && "Error de Aprovisionamiento"}
              </span>
              {effectiveOltIp && (
                <span className="text-[10px] opacity-70">
                  OLT: {effectiveOltName || effectiveOltIp}
                </span>
              )}
              {selectedBox && (
                <span className="text-[10px] opacity-70">
                  Caja: {selectedBox.name}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {step === "select_box" && (
                <div className="flex items-center gap-1">
                  {(() => {
                    const standard = [500, 1000, 3000, 10000];
                    const options = defaultRadius && !standard.includes(defaultRadius)
                      ? [...standard, defaultRadius].sort((a, b) => a - b)
                      : standard;
                    return options.map((r) => (
                      <button
                        key={r}
                        onClick={() => { setRadiusM(r); handleLoadBoxes(r); }}
                        className="px-2 py-0.5 rounded text-[9px] font-bold transition"
                        data-testid={`radius-btn-${r}`}
                        style={{
                          background: radiusM === r ? "#E0E621" : "rgba(255,255,255,0.1)",
                          color: radiusM === r ? "#0A3747" : "#E0E621",
                          border: r === defaultRadius ? "1px solid #E0E621" : "1px solid transparent",
                        }}
                      >
                        {r >= 1000 ? `${r / 1000}km` : `${r}m`}{r === defaultRadius && r !== 1000 ? " ★" : ""}
                      </button>
                    ));
                  })()}
                </div>
              )}
              {step === "select_port" && (
                <button
                  onClick={() => { setStep("select_box"); setSelectedBox(null); setPorts([]); }}
                  className="px-2.5 py-1 rounded-xl text-[10px] font-semibold flex items-center gap-1"
                  style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}
                >
                  <ArrowLeft size={12} /> Otra caja
                </button>
              )}
              {step === "discover" && hasDirectOlt && (
                <button
                  onClick={handleDiscover}
                  disabled={loading}
                  data-testid="btn-refresh-discover"
                  className="px-2.5 py-1 rounded-xl text-[10px] font-semibold transition hover:opacity-80"
                  style={{ background: "rgba(224,230,33,0.15)", color: "#E0E621" }}
                >
                  {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                </button>
              )}
              {(step === "configure" || step === "error") && (
                <button
                  onClick={handleBackToList}
                  data-testid="btn-back-to-list"
                  className="px-2.5 py-1 rounded-xl text-[10px] font-semibold transition hover:opacity-80 flex items-center gap-1"
                  style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}
                >
                  <ArrowLeft size={12} /> Volver
                </button>
              )}
              <button
                onClick={() => { setOpen(false); handleReset(); }}
                data-testid="btn-close-discover"
                className="px-2 py-1 rounded-xl text-[10px] font-semibold hover:opacity-80"
                style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}
              >
                <XCircle size={14} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 py-4">

            {/* v97.4.0 · Materiales primero: sin descarga de materiales no se aprovisiona */}
            {/* v98.3.0 · Traslado omite validación de materiales */}
            {materialsCheck?.blocked && !isTraslado && (
              <MaterialsBlockedBanner
                odooTaskId={odooTaskId}
                onReady={(data) => { setMaterialsCheck(data); if (step === "idle") handleLoadBoxes(radiusM); }}
              />
            )}

            {/* ═══ v91.3.0 · FASE 3 — Panel DATOS DE ORIGEN (Traslado) ═══ */}
            {isTraslado && trasladoOrigin?.origin && (
              <div
                className="rounded-xl p-3 mb-4"
                style={{ background: "#FFF7ED", border: "1.5px solid #FDBA74" }}
                data-testid="traslado-origin-panel"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <RotateCcw size={12} style={{ color: "#EA580C" }} />
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider" style={{ color: "#9A3412" }}>
                    TRASLADO — Datos de Origen
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: "Serial (SN)", value: trasladoOrigin.origin.sn_use || "—" },
                    { label: "OLT Origen", value: trasladoOrigin.origin.olt_name || trasladoOrigin.origin.olt_ip || "—" },
                    { label: "VLAN", value: trasladoOrigin.origin.vlan_name || "—" },
                    { label: "IP Cliente", value: trasladoOrigin.origin.client_ip || "—" },
                    { label: "Caja", value: trasladoOrigin.origin.distribution_box || "—" },
                    { label: "Puerto", value: trasladoOrigin.origin.port_name || "—" },
                  ].map((item) => (
                    <div key={item.label} className="px-2 py-1.5 rounded-lg" style={{ background: "#FFEDD5" }}>
                      <div className="text-[9px] uppercase tracking-wide" style={{ color: "#9A3412" }}>{item.label}</div>
                      <div className="text-xs font-bold font-mono truncate" style={{ color: "#7C2D12" }}>{item.value}</div>
                    </div>
                  ))}
                </div>

                {/* Paso DELETE ORIGEN — se muestra cuando se eligió caja destino pero antes del discover */}
                {!trasladoDeleteDone && selectedBox && step !== "idle" && step !== "loading_boxes" && step !== "select_box" && (
                  <div className="mt-3 pt-3" style={{ borderTop: "1px solid #FDBA74" }}>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle size={14} style={{ color: trasladoOrigin.origin.olt_ip ? "#DC2626" : "#B45309" }} />
                      <span className="text-xs font-bold" style={{ color: trasladoOrigin.origin.olt_ip ? "#DC2626" : "#B45309" }}>
                        {trasladoOrigin.origin.olt_ip && trasladoOrigin.origin.sn_use
                          ? "Antes de descubrir en destino, debe eliminar la ONT del OLT origen"
                          : "No hay OLT de origen registrada para este cliente: no hay ONT que eliminar. Continúe con el destino."}
                      </span>
                    </div>
                    <button
                      onClick={handleTrasladoDeleteOrigin}
                      disabled={trasladoDeletingOrigin}
                      data-testid="btn-traslado-delete-origin"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40"
                      style={{ background: "#DC2626", color: "#fff" }}
                    >
                      {trasladoDeletingOrigin ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                      {trasladoDeletingOrigin ? "Eliminando de OLT origen..."
                        : (trasladoOrigin.origin.olt_ip && trasladoOrigin.origin.sn_use)
                          ? `Eliminar de ${trasladoOrigin.origin.olt_name || trasladoOrigin.origin.olt_ip}`
                          : "Continuar sin eliminar (sin OLT de origen)"}
                    </button>
                  </div>
                )}

                {trasladoDeleteDone && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: "#DCFCE7" }}>
                      <CheckCircle2 size={14} style={{ color: "#16A34A" }} />
                      <span className="text-xs font-bold" style={{ color: "#166534" }}>
                        {provMode === "manual" || step === "manual_select" || step === "configure"
                          ? "Origen procesado. Continúe el aprovisionamiento en destino."
                          : step === "choose_mode"
                            ? "Origen procesado. Elija Automático o Manual para continuar."
                            : loading || step === "discover"
                              ? "Origen procesado. Descubriendo ONTs en destino…"
                              : "Origen procesado. Seleccione la ONT en destino."}
                      </span>
                    </div>
                    {shouldTrasladoAutoDiscover() && !loading && (
                      <button
                        onClick={handleTrasladoContinueDiscover}
                        disabled={loading}
                        data-testid="btn-traslado-continue-discover"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40"
                        style={{ background: "#059669", color: "#fff" }}
                      >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                        {loading ? "Descubriendo en destino..." : `Descubrir ONTs en ${resolvedOltName || resolvedOltIp || "OLT destino"}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ═══ v57.0.0 · Step: Loading Boxes ═══ */}
            {(step === "loading_boxes" || step === "loading_ports" || step === "resolving_olt") && (
              <div className="flex items-center justify-center gap-3 py-8" data-testid="prov-loading">
                <Loader2 size={24} className="animate-spin" style={{ color: NEU.accent }} />
                <span className="text-sm" style={{ color: NEU.text }}>
                  {step === "loading_boxes" && "Buscando cajas cercanas..."}
                  {step === "loading_ports" && `Consultando puertos de ${selectedBox?.name}...`}
                  {step === "resolving_olt" && "Resolviendo OLT + descubriendo ONTs..."}
                </span>
              </div>
            )}

            {/* ═══ v57.0.0 · Step: Select Box ═══ */}
            {step === "select_box" && (
              <div data-testid="prov-select-box">
                {/* v101.22.4 · Indicador filtro sucursal */}
                {boxBranchFilter?.nomenclatura && (
                  <div className="mb-2 px-2.5 py-1.5 rounded-lg text-[10px] font-mono"
                    style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46" }}
                    data-testid="prov-box-branch-filter">
                    Solo cajas {boxBranchFilter.nomenclatura}_* · {boxBranchFilter.branch_name || "sucursal"}
                    {boxBranchFilter.source === "ot_cliente" ? " (cliente/OT)" : boxBranchFilter.source === "usuario" ? " (usuario)" : ""}
                  </div>
                )}
                {/* v72 · Buscador por nombre */}
                <div className="relative mb-3">
                  <input
                    type="text"
                    placeholder={boxBranchFilter?.nomenclatura
                      ? `Buscar caja ${boxBranchFilter.nomenclatura}_…`
                      : "Buscar caja por nombre (ej: QT_CVFA-78)..."}
                    value={boxSearch}
                    onChange={(e) => handleSearchBoxes(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 text-xs rounded-xl border outline-none transition focus:ring-2"
                    style={{ borderColor: NEU.border, color: NEU.text, background: "#FFFFFF", "--tw-ring-color": NEU.accent }}
                    data-testid="prov-box-search"
                  />
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
                  {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin opacity-50" />}
                </div>
                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-xl mb-3" style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}>
                    <AlertTriangle size={14} style={{ color: "#D97706", flexShrink: 0, marginTop: 2 }} />
                    <span className="text-xs" style={{ color: "#92400E" }}>{error}</span>
                  </div>
                )}
                {/* v101.114.0 · Mapa de cajas: cliente + cajas encontradas como postes; clic en un poste = elegir la caja */}
                {boxes.some((b) => b.latitude != null && b.longitude != null) && (
                  <div className="mb-3 rounded-xl overflow-hidden relative isolate" style={{ height: 240, border: `1.5px solid ${NEU.border}`, zIndex: 0 }}
                    data-testid="prov-box-map">
                    <MapContainer
                      key={`${boxes.length}-${boxes[0]?.id}`}
                      bounds={[
                        ...(boxClient ? [boxClient] : []),
                        ...boxes.filter((b) => b.latitude != null && b.longitude != null).slice(0, 60).map((b) => [b.latitude, b.longitude]),
                      ]}
                      boundsOptions={{ padding: [30, 30], maxZoom: 18 }}
                      style={{ height: "100%", width: "100%", zIndex: 0 }}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                      {boxClient && (
                        <Marker position={boxClient} icon={clientIcon}>
                          <Popup><div className="text-xs font-bold">{clientName || "Cliente"}</div></Popup>
                        </Marker>
                      )}
                      <BoxPolesLayer items={boxes} onSelect={(b) => handleSelectBox(boxes.find((x) => x.id === b.id) || b)} />
                    </MapContainer>
                  </div>
                )}
                {boxes.length === 0 ? (
                  <div className="text-center py-8">
                    <Package size={28} className="mx-auto mb-2 opacity-30" style={{ color: NEU.textMuted }} />
                    <p className="text-xs" style={{ color: NEU.textMuted }}>No se encontraron cajas cercanas. Aumente el radio.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {boxes.map((box) => (
                      <button
                        key={box.id}
                        onClick={() => handleSelectBox(box)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition hover:scale-[1.005]"
                        style={{
                          background: "#FFFFFF",
                          border: `1.5px solid ${NEU.border}`,
                          boxShadow: NEU.shadowDown,
                        }}
                        data-testid={`prov-box-${box.id}`}
                      >
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#DBEAFE" }}>
                          <Package size={16} style={{ color: "#1E40AF" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold truncate" style={{ color: NEU.text }}>{box.name}</div>
                          <div className="text-[10px] mt-0.5" style={{ color: NEU.textMuted }}>
                            OLT: {box.olt || "—"} · {box.has_free_ports ? "Con puertos libres" : "—"}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="px-2.5 py-1 rounded-lg text-[10px] font-bold" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
                            {box.distance_m != null ? `${box.distance_m}m` : "Buscar"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ═══ v57.0.0 · Step: Select Port ═══ */}
            {step === "select_port" && selectedBox && (
              <div data-testid="prov-select-port">
                <div className="text-xs font-bold mb-3" style={{ color: NEU.text }}>
                  Puertos libres de <span className="font-mono">{selectedBox.name}</span> ({ports.length})
                </div>
                {error && (
                  <div className="flex items-start gap-2 p-3 rounded-xl mb-3" style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}>
                    <AlertTriangle size={14} style={{ color: "#D97706", flexShrink: 0, marginTop: 2 }} />
                    <span className="text-xs" style={{ color: "#92400E" }}>{error}</span>
                  </div>
                )}
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {ports.map((port) => (
                    <button
                      key={port.id}
                      onClick={() => handleSelectPort(port)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition hover:scale-[1.005]"
                      style={{
                        background: "#FFFFFF",
                        border: `1.5px solid ${NEU.border}`,
                        boxShadow: NEU.shadowDown,
                      }}
                      data-testid={`prov-port-${port.id}`}
                    >
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#D1FAE5" }}>
                        <Wifi size={16} style={{ color: "#059669" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold truncate" style={{ color: NEU.text }}>{port.name}</div>
                        {port.equipment_type && (
                          <div className="text-[10px]" style={{ color: NEU.textMuted }}>Tipo: {port.equipment_type}</div>
                        )}
                      </div>
                      {/* v101.90.0 · Traslado: puerto que el cliente usa hoy */}
                      {port.is_current ? (
                        <div className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
                          ACTUAL DEL CLIENTE
                        </div>
                      ) : (
                        <div className="px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: "#D1FAE5", color: "#059669" }}>
                          LIBRE
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* ═══ v57.0.0 · Error en estado idle (post-residencial) ═══ */}
            {step === "idle" && error && (
              <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: "#FEE2E2", border: "1px solid #FECACA" }} data-testid="prov-idle-error">
                <XCircle size={18} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div className="text-xs font-bold text-red-800">Error en aprovisionamiento</div>
                  <div className="text-[11px] text-red-700 mt-1">{error}</div>
                  <button
                    onClick={() => handleLoadBoxes(radiusM)}
                    className="mt-2 text-[10px] font-semibold text-red-700 underline"
                  >
                    Reintentar seleccion de caja
                  </button>
                </div>
              </div>
            )}

            {/* Step: Discovery — Loading */}
            {step === "discover" && loading && (
              <div className="flex items-center justify-center gap-3 py-8" data-testid="discover-loading">
                <Loader2 size={24} className="animate-spin" style={{ color: NEU.accent }} />
                <span className="text-sm" style={{ color: NEU.text }}>Consultando OLT...</span>
              </div>
            )}

            {/* Step: Discovery — Error */}
            {step === "discover" && error && !loading && (
              <div
                className="flex items-start gap-3 p-4 rounded-xl"
                style={{ background: "#FEE2E2", border: "1px solid #FECACA" }}
                data-testid="discover-error"
              >
                <XCircle size={18} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div className="text-xs font-bold text-red-800">Error al consultar OLT</div>
                  <div className="text-[11px] text-red-700 mt-1">{error}</div>
                  <button
                    onClick={hasDirectOlt ? handleDiscover : handleReset}
                    className="mt-2 text-[10px] font-semibold text-red-700 underline"
                  >
                    {hasDirectOlt ? "Reintentar" : "Volver a seleccionar caja"}
                  </button>
                </div>
              </div>
            )}

            {/* Step: Discovery — Resultados */}
            {step === "discover" && discoveryResult && !loading && !error && (
              <div data-testid="discover-results">
                {/* Contexto de la OT */}
                <div className="flex flex-wrap gap-4 mb-4 text-[10px]" style={{ color: NEU.textMuted }}>
                  <span>Contrato: <strong style={{ color: NEU.text }}>{discoveryResult?.ot_context?.subscription_code || subscriptionCode || "—"}</strong></span>
                  <span>Cliente: <strong style={{ color: NEU.text }}>{discoveryResult?.ot_context?.client_name || clientName || "—"}</strong></span>
                  <span>Plan: <strong style={{ color: NEU.text }}>{discoveryResult?.ot_context?.plan_name || planName || "—"}</strong></span>
                </div>

                {/* Lista de ONTs */}
                {(() => {
                  // v73.1 · Extracción robusta: siempre lista plana
                  const raw = discoveryResult.onts;
                  const ontList = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.data?.ont_entries) ? raw.data.ont_entries
                    : Array.isArray(raw?.ont_entries) ? raw.ont_entries
                    : [];
                  if (ontList.length === 0) {
                    return (
                      <div className="text-center py-6" data-testid="discover-empty">
                        <Wifi size={28} className="mx-auto mb-2 opacity-30" style={{ color: NEU.textMuted }} />
                        <p className="text-xs font-semibold" style={{ color: NEU.textMuted }}>
                          No se detectaron ONTs pendientes en esta OLT
                        </p>
                        <p className="text-[10px] mt-1" style={{ color: NEU.textMuted }}>
                          {discoveryResult.olt_message || "La OLT respondió pero no hay equipos sin aprovisionar."}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div>
                      {/* v97.7.0 · Banner informativo verde si hay ONTs disponibles */}
                      <div className="flex items-center gap-2 p-2.5 rounded-xl mb-2" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                        <Wifi size={14} style={{ color: "#059669" }} />
                        <span className="text-xs font-semibold" style={{ color: "#166534" }}>
                          {ontList.length} ONTs detectadas — seleccione la que desea aprovisionar
                        </span>
                      </div>
                      {/* v91.3.0 · Banner amarillo solo si NO hay ninguna ONT */}
                      {isTraslado && originSn && !ontList.some(o => trasladoSameEquipment(originAliases, [o.sn_use, o.sn_fabric], true)) && (
                        <div className="flex items-start gap-2 p-2.5 rounded-xl mb-2" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                          <AlertTriangle size={14} style={{ color: "#D97706", flexShrink: 0, marginTop: 2 }} />
                          <span className="text-[11px]" style={{ color: "#92400E" }}>
                            El serial de origen <b>{originSn}</b> no aparece entre las ONTs detectadas.
                            Puede seleccionar otra ONT si corresponde al equipo trasladado.
                          </span>
                        </div>
                      )}
                      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                        {ontList.map((ont, idx) => {
                          const isSameSerial = isTraslado && originSn && trasladoSameEquipment(originAliases, [ont.sn_use, ont.sn_fabric], true);
                          const isDifferentSerial = isTraslado && originSn && !isSameSerial;
                          return (
                          <button
                            key={ont.sn_use || ont.sn_fabric || idx}
                            onClick={() => handleSelectOnt(ont)}
                            className="w-full text-left flex items-center gap-3 p-3 rounded-xl transition hover:shadow-md"
                            style={{
                              background: isSameSerial ? "#ECFDF5" : "#FFFFFF",
                              border: `1.5px solid ${isSameSerial ? "#6EE7B7" : NEU.border}`,
                              boxShadow: NEU.shadowDown,
                            }}
                            data-testid={`ont-item-${idx}`}
                          >
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: isSameSerial ? "#10B98120" : "#E0E62120" }}>
                              <Wifi size={14} style={{ color: isSameSerial ? "#059669" : "#0A3747" }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold font-mono" style={{ color: NEU.text }}>
                                {ont.sn_use || "Sin SN"}
                                {isSameSerial && (
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: "#059669", color: "#fff" }}>
                                    MISMO EQUIPO
                                  </span>
                                )}
                                {isDifferentSerial && (
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: "#F59E0B", color: "#fff" }}>
                                    SERIAL DIFERENTE
                                  </span>
                                )}
                                {(techOnus || []).some(t => t.sn_use === (ont.sn_use || "").toUpperCase() || (ont.sn_fabric && t.serial === ont.sn_fabric.toUpperCase())) && (
                                  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: "#2563EB", color: "#fff" }}
                                    title="La serie está en la bodega del técnico asignado">
                                    EN INVENTARIO DEL TÉCNICO
                                  </span>
                                )}
                              </div>
                              {ont.sn_fabric && (
                                <div className="text-[10px] font-mono" style={{ color: NEU.textMuted }}>
                                  Fabric: {ont.sn_fabric}
                                </div>
                              )}
                              {(ont.frame != null || ont.slot != null || ont.port != null) && (
                                <div className="text-[10px] font-mono" style={{ color: NEU.textMuted }}>
                                  F{ont.frame}/S{ont.slot}/P{ont.port}{ont.number != null ? `/N${ont.number}` : ""}
                                </div>
                              )}
                              {isDifferentSerial && (
                                <div className="flex items-center gap-1 text-[9px] mt-0.5" style={{ color: "#D97706" }}>
                                  <AlertTriangle size={10} /> Serial diferente al equipo de origen. Verifique antes de continuar.
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-[9px] px-2 py-0.5 rounded-lg font-semibold" style={{ background: "#DCFCE7", color: "#166534" }}>
                                Disponible
                              </div>
                              <PlayCircle size={16} style={{ color: NEU.accent }} />
                            </div>
                          </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Step: Configure — Formulario de confirmación antes de aprovisionar */}
            {/* v93.5.0 · Tipo de aprovisionamiento (después de caja y puerto) */}
            {step === "choose_mode" && (
              <div data-testid="provision-choose-mode" className="space-y-3">
                <div className="text-xs font-bold" style={{ color: NEU.text }}>
                  Caja {selectedBox?.name} · Puerto {selectedPort?.port_number ?? selectedPort?.name}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={handleChooseAuto} data-testid="btn-mode-auto"
                    className="p-4 rounded-xl text-left transition hover:scale-[1.01]"
                    style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0" }}>
                    <div className="text-sm font-bold" style={{ color: "#059669" }}>Automático</div>
                    <div className="text-[11px] mt-1" style={{ color: NEU.textMuted }}>Consulta la OLT, detecta la ONU y asigna VLAN e IP.</div>
                  </button>
                  <button onClick={handleChooseManual} data-testid="btn-mode-manual"
                    className="p-4 rounded-xl text-left transition hover:scale-[1.01]"
                    style={{ background: NEU.bgSurface, border: `1.5px solid ${NEU.border}` }}>
                    <div className="text-sm font-bold" style={{ color: NEU.accent }}>Manual</div>
                    <div className="text-[11px] mt-1" style={{ color: NEU.textMuted }}>Sin consultar la OLT: equipo descargado por el técnico, VLAN e IP.</div>
                  </button>
                </div>
                <button onClick={() => setStep("select_port")} className="text-[11px] underline" style={{ color: NEU.textMuted }}>
                  ← Cambiar puerto
                </button>
              </div>
            )}

            {step === "manual_select" && (
              <div data-testid="provision-manual-select" className="space-y-3">
                <div className="text-xs font-bold" style={{ color: NEU.text }}>Equipo de Receta (CPE)</div>

                {/* v98.0.0 · TRASLADO: serial de origen precargado */}
                {isTraslado && trasladoOrigin?.origin?.sn_use ? (
                  <>
                    <div className="p-3 rounded-lg text-[11px] space-y-1" style={{ background: "#EFF6FF", border: "1.5px solid #93C5FD" }}>
                      <div className="flex items-center gap-1.5 font-bold" style={{ color: "#1E40AF" }}>
                        <RotateCcw size={12} /> Serial de origen (traslado · mismo equipo)
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <input value={manualSerialInput} onChange={(e) => setManualSerialInput(e.target.value)}
                        placeholder="Serie del equipo" data-testid="manual-serial-input"
                        className="flex-1 px-3 py-2 text-xs rounded-lg font-mono"
                        style={{ border: `1.5px solid #93C5FD`, background: "#F0F9FF" }} />
                      <button disabled={!manualSerialInput.trim()} onClick={() => handlePickManualSerial(manualSerialInput)}
                        data-testid="manual-serial-confirm"
                        className="px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-40"
                        style={{ background: NEU.accent, color: "#fff" }}>
                        Confirmar
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {manualSerials.length === 0 && (
                      <div className="text-[11px] p-3 rounded-lg" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="manual-no-recipe">
                        {isResMiNegocio
                          ? "No hay CPE (onu/onu_wifi) en Receta. Registre materiales y vuelva."
                          : "Sin series en Receta. Registre los materiales (equipo de la bodega del técnico) y vuelva."}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {manualSerials.map((sn) => {
                        const mat = [...(downloadedOnus || []), ...(downloadedMaterials || [])].find((t) => String(t.serial || "").toUpperCase() === sn);
                        return (
                          <button key={sn} onClick={() => handlePickManualSerial(sn)} data-testid={`manual-serial-${sn}`}
                            className="px-3 py-2 rounded-lg text-xs font-mono transition hover:opacity-80 text-left"
                            style={{ border: "1.5px solid #6EE7B7", background: "#ECFDF5" }}>
                            <div className="font-bold">{sn}</div>
                            {mat?.product_name && <div className="text-[9px] font-sans" style={{ color: NEU.textMuted }}>{mat.product_name}</div>}
                            <div className="text-[9px] font-sans font-bold" style={{ color: "#059669" }}>Receta</div>
                          </button>
                        );
                      })}
                    </div>
                    {/* v101.153.0 · Sin campo para digitar: la serie se toma solo de la Receta (bodega del técnico) */}
                  </>
                )}

                <button onClick={() => setStep("choose_mode")} className="text-[11px] underline" style={{ color: NEU.textMuted }}>
                  ← Cambiar tipo de aprovisionamiento
                </button>
              </div>
            )}

            {step === "configure" && selectedOnt && (
              <div data-testid="provision-configure">
                <div className="text-xs font-bold mb-3" style={{ color: NEU.text }}>
                  Confirmar datos de aprovisionamiento
                </div>

                {/* ONT seleccionada */}
                <div
                  className="flex items-center gap-3 p-3 rounded-xl mb-4"
                  style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0" }}
                  data-testid="selected-ont-summary"
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "#0A374710" }}>
                    <Wifi size={18} style={{ color: "#0A3747" }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-bold font-mono" style={{ color: NEU.text }}>
                      {selectedOnt.manual
                        ? <>CPE Receta: {selectedOnt.sn_fabric || "—"}</>
                        : <>SN (PON): {selectedOnt.sn_use || "—"}</>}
                    </div>
                    <div className="text-[10px] font-mono" style={{ color: NEU.textMuted }}>
                      {selectedOnt.manual
                        ? <>PON: {(manualPonInput || "—").toUpperCase()}</>
                        : <>Fabric: {selectedOnt.sn_fabric || "—"}</>}
                      {!selectedOnt.manual && (selectedOnt.frame != null || selectedOnt.slot != null || selectedOnt.port != null) && (
                        <span className="ml-2">
                          F{selectedOnt.frame}/S{selectedOnt.slot}/P{selectedOnt.port}
                          {selectedOnt.number != null && `/N${selectedOnt.number}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <CheckCircle2 size={18} style={{ color: "#059669" }} />
                </div>

                {/* v80.0 · Yellow Banner: SN ya enganchado en otro contrato */}
                {snChecking && (
                  <div className="flex items-center gap-2 p-3 rounded-xl mb-4" style={{ background: "#F0F9FF", border: "1px solid #BAE6FD" }} data-testid="sn-check-loading">
                    <Loader2 size={14} className="animate-spin" style={{ color: "#0284C7" }} />
                    <span className="text-[11px]" style={{ color: "#0369A1" }}>Verificando serial en base de datos...</span>
                  </div>
                )}
                {!snChecking && snEngaged?.engaged && (
                  <div className="flex items-start gap-3 p-3 rounded-xl mb-4" style={{ background: "#FFFBEB", border: "1.5px solid #FDE68A" }} data-testid="sn-engaged-warning">
                    <AlertTriangle size={18} style={{ color: "#D97706", flexShrink: 0, marginTop: 2 }} />
                    <div className="flex-1">
                      <div className="text-xs font-bold" style={{ color: "#92400E" }}>
                        Serial ya activo en otro equipo
                      </div>
                      <div className="text-[11px] mt-1" style={{ color: "#A16207" }}>
                        El SN <strong className="font-mono">{selectedOnt.sn_use}</strong> aparece como <em>no libre</em> en la base de equipos pasivos.
                        Verifique antes de continuar.
                      </div>
                      {snEngaged.records?.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {snEngaged.records.map((rec, i) => (
                            <div key={i} className="text-[10px] font-mono px-2 py-1 rounded" style={{ background: "#FEF3C7" }}>
                              {rec.name || "—"} · OLT: {rec.olt_name || "—"} · Caja: {rec.box_name || "—"}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* v80.0 · Red Banner: materiales no descargados */}
                {!materialsLoading && materialsCheck && !materialsCheck.materials_registered && materialsCheck.has_submission && !materialsCheck.blocked && (
                  <div className="flex items-start gap-3 p-3 rounded-xl mb-4" style={{ background: "#FEF2F2", border: "1.5px solid #FECACA" }} data-testid="materials-not-registered-warning">
                    <AlertTriangle size={18} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
                    <div className="flex-1">
                      <div className="text-xs font-bold" style={{ color: "#991B1B" }}>
                        Descarga de materiales pendiente
                      </div>
                      <div className="text-[11px] mt-1" style={{ color: "#B91C1C" }}>
                        El técnico aún no ha registrado la descarga de materiales (bodega). El aprovisionamiento queda bloqueado hasta que se complete.
                      </div>
                    </div>
                  </div>
                )}

                {/* Contexto OT (auto-rellenado) */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-xl" style={{ background: NEU.bgSurface, border: `1px solid ${NEU.border}` }}>
                    <div className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: NEU.textLabel }}>Contrato</div>
                    <div className="text-xs font-mono font-bold" style={{ color: NEU.text }}>
                      {discoveryResult?.ot_context?.subscription_code || subscriptionCode || "—"}
                    </div>
                  </div>
                  <div className="p-3 rounded-xl" style={{ background: NEU.bgSurface, border: `1px solid ${NEU.border}` }}>
                    <div className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: NEU.textLabel }}>Plan</div>
                    <div className="text-xs font-mono font-bold" style={{ color: NEU.text }}>
                      {discoveryResult?.ot_context?.plan_name || planName || "—"}
                    </div>
                  </div>
                  <div className="p-3 rounded-xl col-span-2" style={{ background: NEU.bgSurface, border: `1px solid ${NEU.border}` }}>
                    <div className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: NEU.textLabel }}>Cliente</div>
                    <div className="text-xs font-bold" style={{ color: NEU.text }}>
                      {discoveryResult?.ot_context?.client_name || clientName || "—"}
                    </div>
                  </div>
                </div>

                {/* v101.22.16 · Modo ya elegido en choose_mode — sin segundo switch */}
                <div className="text-[10px] mb-3 px-2 py-1 rounded-lg inline-block" style={{ background: NEU.bgSurface, color: NEU.textMuted }} data-testid="prov-mode-label">
                  Modo: <b style={{ color: NEU.text }}>{provMode === "manual" ? "Manual" : "Automático"}</b>
                  {autoVlanPickMode && provMode === "auto" ? " · reintento (puede elegir VLAN)" : ""}
                </div>

                {/* v101.50.4 · Manual: PON/MAC obligatorio (≥1 caracter) → cierra como mac_number / pon_mac_id */}
                {provMode === "manual" && (
                  <div className="mb-4 p-3 rounded-xl space-y-2" style={{ background: "#EFF6FF", border: "1.5px solid #93C5FD" }} data-testid="manual-pon-block">
                    <div className="text-xs font-bold" style={{ color: "#1E40AF" }}>PON / MAC del equipo *</div>
                    <div className="text-[10px]" style={{ color: "#1E3A5F" }}>
                      Obligatorio. Serie Receta: <b className="font-mono">{selectedOnt.sn_fabric || selectedOnt.sn_use || "—"}</b>
                      {" "}— va a Odoo como <code>mac_number</code> / <code>pon_mac_id</code>; si el equipo tiene el mismo PON que la serie, ingrese el mismo valor.
                    </div>
                    <input
                      value={manualPonInput}
                      onChange={(e) => setManualPonInput(e.target.value)}
                      placeholder="PON/MAC (mín. 1 caracter, hex ej. 5A544547…)"
                      data-testid="manual-pon-input"
                      required
                      className="w-full px-3 py-2 text-xs rounded-lg font-mono"
                      style={{
                        border: `1.5px solid ${!(manualPonInput || "").trim() ? "#F87171" : "#93C5FD"}`,
                        background: "#fff",
                      }}
                    />
                    {!(manualPonInput || "").trim() && (
                      <div className="text-[10px] font-semibold" style={{ color: "#DC2626" }} data-testid="manual-pon-required-hint">
                        Ingrese el PON/MAC para habilitar Aprovisionar
                      </div>
                    )}
                  </div>
                )}

                {/* AUTO MODE: resolved info + selector en reintento */}
                {provMode === "auto" && (
                  <div className="mb-4">
                    {autoResolving && (
                      <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: "#F0F9FF", border: "1px solid #BAE6FD" }} data-testid="auto-resolving">
                        <Loader2 size={14} className="animate-spin" style={{ color: "#0284C7" }} />
                        <span className="text-xs" style={{ color: "#0369A1" }}>Auto-resolviendo VLAN + IP...</span>
                      </div>
                    )}
                    {!autoResolving && autoResolved?.error && (
                      <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: "#FEF2F2", border: "1.5px solid #FECACA" }} data-testid="auto-resolve-error">
                        <AlertTriangle size={14} style={{ color: "#DC2626", flexShrink: 0, marginTop: 2 }} />
                        <div className="text-xs" style={{ color: "#991B1B" }}>{autoResolved.error}</div>
                      </div>
                    )}
                    {!autoResolving && autoResolved?.resolved && (
                      <div className="p-3 rounded-xl space-y-2" style={{ background: autoResolved.kept_from_origin ? "#FFF7ED" : "#ECFDF5", border: `1.5px solid ${autoResolved.kept_from_origin ? "#FDBA74" : "#A7F3D0"}` }} data-testid="auto-resolved-ok">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 size={14} style={{ color: autoResolved.kept_from_origin ? "#EA580C" : "#059669" }} />
                          <span className="text-xs font-bold" style={{ color: autoResolved.kept_from_origin ? "#9A3412" : "#065F46" }}>
                            {autoResolved.kept_from_origin ? "VLAN + IP conservadas del origen (traslado)" : "VLAN + IP resueltas automaticamente"}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-2 rounded-lg" style={{ background: autoResolved.kept_from_origin ? "rgba(234,88,12,0.08)" : "rgba(5,150,105,0.08)" }}>
                            <div className="text-[9px] uppercase tracking-wider" style={{ color: "#6B7280" }}>VLAN</div>
                            <div className="text-sm font-mono font-bold" style={{ color: autoResolved.kept_from_origin ? "#9A3412" : "#065F46" }}>{autoResolved.vlan_name}</div>
                          </div>
                          <div className="p-2 rounded-lg" style={{ background: autoResolved.kept_from_origin ? "rgba(234,88,12,0.08)" : "rgba(5,150,105,0.08)" }}>
                            <div className="text-[9px] uppercase tracking-wider" style={{ color: "#6B7280" }}>IP</div>
                            <div className="text-sm font-mono font-bold" style={{ color: autoResolved.kept_from_origin ? "#9A3412" : "#065F46" }}>{autoResolved.ip_address}</div>
                          </div>
                        </div>
                        {autoVlanPickMode && (
                          <button
                            type="button"
                            onClick={() => setAutoVlanPickMode(true)}
                            className="text-[11px] underline font-semibold"
                            style={{ color: "#0369A1" }}
                            data-testid="btn-elegir-otra-vlan"
                          >
                            Elegir otra VLAN
                          </button>
                        )}
                      </div>
                    )}
                    {/* v101.22.16 · Selector VLAN/IP en 2.º intento AUTO */}
                    {autoVlanPickMode && (
                      <div className="mt-3 space-y-2 p-3 rounded-xl" style={{ background: "#F8FAFC", border: `1px solid ${NEU.border}` }} data-testid="auto-retry-vlan-pick">
                        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: NEU.textLabel }}>VLAN candidatas (reintento)</div>
                        <div className="max-h-36 overflow-y-auto rounded-lg border" style={{ borderColor: NEU.border }}>
                          {(autoPermitVlans.length ? autoPermitVlans : manualVlans).map((v) => (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => {
                                handleSelectManualVlan(v);
                                setAutoResolved({
                                  resolved: true,
                                  vlan_id: v.id,
                                  vlan_name: v.name,
                                  alfanet_id: v.alfanet_id,
                                  ip_address: selectedIp?.name || "",
                                });
                                setVlan(v.name || String(v.alfanet_id || ""));
                              }}
                              className="w-full text-left px-3 py-2 text-xs border-b last:border-b-0"
                              style={{ background: selectedVlan?.id === v.id ? "#ECFDF5" : "transparent", borderColor: NEU.border }}
                              data-testid={`auto-retry-vlan-${v.id}`}
                            >
                              <span className="font-bold">{v.name}</span>
                              <span className="ml-2 text-[10px]" style={{ color: NEU.textMuted }}>#{v.alfanet_id}</span>
                            </button>
                          ))}
                          {!autoPermitVlans.length && !manualVlans.length && (
                            <div className="text-center py-3 text-[11px]" style={{ color: NEU.textMuted }}>Sin VLANs candidatas</div>
                          )}
                        </div>
                        {selectedVlan && (
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: NEU.textLabel }}>IP libre</div>
                            <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto">
                              {manualIps.map((ip) => (
                                <button
                                  key={ip.id}
                                  type="button"
                                  onClick={() => {
                                    handleSelectManualIp(ip);
                                    setAutoResolved((prev) => ({ ...(prev || {}), resolved: true, ip_address: ip.name }));
                                  }}
                                  className="px-2 py-1 rounded text-[10px] font-mono"
                                  style={{
                                    background: selectedIp?.id === ip.id ? "#059669" : NEU.bgSurface,
                                    color: selectedIp?.id === ip.id ? "#fff" : NEU.text,
                                    border: `1px solid ${NEU.border}`,
                                  }}
                                  data-testid={`auto-retry-ip-${ip.id}`}
                                >
                                  {ip.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {!autoResolving && !autoResolved && (
                      <div className="text-xs p-3 rounded-xl" style={{ background: NEU.bgSurface, color: NEU.textMuted }}>
                        Seleccione una ONT para auto-resolver VLAN e IP.
                      </div>
                    )}
                  </div>
                )}

                {/* MANUAL MODE: VLAN selector + IP selector */}
                {provMode === "manual" && (
                  <div className="mb-4 space-y-3">
                    {/* VLAN Selector */}
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest block mb-1.5" style={{ color: NEU.textLabel }}>
                        VLAN *
                      </label>
                      <input
                        type="text"
                        value={vlanSearch}
                        onChange={(e) => setVlanSearch(e.target.value)}
                        placeholder="Buscar VLAN..."
                        className="w-full px-3 py-2 text-xs rounded-lg mb-1"
                        style={{ border: `1px solid ${NEU.border}`, background: NEU.bgLight }}
                        data-testid="manual-vlan-search"
                      />
                      {manualVlansLoading ? (
                        <div className="flex items-center gap-2 py-3 justify-center">
                          <Loader2 size={14} className="animate-spin" style={{ color: NEU.textMuted }} />
                          <span className="text-xs" style={{ color: NEU.textMuted }}>Cargando VLANs...</span>
                        </div>
                      ) : (
                        <div className="max-h-40 overflow-y-auto rounded-lg border" style={{ borderColor: NEU.border }} data-testid="manual-vlan-list">
                          {manualVlans
                            .filter(v => !vlanSearch.trim() || (v.name || "").toLowerCase().includes(vlanSearch.toLowerCase()) || String(v.alfanet_id || "").includes(vlanSearch))
                            .map((v) => (
                              <button
                                key={v.id}
                                onClick={() => handleSelectManualVlan(v)}
                                className="w-full text-left px-3 py-2 text-xs border-b last:border-b-0 transition-all hover:bg-gray-50 flex items-center justify-between"
                                style={{
                                  borderColor: NEU.border,
                                  background: selectedVlan?.id === v.id ? "#ECFDF5" : "transparent",
                                }}
                                data-testid={`manual-vlan-${v.id}`}
                              >
                                <div>
                                  <span className="font-bold" style={{ color: NEU.text }}>{v.name}</span>
                                  <span className="ml-2 text-[10px]" style={{ color: NEU.textMuted }}>#{v.alfanet_id}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {v.free_ips != null && (
                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{
                                      background: v.free_ips > 0 ? "#ECFDF5" : "#FEF2F2",
                                      color: v.free_ips > 0 ? "#059669" : "#DC2626",
                                    }}>
                                      {v.free_ips} IP{v.free_ips !== 1 ? "s" : ""} libre{v.free_ips !== 1 ? "s" : ""}
                                    </span>
                                  )}
                                  <span className="text-[10px]" style={{ color: NEU.textMuted }}>
                                    {[v.branch_office, v.client_type, v.plan_type].filter(Boolean).join(" · ")}
                                  </span>
                                </div>
                              </button>
                            ))}
                          {manualVlans.length === 0 && (
                            <div className="text-center py-4 text-xs" style={{ color: NEU.textMuted }}>
                              No hay VLANs activas con sucursal={branchOfficeId || "—"}
                              {branchOffice ? ` (${branchOffice})` : ""} · plan={planType || "—"} · cliente={resolveVlanClientType(subscriptionClientType, clientType, planName) || "—"}.
                              Modo manual no usa VLANs permitidas de la OLT.
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* IP Selector — visible post-VLAN */}
                    {selectedVlan && (
                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-widest block mb-1.5" style={{ color: NEU.textLabel }}>
                          IP * <span className="normal-case tracking-normal font-normal" style={{ color: "#059669" }}>({manualIps.length} libres{manualIps.length >= 100 ? " · primeras 100" : ""})</span>
                        </label>
                        {/* v93.9.0 · Buscar una IP específica sin cargar todas */}
                        <div className="flex gap-2 mb-2">
                          <input value={manualIpQuery} onChange={(e) => setManualIpQuery(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") loadManualIps(selectedVlan.id, manualIpQuery); }}
                            placeholder="Buscar IP (ej. 10.17.104.)" data-testid="manual-ip-search"
                            className="flex-1 px-3 py-1.5 text-xs rounded-lg font-mono"
                            style={{ border: `1px solid ${NEU.border}`, background: NEU.bgLight }} />
                          <button onClick={() => loadManualIps(selectedVlan.id, manualIpQuery)}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: NEU.accent, color: "#fff" }}>
                            Buscar
                          </button>
                        </div>
                        {manualIpsLoading ? (
                          <div className="flex items-center gap-2 py-3 justify-center">
                            <Loader2 size={14} className="animate-spin" style={{ color: NEU.textMuted }} />
                            <span className="text-xs" style={{ color: NEU.textMuted }}>Cargando IPs libres...</span>
                          </div>
                        ) : (
                          <div className="max-h-40 overflow-y-auto rounded-lg border" style={{ borderColor: NEU.border }} data-testid="manual-ip-list">
                            {manualIps.map((ip) => (
                              <button
                                key={ip.id}
                                onClick={() => handleSelectManualIp(ip)}
                                className="w-full text-left px-3 py-2 text-xs border-b last:border-b-0 transition-all hover:bg-gray-50 flex items-center justify-between"
                                style={{
                                  borderColor: NEU.border,
                                  background: selectedIp?.id === ip.id ? "#ECFDF5" : "transparent",
                                }}
                                data-testid={`manual-ip-${ip.id}`}
                              >
                                <span className="font-mono font-bold" style={{ color: NEU.text }}>{ip.name}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "#ECFDF5", color: "#059669" }}>
                                  {ip.status || "Libre"}
                                </span>
                              </button>
                            ))}
                            {manualIps.length === 0 && (
                              <div className="text-center py-4 text-xs flex items-center justify-center gap-2" style={{ color: "#DC2626" }}>
                                <AlertTriangle size={12} /> No hay IPs libres en {selectedVlan.name}.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* PON (sigue texto libre) */}
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest block mb-1.5" style={{ color: NEU.textLabel }}>
                        PON (opcional)
                      </label>
                      <input
                        type="text"
                        value={vlan}
                        readOnly
                        className="w-full px-3 py-2 text-xs font-mono rounded-lg"
                        style={{ border: `1px solid ${NEU.border}`, background: NEU.bgSurface, color: NEU.text }}
                        data-testid="manual-vlan-display"
                        placeholder="Se completa al seleccionar VLAN"
                      />
                    </div>
                  </div>
                )}

                {/* v91.1.0-RN7 · Selector tipo de equipo */}
                <div className="space-y-1.5" data-testid="tipo-equipo-selector">
                  <label className="text-[11px] font-semibold" style={{ color: NEU.textMuted }}>Tipo de Equipo</label>
                  <div className="flex gap-2">
                    {tipoOptions.map(t => (
                      <button
                        key={t}
                        data-testid={`tipo-equipo-${t}`}
                        onClick={() => setTipoEquipo(t)}
                        className="flex-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                        style={{
                          background: tipoEquipo === t ? "#059669" : NEU.bgSurface,
                          color: tipoEquipo === t ? "#fff" : NEU.text,
                          border: `1.5px solid ${tipoEquipo === t ? "#059669" : NEU.border}`,
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  {isFiberResidential && (
                    <div className="text-[10px]" style={{ color: NEU.textMuted }}>Cliente residencial de fibra: ONU WiFi u ONU + Router.</div>
                  )}
                  {/* v101.17.0 · Productos descargados/registrados por el técnico (instalación FO residencial) */}
                  {isFiberResidential && (
                    <div
                      className="mt-2 rounded-xl p-3 space-y-2"
                      style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0" }}
                      data-testid="downloaded-materials-panel"
                    >
                      <div className="text-[10px] font-mono font-bold uppercase tracking-wider" style={{ color: "#047857" }}>
                        Productos registrados por el técnico
                      </div>
                      {downloadedMaterials.length === 0 ? (
                        <div className="text-[11px]" style={{ color: "#92400E" }} data-testid="downloaded-materials-empty">
                          Aún no hay materiales descargados en esta OT. El técnico debe registrarlos en la app (Materiales).
                        </div>
                      ) : (
                        <div className="space-y-1.5" data-testid="downloaded-materials-list">
                          {downloadedMaterials.map((m, idx) => {
                            const isOnu = /ONU|ONT|WIFI|WI-FI/.test(_matBlob(m));
                            const isRouter = /ROUTER|MIKROTIK/.test(_matBlob(m));
                            const badge = isOnu ? "ONU" : isRouter ? "Router" : "Material";
                            // v101.17.0 · Serial ocupado por aprovisionamiento: no removible
                            const isLocked = !!(m.locked || (materialsCheck?.locked_serials || []).some(
                              (s) => String(s).toUpperCase() === String(m.serial || "").toUpperCase()
                            ));
                            return (
                              <div
                                key={`${m.product_id || m.product_code || "m"}-${m.serial || idx}`}
                                className="flex items-start justify-between gap-2 px-2.5 py-2 rounded-lg"
                                style={{
                                  background: isLocked ? "#FFFBEB" : "#fff",
                                  border: `1px solid ${isLocked ? "#FCD34D" : "#D1FAE5"}`,
                                }}
                                data-testid={`downloaded-material-${idx}`}
                              >
                                <div className="min-w-0">
                                  <div className="text-xs font-bold truncate" style={{ color: NEU.text }}>
                                    {m.product_name || m.product_code || "Producto"}
                                  </div>
                                  <div className="text-[10px] font-mono mt-0.5" style={{ color: NEU.textMuted }}>
                                    {m.product_code || "—"}
                                    {m.serial ? ` · SN ${m.serial}` : ""}
                                    {m.quantity != null ? ` · qty ${m.quantity}` : ""}
                                  </div>
                                  {isLocked && (
                                    <div className="text-[9px] font-bold mt-1" style={{ color: "#92400E" }} data-testid={`downloaded-material-locked-${idx}`}>
                                      Bloqueado · aprovisionado (no se puede retirar)
                                    </div>
                                  )}
                                </div>
                                <span
                                  className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0"
                                  style={{
                                    background: isLocked ? "#D97706" : (isOnu || isRouter ? "#059669" : "#E5E7EB"),
                                    color: isLocked || isOnu || isRouter ? "#fff" : "#374151",
                                  }}
                                >
                                  {isLocked ? "Locked" : badge}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {(tipoEquipo === "ONU-WiFi" || tipoEquipo === "ONU+Router") && downloadedOnus.length > 0 && (
                        <div className="text-[10px]" style={{ color: "#047857" }} data-testid="downloaded-onu-hint">
                          ONU/ONT registradas: {downloadedOnus.map((m) => m.serial || m.product_name).filter(Boolean).join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                  {tipoEquipo === "ONU+Router" && (
                    <div className="mt-1 space-y-1">
                      {/* v96.4.0 / v101.17.0 · Router: descargados en OT primero, luego bodega técnico */}
                      <select data-testid="router-serial-select" value={techRouters.some(r => r.serial === routerSerial) ? routerSerial : ""}
                        onChange={e => {
                          const v = e.target.value;
                          setRouterSerial(v);
                          // v101.17.0 · Auto-descargo del inventario al seleccionar router (instalación FO)
                          if (v) occupyMaterialSerial(v, "Router");
                        }}
                        className="w-full px-3 py-2 text-xs font-mono rounded-lg"
                        style={{ border: `1px solid ${NEU.border}`, background: NEU.bgSurface, color: NEU.text }}>
                        <option value="">
                          {techRouters.length
                            ? "— Seleccione router (descargado / inventario) —"
                            : "Sin routers descargados ni en inventario del técnico"}
                        </option>
                        {techRouters.map(r => (
                          <option key={r.serial} value={r.serial}>
                            {r.serial} · {r.product_name}{r.from_download ? " · descargado OT" : ""}
                          </option>
                        ))}
                      </select>
                      {/* v101.153.0 · Solo traslado: router del cliente (no sale de bodega ni va a la receta) */}
                      {isTraslado && (
                        <input
                          data-testid="router-serial-input"
                          value={routerSerial}
                          onChange={e => setRouterSerial(e.target.value.toUpperCase())}
                          placeholder="Traslado: serie del router del cliente"
                          className="w-full px-3 py-2 text-xs font-mono rounded-lg"
                          style={{ border: `1px solid ${NEU.border}`, background: NEU.bgSurface, color: NEU.text }}
                        />
                      )}
                      <div className="text-[9px]" style={{ color: "#047857" }}>
                        Al seleccionar el router se descarga automáticamente a materiales de la OT.
                      </div>
                    </div>
                  )}
                </div>

                {/* Botón ejecutar */}
                {(() => {
                  // v97.4.0 · Bloquea también cuando aún no existe formulario (antes pasaba sin materiales)
                  // v98.3.0 · Traslado omite validación de materiales
                  const materialsBlocked = isTraslado ? false : (materialsCheck ? (materialsCheck.blocked ?? (!materialsCheck.materials_registered)) : false);
                  const autoNotReady = provMode === "auto" && (!autoResolved?.resolved);
                  const manualNotReady = provMode === "manual" && (!selectedVlan || !selectedIp);
                  // v101.50.4 · PON/MAC no vacío: manual=input; auto=sn_use del discover
                  const ponFilled = provMode === "manual"
                    ? (manualPonInput || "").trim().length > 0
                    : !!(selectedOnt?.sn_use || "").trim();
                  const ponMissing = !ponFilled;
                  const cantExecute = provisioning || materialsBlocked || autoNotReady || manualNotReady || ponMissing;
                  const provisionHandler = isTraslado ? handleTrasladoProvision : handleProvision;
                  return (
                    <div className="relative">
                      <button
                        onClick={provisionHandler}
                        disabled={cantExecute}
                        data-testid="btn-execute-provision"
                        className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl font-bold text-sm transition-all disabled:cursor-not-allowed"
                        style={{
                          background: cantExecute ? NEU.bgSurface : `linear-gradient(135deg, #059669 0%, #10B981 100%)`,
                          color: cantExecute ? NEU.textMuted : "#FFFFFF",
                          boxShadow: !cantExecute ? `0 4px 14px rgba(5,150,105,0.3)` : "none",
                          border: `1.5px solid ${cantExecute ? NEU.border : "transparent"}`,
                          opacity: cantExecute ? 0.5 : 1,
                        }}
                      >
                        {provisioning ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Zap size={16} />
                        )}
                        {provisioning ? "Aprovisionando..." : "Ejecutar Aprovisionamiento"}
                      </button>
                      {materialsBlocked && !provisioning && (
                        <div className="text-[10px] mt-1.5 text-center font-semibold" style={{ color: "#DC2626" }} data-testid="materials-blocked-reason">
                          Descarga de materiales pendiente — aprovisionamiento bloqueado
                        </div>
                      )}
                      {!materialsBlocked && ponMissing && !provisioning && (
                        <div className="text-[10px] mt-1.5 text-center font-semibold" style={{ color: "#DC2626" }} data-testid="pon-missing-reason">
                          {provMode === "manual"
                            ? "Ingrese el PON/MAC (no puede ir vacío) para aprovisionar"
                            : "Seleccione una ONT con SN/PON válido"}
                        </div>
                      )}
                      {!materialsBlocked && !ponMissing && autoNotReady && !provisioning && (
                        <div className="text-[10px] mt-1.5 text-center font-semibold" style={{ color: "#D97706" }} data-testid="auto-not-ready-reason">
                          {autoResolving ? "Resolviendo VLAN+IP..." : "VLAN e IP no resueltas — seleccione ONT o intente modo manual"}
                        </div>
                      )}
                      {!materialsBlocked && !ponMissing && manualNotReady && !provisioning && (
                        <div className="text-[10px] mt-1.5 text-center font-semibold" style={{ color: "#D97706" }} data-testid="manual-not-ready-reason">
                          Seleccione VLAN e IP para habilitar el aprovisionamiento
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="text-[10px] mt-2 text-center" style={{ color: NEU.textMuted }}>
                  Se enviará el comando <code className="font-mono bg-slate-100 px-1 rounded">set-ont</code> a la OLT {oltName || oltIp}
                </div>
              </div>
            )}

            {/* Step: Executing — Animación de carga */}
            {step === "executing" && (
              <div className="flex flex-col items-center justify-center py-10 gap-4" data-testid="provision-executing">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#0A374710" }}>
                    <Loader2 size={32} className="animate-spin" style={{ color: NEU.accent }} />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full animate-pulse flex items-center justify-center" style={{ background: "#E0E621" }}>
                    <Zap size={10} style={{ color: "#0A3747" }} />
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm font-bold" style={{ color: NEU.text }}>Aprovisionando ONT en la OLT</div>
                  <div className="text-[11px] mt-1" style={{ color: NEU.textMuted }}>
                    SN: <span className="font-mono font-bold">{selectedOnt?.sn_use}</span> · OLT: {oltIp}
                  </div>
                  <div className="text-[10px] mt-2" style={{ color: NEU.textMuted }}>
                    Enviando comando set-ont... no cierre esta ventana.
                  </div>
                </div>
              </div>
            )}

            {/* Step: Success */}
            {step === "success" && provisionResult && (
              <div data-testid="provision-success">
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#DCFCE7" }}>
                    <CheckCircle2 size={32} style={{ color: "#059669" }} />
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold" style={{ color: "#059669" }} data-testid="provision-complete-title">
                      APROVISIONAMIENTO COMPLETO
                    </div>
                    <div className="text-xs mt-1" style={{ color: "#047857" }}>
                      {provisionResult.manual ? "Registro manual" : "Equipo activo en la red"}
                    </div>
                  </div>
                </div>

                {/* Resumen del resultado */}
                <div className="rounded-xl p-4 space-y-2 mb-4" style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0" }}>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div>
                      <span style={{ color: NEU.textLabel }}>SN (OLT): </span>
                      <span className="font-mono font-bold" style={{ color: NEU.text }}>{provisionResult.sn_use}</span>
                    </div>
                    {provisionResult.vlan && (
                      <div>
                        <span style={{ color: NEU.textLabel }}>VLAN: </span>
                        <span className="font-mono font-bold" style={{ color: NEU.text }}>{provisionResult.vlan}</span>
                      </div>
                    )}
                    {provisionResult.olt_ip && (
                      <div>
                        <span style={{ color: NEU.textLabel }}>OLT IP: </span>
                        <span className="font-mono font-bold" style={{ color: NEU.text }}>{provisionResult.olt_ip}</span>
                      </div>
                    )}
                    <div>
                      <span style={{ color: NEU.textLabel }}>Tipo: </span>
                      <span className="font-mono font-bold" style={{ color: NEU.text }}>{provisionResult.tipo_equipo || tipoEquipo}</span>
                    </div>
                  </div>
                </div>

                {/* v91.1.0-RN7 · Paso Axxess (WiFi) */}
                {axxessStep && !(axxessResult?.ok) && (
                  <div className="rounded-xl p-4 mb-4 space-y-3" style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA" }} data-testid="axxess-step">
                    <div className="text-xs font-bold" style={{ color: "#9A3412" }}>
                      Configurar WiFi (Axxess){provisionResult?.tipo_solucion === "traslado" ? " · Traslado" : ""}
                    </div>
                    <div className="space-y-2">
                      <UnifyNetworksToggle
                        checked={unifyNetworks}
                        testid="unify-networks-prov"
                        onChange={(v) => { setUnifyNetworks(v); if (v) setSsid5(ssid24); }}
                      />
                      <SsidSuffixInput
                        label={unifyNetworks ? "SSID (2.4 y 5 GHz)" : "SSID 2.4 GHz"}
                        value={ssid24}
                        onChange={(v) => { setSsid24(v); if (unifyNetworks) setSsid5(v); }}
                        testid="ssid24-input"
                      />
                      {!unifyNetworks && (
                        <SsidSuffixInput label="SSID 5 GHz" value={ssid5} onChange={setSsid5} testid="ssid5-input" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button data-testid="btn-axxess-configure" onClick={handleAxxessConfigure} disabled={axxessLoading}
                        className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition"
                        style={{ background: axxessLoading ? NEU.border : "#EA580C" }}>
                        {axxessLoading ? "Configurando WiFi..." : "Aplicar Configuración WiFi"}
                      </button>
                      {/* v101.66.0 · WiFi OBLIGATORIO: sin «Omitir / Configurar después» */}
                    </div>
                    {/* v101.57.0 · Manual / Axxess caído: registrar el SSID configurado a mano en el equipo */}
                    {canManualMode && (provMode === "manual" || axxessResult?.ok === false) && (
                      <button data-testid="btn-axxess-manual" onClick={handleAxxessManual} disabled={axxessLoading}
                        className="w-full px-4 py-2 rounded-xl text-xs font-semibold transition"
                        style={{ background: "#FFFFFF", color: "#9A3412", border: "1.5px solid #FDBA74" }}>
                        Registrar SSID sin Axxess (WiFi configurado a mano en el equipo)
                      </button>
                    )}
                  </div>
                )}
                {axxessResult && (
                  <div className="rounded-xl p-3 mb-4 text-xs" data-testid="axxess-result"
                    style={{ background: axxessResult.ok ? "#F0FDF4" : "#FEF2F2", border: `1.5px solid ${axxessResult.ok ? "#BBF7D0" : "#FECACA"}`, color: axxessResult.ok ? "#059669" : "#DC2626" }}>
                    {axxessResult.ok
                      ? (axxessResult.manual ? "SSID registrado (configurado a mano)" : "WiFi configurado correctamente")
                      : `WiFi: ${axxessResult.error || "Error"} · reintente o registre el SSID`}
                  </div>
                )}

                {/* v95.3.0 · Sin "Aprovisionar otra": la OT queda aprovisionada; para repetir se usa Cancelar aprovisionamiento */}
                {/* v101.66.0 · No se puede finalizar el asistente sin el WiFi (Axxess) configurado o registrado */}
                <button
                  onClick={() => { setOpen(false); setStep("idle"); setSelectedOnt(null); setProvisionResult(null); setAxxessStep(false); setAxxessResult(null); }}
                  disabled={axxessStep && !(axxessResult?.ok)}
                  title={axxessStep && !(axxessResult?.ok) ? "Configure el WiFi (Axxess) para finalizar" : undefined}
                  data-testid="btn-close-success"
                  className="w-full px-4 py-2.5 rounded-xl text-xs font-bold transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: NEU.accent, color: "#E0E621" }}
                >
                  {axxessStep && !(axxessResult?.ok) ? "Configure el WiFi para finalizar" : "Finalizar"}
                </button>
              </div>
            )}

            {/* v91.1.0 · Modal: equipo ya aprovisionado */}
            {alreadyProvModal && (
              <div data-testid="already-prov-modal" className="rounded-xl p-5 space-y-4" style={{ background: "#FFFBEB", border: "1.5px solid #FDE68A" }}>
                <div className="text-center space-y-2">
                  <div className="text-sm font-bold" style={{ color: "#92400E" }}>
                    La OLT indica que el equipo ya está aprovisionado
                  </div>
                  <div className="text-xs" style={{ color: "#78350F" }}>
                    El equipo con SN <span className="font-mono font-bold">{selectedOnt?.sn_use}</span> ya existe en la OLT
                    ({alreadyProvModal.olt_ip}).
                  </div>
                  <div className="text-xs font-semibold mt-2" style={{ color: "#92400E" }}>
                    ¿El cliente tiene navegación?
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="btn-confirm-existing-yes"
                    onClick={handleConfirmExisting}
                    disabled={provisioning}
                    className="flex-1 px-4 py-3 rounded-xl text-xs font-bold text-white transition"
                    style={{ background: "#059669" }}
                  >
                    {provisioning ? "Confirmando..." : "Sí, tiene navegación"}
                  </button>
                  <button
                    data-testid="btn-confirm-existing-no"
                    onClick={() => { setAlreadyProvModal(null); setStep("configure"); }}
                    className="flex-1 px-4 py-3 rounded-xl text-xs font-bold transition"
                    style={{ background: "#FEF3C7", color: "#92400E", border: "1.5px solid #FDE68A" }}
                  >
                    No, sin navegación
                  </button>
                </div>
              </div>
            )}

            {/* Step: Error */}
            {step === "error" && (
              <div data-testid="provision-error">
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "#FEE2E2" }}>
                    <XCircle size={32} style={{ color: "#DC2626" }} />
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold" style={{ color: "#DC2626" }}>
                      Error al aprovisionar
                    </div>
                    <div className="text-[11px] mt-1 max-w-sm" style={{ color: NEU.textMuted }}>
                      {provisionError || "La OLT rechazó el comando de aprovisionamiento."}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={isTraslado ? handleTrasladoProvision : handleProvision}
                    data-testid="btn-retry-provision"
                    className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition hover:opacity-90"
                    style={{ background: "#DC2626" }}
                  >
                    Reintentar
                  </button>
                  <button
                    onClick={handleBackToList}
                    data-testid="btn-back-from-error"
                    className="px-4 py-2.5 rounded-xl text-xs font-semibold transition"
                    style={{
                      background: NEU.bgSurface,
                      color: NEU.text,
                      border: `1.5px solid ${NEU.border}`,
                    }}
                  >
                    Seleccionar otra ONT
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   FASE 4 · CambioEquipoWizard — Cambio de equipo (flujo=3, en_sitio)
   ═══════════════════════════════════════════════════════════════════════════ */
/* ── v101.135.0 · Cambio de IP guiado para el cambio de equipo ──
   VLANs que cumplen sucursal + tipo de cliente + tipo de plan de la OT y están en la OLT de la caja; IPs libres de la
   VLAN elegida (orden aleatorio, verificadas en vivo). Guarda la IP en la OT antes del cambio de equipo. */
function GuidedIpChangePanel({ odooTaskId, onDone }) {
  const [opts, setOpts] = useState(null);
  const [vlanId, setVlanId] = useState("");
  const [ip, setIp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = async (vid) => {
    setErr(null);
    try {
      const { data } = await api.get(`/v1/provisioning/provision/cambio-equipo/ip-opciones/${odooTaskId}`,
        { params: vid ? { vlan_id: vid } : {} });
      setOpts(data);
    } catch (e) { setErr(e?.response?.data?.detail || "No se pudieron cargar las VLANs."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [odooTaskId]);
  const save = async () => {
    if (!vlanId || !ip) return;
    setBusy(true); setErr(null);
    try {
      await api.post("/v1/provisioning/provision/cambio-equipo/cambiar-ip", { odoo_task_id: odooTaskId, vlan_id: parseInt(vlanId), new_ip: ip });
      if (onDone) onDone();
    } catch (e) { setErr(e?.response?.data?.detail || "No se pudo cambiar la IP."); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-3 rounded-xl space-y-2" style={{ background: "#F5F3FF", border: "1px solid #DDD6FE" }} data-testid="guided-ip-change">
      <div className="text-xs font-bold text-violet-900">Cambiar IP para el cambio de equipo</div>
      <div className="text-[11px] text-violet-800">
        IP actual: <b className="font-mono">{opts?.current?.ip || "—"}</b> ({opts?.current?.vlan_name || "VLAN sin dato"}) ·
        OLT {opts?.olt?.name || "—"}. Elija una VLAN de esa OLT (misma sucursal, tipo de cliente y plan) y una IP libre.
      </div>
      <select value={vlanId} onChange={(e) => { setVlanId(e.target.value); setIp(""); load(e.target.value); }}
        className="w-full border rounded px-2 py-1.5 text-xs" data-testid="guided-ip-vlan">
        <option value="">— VLAN —</option>
        {(opts?.vlans || []).map((v) => <option key={v.id} value={v.id}>{v.name} · {v.free_ips} libres</option>)}
      </select>
      {vlanId && (
        <select value={ip} onChange={(e) => setIp(e.target.value)} className="w-full border rounded px-2 py-1.5 text-xs font-mono" data-testid="guided-ip-ip">
          <option value="">— IP libre —</option>
          {(opts?.ips || []).map((i) => <option key={i.id || i.name} value={i.name}>{i.name}</option>)}
        </select>
      )}
      {(opts && !(opts.vlans || []).length) && <div className="text-[11px] text-rose-700">No hay VLANs de la OLT que cumplan sucursal, tipo de cliente y plan. Pida a NOC que la habilite en Odoo.</div>}
      {err && <div className="text-[11px] text-rose-700">{String(err)}</div>}
      <button onClick={save} disabled={busy || !vlanId || !ip}
        className="px-3 py-1.5 rounded bg-violet-700 text-white text-xs font-bold disabled:opacity-50" data-testid="guided-ip-save">
        {busy ? "Guardando…" : "Guardar IP nueva"}
      </button>
    </div>
  );
}

function CambioEquipoWizard({ odooTaskId, canEdit, internalStatus, onDone, provisioned, clientType, subscriptionClientType, manualOnly = false }) {
  const [open, setOpen] = useState(false);
  const [ceData, setCeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snInput, setSnInput] = useState("");
  // v101.91.0 · Serie GPON del equipo ACTUAL (cuando la de Odoo no sirve a la OLT)
  const [needOldSn, setNeedOldSn] = useState(false);
  const [optOldSn, setOptOldSn] = useState(false);   // v101.103.0 · serie GPON opcional si Odoo y la OT no coinciden
  const [useOther, setUseOther] = useState(false);   // v101.104.0 · usar otro equipo en vez del de la receta
  const [oldSnInput, setOldSnInput] = useState("");
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [inventorySearch, setInventorySearch] = useState("");
  // v101.101.0 · Mismos valores por defecto que la app; se ajustan con el tipo sugerido del producto/modelo
  const [tipoEquipoNew, setTipoEquipoNew] = useState("ONU-WiFi");
  const [tipoEquipoOld, setTipoEquipoOld] = useState("ONU-WiFi");
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [escalating, setEscalating] = useState(false);
  // Axxess step
  const [axxessStep, setAxxessStep] = useState(false);
  const [ssid24, setSsid24] = useState("ALFANET_");
  const [ssid5, setSsid5] = useState("ALFANET_");
  // v101.19.0 · Unificar redes
  const [unifyNetworks, setUnifyNetworks] = useState(false);
  const [axxessLoading, setAxxessLoading] = useState(false);
  const [axxessResult, setAxxessResult] = useState(null);

  const { user } = useAuth();
  // v101.19.0 · Técnico/contratista = solo auto; TODO el resto ve Auto + Manual (sin whitelist)
  const canManualMode = !["technician", "tecnico", "contractor", "contratista"].includes((user?.role || "").toLowerCase());
  const [provMode, setProvMode] = useState(manualOnly ? "manual" : "auto");   // v101.119.0 · corporativo = manual

  // v97.4.0 · La app deja la OT en «en ejecución» (in_progress) al llegar/iniciar: también cuenta como en sitio
  const isEnSitio = ["on_site", "en_sitio", "in_progress", "en_ejecucion"].includes((internalStatus || "").toLowerCase());
  // v97.4.0 · El wizard solo se monta cuando el flujo de la OT es 3; antes el botón dependía de datos
  //           que se cargaban recién al abrir, y por eso nunca aparecía.
  const isCambioEquipo = isEnSitio && (ceData ? ceData.is_cambio_equipo !== false : true);
  const gate = useMaterialsGate(odooTaskId);
  useEffect(() => {
    if (odooTaskId && isEnSitio && !provisioned) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odooTaskId, isEnSitio]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/provisioning/provision/cambio-equipo/inventory/${odooTaskId}`);
      setCeData(data);
      // v101.101.0 · Tipo del equipo actual sugerido y serie GPON pedida ANTES (no después de un error)
      if (data?.current?.tipo_sugerido) setTipoEquipoOld(data.current.tipo_sugerido);
      if (data?.current?.needs_gpon_serial) setNeedOldSn(true);
      setOptOldSn(!!data?.current?.odoo_equipment_mismatch);   // v101.103.0
      // v101.104.0 · Equipo NUEVO ya registrado en la receta (Materiales): se usa directo
      if (data?.recipe_serial) {
        setSnInput((cur) => cur || String(data.recipe_serial).toUpperCase());
        const ri = (data?.recipe_equipment || []).find((x) => x?.serial === data.recipe_serial);
        if (ri?.tipo_sugerido) setTipoEquipoNew(ri.tipo_sugerido);
      }
    } catch { setCeData(null); }
    finally { setLoading(false); }
  };

  const handleOpen = () => {
    setOpen(true);
    setResult(null);
    setError(null);
    setSelectedSerial(null);
    setSnInput("");
    setNeedOldSn(false);   // v101.91.0
    setUseOther(false);    // v101.104.0
    setOldSnInput("");
    setAxxessStep(false);
    setAxxessResult(null);
    fetchData();
    gate.check();   // v97.4.0 · materiales primero
  };

  const handleClose = () => {
    setOpen(false);
    setResult(null);
    setError(null);
  };

  const handleSelectFromInventory = (item) => {
    setSelectedSerial(item);
    setSnInput(item.serial);
    if (item.tipo_sugerido) setTipoEquipoNew(item.tipo_sugerido);   // v101.101.0
  };

  const handleExecute = async () => {
    const sn = snInput.trim();
    if (!sn) { toast.error("Ingrese el serial del equipo nuevo"); return; }

    setExecuting(true);
    setError(null);
    try {
      const endpoint = provMode === "manual" && canManualMode
        ? "/v1/provisioning/provision/cambio-equipo-manual"
        : "/v1/provisioning/provision/cambio-equipo";

      const { data } = await api.post(endpoint, {
        odoo_task_id: odooTaskId,
        sn_fabric_new: sn,
        tipo_equipo_new: tipoEquipoNew,
        tipo_equipo_old: tipoEquipoOld,
        ...(oldSnInput.trim() && !(provMode === "manual" && canManualMode) ? { sn_antiguo: oldSnInput.trim() } : {}),
      });
      setResult(data);
      setNeedOldSn(false);
      toast.success("Cambio de equipo completado");
      if (data.rn8_discrepancy) {
        toast.warning("RN8: Serial no coincide con inventario asignado (registrado en audit).");
      }
      if (data.requires_return) {
        toast.warning("RN13: El equipo retirado NO está facturado — se requiere devolución.");
      }
      if (data.axxess_delete_ticket) {
        toast.warning("Ticket Axxess creado: se requiere /delete del equipo viejo.");
      }
      // Si equipo nuevo es Router/ONU-WiFi → paso Axxess
      if (needsAxxessForClient(tipoEquipoNew, subscriptionClientType, clientType)) setAxxessStep(true);   // v101.83.0
      if (onDone) try { await onDone(); } catch (_) {}
    } catch (err) {
      const detail = err.response?.data?.detail;
      // v101.104.0 · Validación (lista), sin respuesta u otro formato: siempre con la causa y el código HTTP
      const msg = !err.response
        ? `Sin respuesta del servidor (${err.code || "red"}). Revise si el cambio ya se ejecutó antes de reintentar.`
        : Array.isArray(detail)
          ? `Datos no válidos (HTTP ${err.response.status}): ${detail.map((x) => x?.msg).filter(Boolean).join(" · ")}`
          : (typeof detail === "object" && detail ? detail.message : detail) || `Error en cambio de equipo (HTTP ${err.response.status})`;
      // v101.91.0 · La OLT no puede ubicar el equipo actual → pedir su serie GPON
      const raw = err.response?.data?._detail_raw;
      if ((raw && raw.code === "old_ont_not_found") || (detail && detail.code === "old_ont_not_found")) setNeedOldSn(true);
      setError(msg);
      toast.error(msg || "Error en cambio de equipo");
    } finally { setExecuting(false); }
  };

  const handleEscalar = async () => {
    // v101.101.0 · Crea un ticket al NOC: se confirma antes (antes un clic lo creaba sin preguntar)
    if (!window.confirm("¿Hay que cambiar la caja o el puerto del cliente? Se creará un ticket al NOC y el cambio de equipo no se ejecuta.")) return;
    setEscalating(true);
    try {
      await api.post("/v1/provisioning/provision/cambio-equipo/escalar", {
        odoo_task_id: odooTaskId,
        motivo: "Cambio de caja/puerto requerido durante cambio de equipo",
      });
      toast.success("Ticket de escalamiento creado exitosamente");
    } catch (err) {
      toast.error("Error al escalar: " + (err.response?.data?.detail || err.message));
    } finally { setEscalating(false); }
  };

  const handleAxxessConfigure = async () => {
    if (!result?.sn_use_new) return;
    const s24 = ssid24;
    const s5 = unifyNetworks ? ssid24 : ssid5;
    if (!ssidValid(s24) || !ssidValid(s5)) {
      toast.error(unifyNetworks ? "Completa el SSID (ALFANET_…)" : "Completa ambos SSID (ALFANET_…)");
      return;
    }
    setAxxessLoading(true);
    try {
      const { data } = await api.post("/v1/provisioning/axxess/configure", {
        odoo_task_id: odooTaskId,
        router_serial: result.sn_use_new,
        ssid24: s24, ssid5: s5,
      });
      setAxxessResult(data);
      toast.success(unifyNetworks ? "Router configurado (redes unificadas)" : "Router configurado en Axxess");
    } catch (err) {
      toast.error("Error Axxess: " + (err.response?.data?.detail || err.message));
    } finally { setAxxessLoading(false); }
  };

  if (!canEdit || !isEnSitio || (provisioned && !open)) return null;   // v93.5.0

  const inventory = ceData?.inventory || [];
  const filteredInv = inventorySearch.trim()
    ? inventory.filter(i => `${i.serial} ${i.product_name || ""}`.toLowerCase().includes(inventorySearch.toLowerCase()))
    : inventory;
  const currentEquip = ceData?.current || {};
  const isManual = provMode === "manual" && canManualMode;
  const stepNum = (n, t) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "#EA580C" }}>{n}</span>
      <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#9A3412" }}>{t}</span>
    </div>
  );
  const TIPOS_CE = [["ONU", "ONU"], ["ONU-WiFi", "ONU con WiFi"], ["ONU+Router", "ONU + Router"]];

  return (
    <div data-testid="cambio-equipo-wizard">
      {/* Botón principal */}
      {!open && isCambioEquipo && (
        <button
          onClick={handleOpen}
          data-testid="btn-open-cambio-equipo"
          className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-xs font-bold transition-all hover:shadow-md"
          style={{ background: "#FFF7ED", border: "1.5px solid #FDBA74", color: "#9A3412" }}
        >
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#EA580C20" }}>
            <RefreshCw size={14} style={{ color: "#EA580C" }} />
          </div>
          <div className="flex-1 text-left">
            <div className="font-bold">Cambio de Equipo</div>
            <div className="text-[10px] font-normal" style={{ color: "#C2410C" }}>
              Equipo actual: {currentEquip.serial_label || currentEquip.sn_use || "—"}
            </div>
          </div>
          <PlayCircle size={18} style={{ color: "#EA580C" }} />
        </button>
      )}

      {open && (
        <div className="rounded-2xl overflow-hidden" style={{ background: NEU.bgLight, border: `1.5px solid #FDBA74`, boxShadow: NEU.shadowUp }}
          data-testid="cambio-equipo-panel">
          <div className="px-5 py-3 flex items-center justify-between" style={{ background: "linear-gradient(135deg, #EA580C 0%, #C2410C 100%)" }}>
            <div className="flex items-center gap-2">
              <RefreshCw size={14} style={{ color: "#fff" }} />
              <span className="text-xs font-bold text-white">CAMBIO DE EQUIPO</span>
            </div>
            <button onClick={handleClose} className="text-white/80 hover:text-white"><XCircle size={16} /></button>
          </div>

          <div className="px-5 py-4 space-y-4">
            {gate.blocked && <MaterialsBlockedBanner odooTaskId={odooTaskId} onReady={(d) => gate.setMc(d)} />}

            {/* 1 · Equipo actual (se retira) */}
            <div>
              {stepNum(1, "Equipo actual del cliente (se retira)")}
              <div className="rounded-xl p-3" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
                <div className="text-sm font-mono font-bold" style={{ color: "#7F1D1D" }}
                  title="Serie que la OLT usará para ubicar el equipo actual">{currentEquip.serial_label || currentEquip.sn_use || "—"}</div>
                {currentEquip.model && <div className="text-[11px]" style={{ color: "#991B1B" }}>{currentEquip.model}</div>}
                {currentEquip.odoo_equipment && (
                  <div className="text-[11px] mt-0.5" style={{ color: "#6B7280" }}>Equipo registrado en Odoo: <b className="font-mono">{currentEquip.odoo_equipment}</b></div>
                )}
                {currentEquip.odoo_equipment_mismatch && (
                  <div className="mt-1.5 px-2 py-1.5 rounded-lg text-[11px]" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }}
                       data-testid="cambio-eq-mismatch">
                    No coinciden: la OT tiene la serie {currentEquip.serial_label} y Odoo registra el equipo {currentEquip.odoo_equipment}. Revise la etiqueta
                    del equipo instalado; si es el de Odoo, escriba su serie GPON abajo para que la OLT lo encuentre.
                  </div>
                )}
                <div className="text-[10px] mt-1.5" style={{ color: "#6B7280" }}>
                  Se conservan: OLT {currentEquip.olt_name || currentEquip.olt_ip || "—"} · Caja {currentEquip.distribution_box || "—"} ·
                  Puerto {currentEquip.port_name || "—"} · IP {currentEquip.client_ip || "—"}
                </div>
              </div>
              <div className="mt-2">
                <ClientEquipmentCard odooTaskId={odooTaskId} hint="Opcional: consultar en Alfanet los equipos que el cliente tiene registrados." />
              </div>
              {!result && (needOldSn || optOldSn) && !isManual && (
                <div className="mt-2 rounded-xl p-3 space-y-1.5" style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA" }}
                     data-testid="cambio-old-sn-box">
                  <div className="text-xs font-bold" style={{ color: "#9A3412" }}>
                    {needOldSn ? "Escriba la serie GPON del equipo actual" : "Serie GPON del equipo actual (opcional)"}
                  </div>
                  <div className="text-[11px]" style={{ color: "#9A3412" }}>
                    {needOldSn ? "La registrada no sirve para ubicarlo en la OLT. " : "Solo si el equipo instalado NO es el de la serie de arriba. "}
                    Está en la etiqueta del equipo instalado («GPON SN»), ej. HWTC-1234ABCD.
                  </div>
                  <input value={oldSnInput} onChange={(e) => setOldSnInput(e.target.value.toUpperCase())} placeholder="HWTC-1234ABCD"
                    className="w-full px-3 py-2 rounded-lg text-sm font-mono border" data-testid="cambio-old-sn-input" />
                </div>
              )}
            </div>

            {!result && (
              <>
                {/* 2 · Equipo nuevo */}
                <div>
                  {stepNum(2, "Equipo nuevo")}
                  {(ceData?.recipe_equipment || []).length > 0 && !useOther ? (
                    <div data-testid="cambio-recipe-eq">
                      <div className="text-[10px] mb-1" style={{ color: NEU.textMuted }}>
                        Equipo registrado en la receta (Materiales){(ceData.recipe_equipment.length > 1) ? " · elija el que se instala" : ""}
                      </div>
                      <div className="rounded-xl border" style={{ borderColor: NEU.border }}>
                        {ceData.recipe_equipment.map((item) => (
                          <button key={item.serial}
                            onClick={() => { setSnInput(String(item.serial).toUpperCase()); setSelectedSerial(null); if (item.tipo_sugerido) setTipoEquipoNew(item.tipo_sugerido); }}
                            className="w-full text-left px-3 py-2 text-xs border-b last:border-b-0 flex items-center justify-between"
                            style={{ borderColor: NEU.border, background: snInput === String(item.serial).toUpperCase() ? "#F0FDF4" : "transparent" }}>
                            <div className="min-w-0">
                              <div className="font-mono font-bold" style={{ color: NEU.text }}>{item.serial}</div>
                              <div className="text-[10px] truncate" style={{ color: NEU.textMuted }}>{item.product_name}</div>
                              {item.same_as_current && (
                                <div className="text-[10px]" style={{ color: "#92400E" }}>Misma serie que el equipo actual de la OT: revise cuál se retira.</div>
                              )}
                            </div>
                            {snInput === String(item.serial).toUpperCase() && <CheckCircle2 size={14} style={{ color: "#059669" }} />}
                          </button>
                        ))}
                      </div>
                      <button onClick={() => { setUseOther(true); setSnInput(""); }} className="mt-2 text-[10px] font-semibold underline"
                        style={{ color: "#0369A1" }} data-testid="cambio-use-other">Usar otro equipo (no el de la receta)</button>
                    </div>
                  ) : loading ? (
                    <div className="flex items-center gap-2 py-2"><Loader2 size={14} className="animate-spin" style={{ color: NEU.accent }} />
                      <span className="text-xs" style={{ color: NEU.textMuted }}>Cargando bodega…</span></div>
                  ) : inventory.length > 0 ? (
                    <>
                      <div className="text-[10px] mb-1" style={{ color: NEU.textMuted }}>
                        Equipos en la bodega de {ceData?.technician || "el técnico"} ({inventory.length}) · toque uno para usarlo
                      </div>
                      <input type="text" placeholder="Buscar serie o modelo…" value={inventorySearch} onChange={e => setInventorySearch(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl text-xs border mb-2 outline-none" style={{ borderColor: NEU.border }}
                        data-testid="input-inventory-search" />
                      <div className="max-h-[180px] overflow-y-auto rounded-xl border" style={{ borderColor: NEU.border }}>
                        {filteredInv.slice(0, 50).map((item, idx) => (
                          <button key={idx} onClick={() => handleSelectFromInventory(item)}
                            className="w-full text-left px-3 py-2 text-xs border-b last:border-b-0 transition hover:bg-orange-50 flex items-center justify-between"
                            style={{ borderColor: NEU.border, background: selectedSerial?.serial === item.serial ? "#FFF7ED" : "transparent" }}
                            data-testid={`inv-item-${idx}`}>
                            <div className="min-w-0">
                              <div className="font-mono font-bold" style={{ color: NEU.text }}>{item.serial}</div>
                              <div className="text-[10px] truncate" style={{ color: NEU.textMuted }}>{item.product_name}</div>
                            </div>
                            {selectedSerial?.serial === item.serial && <CheckCircle2 size={14} style={{ color: "#059669" }} />}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-[11px] mb-1" style={{ color: "#B45309" }}>
                      La bodega de {ceData?.technician || "el técnico"} no tiene equipos con serie. Escriba la serie a mano.
                    </div>
                  )}
                  {((ceData?.recipe_equipment || []).length === 0 || useOther) && <input type="text" value={snInput}
                    onChange={e => { setSnInput(e.target.value.toUpperCase()); setSelectedSerial(null); }}
                    placeholder="O escriba la serie del equipo nuevo (ej. HWTC-746A585A)"
                    className="mt-2 w-full px-3 py-2.5 rounded-xl text-xs font-mono border outline-none"
                    style={{ borderColor: selectedSerial ? "#059669" : NEU.border, background: selectedSerial ? "#F0FDF4" : "#fff" }}
                    data-testid="input-sn-fabric-new" />}
                </div>

                {/* 3 · Tipos */}
                <div>
                  {stepNum(3, "Tipo de equipo")}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: NEU.textLabel }}>Nuevo</label>
                      <select value={tipoEquipoNew} onChange={e => setTipoEquipoNew(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl text-xs border" style={{ borderColor: NEU.border }} data-testid="select-tipo-equipo-new">
                        {TIPOS_CE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: NEU.textLabel }}>El que se retira</label>
                      <select value={tipoEquipoOld} onChange={e => setTipoEquipoOld(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl text-xs border" style={{ borderColor: NEU.border }} data-testid="select-tipo-equipo-old">
                        {TIPOS_CE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                  {canManualMode && (
                    <div className="flex gap-2 mt-3">
                      {(manualOnly ? ["manual"] : ["auto", "manual"]).map(m => (   // v101.119.0
                        <button key={m} onClick={() => setProvMode(m)}
                          className="flex-1 px-3 py-2 rounded-xl text-[10px] font-bold uppercase transition-all"
                          style={{ background: provMode === m ? "#EA580C" : NEU.bgSurface, color: provMode === m ? "#fff" : NEU.textMuted,
                                   border: `1.5px solid ${provMode === m ? "#EA580C" : NEU.border}` }}
                          data-testid={`btn-ce-mode-${m}`}>
                          {m === "auto" ? "Automático (OLT)" : "Manual (sin OLT)"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {error && (
                  <div className="p-3 rounded-xl" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
                    <div className="text-xs font-bold" style={{ color: "#DC2626" }}>No se pudo cambiar el equipo</div>
                    <div className="text-[11px] mt-1" style={{ color: "#991B1B" }}>{error}</div>
                  </div>
                )}
                {/* v101.135.0 · Cambio de IP guiado: la IP actual no es de una VLAN de la OLT */}
                {error && /Cambiar IP para el cambio de equipo/i.test(String(error)) && canManualMode && (
                  <GuidedIpChangePanel odooTaskId={odooTaskId}
                    onDone={() => { setError(null); toast.success("IP cambiada. Vuelva a ejecutar el cambio de equipo."); }} />
                )}

                {/* 4 · Ejecutar */}
                <div>
                  {stepNum(4, "Cambiar")}
                  <button onClick={handleExecute}
                    disabled={executing || !snInput.trim() || gate.blocked || (needOldSn && !isManual && !oldSnInput.trim())}
                    data-testid="btn-execute-cambio-equipo"
                    className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-xl font-bold text-sm text-white transition-all disabled:opacity-40"
                    style={{ background: executing ? NEU.textMuted : "linear-gradient(135deg, #EA580C 0%, #C2410C 100%)" }}>
                    {executing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                    {executing ? "Cambiando equipo…" : `Retirar ${currentEquip.serial_label || "equipo actual"} e instalar ${snInput.trim() || "…"}`}
                  </button>
                  {!snInput.trim() && <div className="text-[10px] mt-1 text-center" style={{ color: NEU.textMuted }}>Elija o escriba la serie del equipo nuevo.</div>}
                  <button onClick={handleEscalar} disabled={escalating} data-testid="btn-escalar-cambio"
                    className="w-full mt-3 text-[10px] font-semibold underline disabled:opacity-40" style={{ color: "#92400E" }}>
                    {escalating ? "Escalando…" : "¿Hay que cambiar caja o puerto? Escalar al NOC"}
                  </button>
                </div>
              </>
            )}

            {/* Resultado */}
            {result && (
              <div className="p-3 rounded-xl space-y-2" style={{ background: "#ECFDF5", border: "1.5px solid #A7F3D0" }}>
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={14} style={{ color: "#059669" }} />
                  <span className="text-xs font-bold" style={{ color: "#065F46" }}>Cambio de equipo realizado</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <div><span style={{ color: "#6B7280" }}>Retirado:</span> <b className="font-mono">{snLabelCe(result.sn_fabric_old || result.sn_use_old)}</b></div>
                  <div><span style={{ color: "#6B7280" }}>Instalado:</span> <b className="font-mono">{snLabelCe(result.sn_fabric_new || result.sn_use_new)}</b></div>
                </div>
                {result.rn8_discrepancy && (
                  <div className="px-2 py-1 rounded text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }}>
                    La serie nueva no está en la bodega del técnico (quedó registrado para revisión).
                  </div>
                )}
                {result.axxess_delete_ticket && (
                  <div className="px-2 py-1 rounded text-[10px]" style={{ background: "#EFF6FF", color: "#1E40AF" }}>
                    Se creó un ticket para dar de baja en Axxess el equipo anterior.
                  </div>
                )}
                <div className="text-[10px]" style={{ color: "#065F46" }}>
                  Siguiente: {axxessStep && !axxessResult ? "configurar el WiFi, " : ""}{result.requires_return ? "registrar la devolución del equipo retirado, " : ""}y finalizar la OT.
                </div>
              </div>
            )}

            {/* 5 · WiFi del equipo nuevo */}
            {axxessStep && !axxessResult && (
              <div className="p-3 rounded-xl space-y-3" style={{ background: "#F0F9FF", border: "1.5px solid #BAE6FD" }}>
                {stepNum(5, `Configurar WiFi del equipo nuevo`)}
                <UnifyNetworksToggle checked={unifyNetworks} testid="unify-networks-ce"
                  onChange={(v) => { setUnifyNetworks(v); if (v) setSsid5(ssid24); }} />
                <div className={unifyNetworks ? "grid grid-cols-1 gap-2" : "grid grid-cols-2 gap-2"}>
                  <div>
                    <label className="text-[9px] font-bold block mb-0.5" style={{ color: NEU.textLabel }}>{unifyNetworks ? "SSID (2.4 y 5 GHz)" : "SSID 2.4 GHz"}</label>
                    <input value={ssid24} onChange={e => { setSsid24(e.target.value); if (unifyNetworks) setSsid5(e.target.value); }}
                      className="w-full px-2 py-1.5 rounded-lg text-xs border" style={{ borderColor: NEU.border }} data-testid="input-ce-ssid24" />
                  </div>
                  {!unifyNetworks && (
                    <div>
                      <label className="text-[9px] font-bold block mb-0.5" style={{ color: NEU.textLabel }}>SSID 5 GHz</label>
                      <input value={ssid5} onChange={e => setSsid5(e.target.value)}
                        className="w-full px-2 py-1.5 rounded-lg text-xs border" style={{ borderColor: NEU.border }} data-testid="input-ce-ssid5" />
                    </div>
                  )}
                </div>
                <button onClick={handleAxxessConfigure} disabled={axxessLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40"
                  style={{ background: "#1D4ED8" }} data-testid="btn-ce-axxess-configure">
                  {axxessLoading ? <Loader2 size={14} className="animate-spin" /> : <Wifi size={14} />}
                  {axxessLoading ? "Configurando..." : "Configurar WiFi"}
                </button>
              </div>
            )}
            {axxessResult && (
              <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}>
                <CheckCircle2 size={14} style={{ color: "#059669" }} />
                <span className="text-xs font-bold" style={{ color: "#065F46" }}>WiFi configurado</span>
              </div>
            )}

            {/* 6 · Devolución (solo si el retirado no está facturado) */}
            {result && result.requires_return && (
              <EquipmentReturnsCard odooTaskId={odooTaskId} canEdit
                prefillSerial={snLabelCe(result.sn_fabric_old || result.sn_use_old || currentEquip.sn_use)}
                title="Devolución del equipo retirado"
                defaultOpen />
            )}
            {result && !result.requires_return && (
              <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: "#ECFDF5", border: "1px solid #A7F3D0" }}
                data-testid="cambio-return-skip">
                <CheckCircle2 size={14} style={{ color: "#059669" }} />
                <span className="text-xs font-bold" style={{ color: "#065F46" }}>El equipo retirado no requiere devolución</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** v101.101.0 · Serie legible: '485754439CCB1BB5' → 'HWTC-9CCB1BB5' (como en la etiqueta). */
function snLabelCe(sn) {
  const v = String(sn || "").replace(/\s/g, "").toUpperCase();
  if (/^[0-9A-F]{16}$/.test(v)) {
    try {
      const pre = v.slice(0, 8).match(/../g).map((h) => String.fromCharCode(parseInt(h, 16))).join("");
      if (/^[A-Z0-9]{4}$/.test(pre)) return `${pre}-${v.slice(8)}`;
    } catch (_) { /* noop */ }
  }
  return String(sn || "").trim();
}


/* ═══════════════════════════════════════════════════════════════════════════
   FASE 2 · ProvisioningFase2Actions — Tickets, Cancelar, Cambiar IP
   ═══════════════════════════════════════════════════════════════════════════ */
// v101.57.0 · WiFi (bloque axxess) pendiente en una instalación ya aprovisionada: Odoo no acepta el cierre sin él.
// v101.76.0 · Paso WiFi abierto en el asistente de aprovisionamiento (por OT): la tarjeta pendiente no se duplica
const _axxessWizardOpen = new Set();
function setAxxessWizardOpen(taskId, open) {
  const k = String(taskId || "");
  if (!k) return;
  const had = _axxessWizardOpen.has(k);
  if (open) _axxessWizardOpen.add(k); else _axxessWizardOpen.delete(k);
  if (had !== !!open) {
    try { window.dispatchEvent(new CustomEvent("afc-axxess-wizard", { detail: { taskId: k, open: !!open } })); } catch (_) { /* noop */ }
  }
}

function AxxessPendingCard({ odooTaskId, ot, canEdit, onRefresh }) {
  const { user } = useAuth();
  const [wizardOpen, setWizardOpen] = useState(_axxessWizardOpen.has(String(odooTaskId || "")));
  useEffect(() => {
    const k = String(odooTaskId || "");
    const h = () => setWizardOpen(_axxessWizardOpen.has(k));
    h();
    window.addEventListener("afc-axxess-wizard", h);
    return () => window.removeEventListener("afc-axxess-wizard", h);
  }, [odooTaskId]);
  const canManual = !["technician", "tecnico", "contractor", "contratista"].includes((user?.role || "").toLowerCase());
  const [unify, setUnify] = useState(true);
  const [s24, setS24] = useState("ALFANET_");
  const [s5, setS5] = useState("ALFANET_");
  const [router, setRouter] = useState(ot?.pe_cpe_serial || ot?.network_infrastructure?.cpe_serial || "");
  const [busy, setBusy] = useState(false);
  const code = String(ot?.activity_type_code || "").toLowerCase();
  const done = !!(ot?.provisioning_data?.cpe_supply_completed || ot?.has_successful_provision);
  const hasSsid = !!(ot?.pe_axxess_ssid24 || ot?.pe_axxess_ssid5);
  // v101.83.0 · solo clientes residenciales (corporativo no lleva WiFi Axxess)
  const isCorporate = resolveVlanClientType(ot?.client_type, ot?.segment_name) === "corporate";
  // v101.135.0 · A quién aplica lo decide el backend (tipo de cliente + plan + interruptores); sin dato, regla anterior
  const wifiRequired = (typeof ot?.wifi_required === "boolean") ? ot.wifi_required
    : (!isCorporate && ["installation_fiber", "subscription_transfer"].includes(code));
  if (wizardOpen || !done || hasSsid || !wifiRequired) return null;
  const v24 = s24, v5 = unify ? s24 : s5;
  const ok = ssidValid(v24) && ssidValid(v5);
  const run = async (manual) => {
    if (!ok) { toast.error("Completa el sufijo del SSID (ALFANET_…)"); return; }
    setBusy(true);
    try {
      if (manual) {
        await api.post("/v1/provisioning/axxess/register-manual", { odoo_task_id: odooTaskId, ssid24: v24, ssid5: v5 });
        toast.success("SSID registrado (sin Axxess)");
      } else {
        await api.post("/v1/provisioning/axxess/configure", { odoo_task_id: odooTaskId, router_serial: router, ssid24: v24, ssid5: v5 }, { timeout: 90000 });
        toast.success("WiFi configurado en Axxess");
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo configurar el WiFi", { duration: 10000 });
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl p-4 space-y-3" style={{ background: "#FFF7ED", border: "1.5px solid #FED7AA" }} data-testid="axxess-pending-card">
      <div className="text-xs font-bold" style={{ color: "#9A3412" }}>
        WiFi pendiente · Odoo exige el bloque axxess para cerrar esta {code === "subscription_transfer" ? "OT de traslado" : "instalación"}
      </div>
      {canEdit ? (
        <>
          <UnifyNetworksToggle checked={unify} testid="unify-networks-pending" onChange={(v) => { setUnify(v); if (v) setS5(s24); }} />
          <SsidSuffixInput label={unify ? "SSID (2.4 y 5 GHz)" : "SSID 2.4 GHz"} value={s24} onChange={(v) => { setS24(v); if (unify) setS5(v); }} testid="ssid24-pending" />
          {!unify && <SsidSuffixInput label="SSID 5 GHz" value={s5} onChange={setS5} testid="ssid5-pending" />}
          <label className="block text-[11px]" style={{ color: "#9A3412" }}>Serie del router / ONU-WiFi
            <input value={router} onChange={(e) => setRouter(e.target.value)} data-testid="router-serial-pending"
              className="mt-1 w-full px-2 py-1.5 rounded-lg text-xs font-mono" style={{ border: "1.5px solid #FED7AA" }} />
          </label>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => run(false)} disabled={busy || !router} data-testid="btn-axxess-pending"
              className="flex-1 px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: busy ? "#FDBA74" : "#EA580C" }}>
              {busy ? "Procesando..." : "Configurar WiFi (Axxess)"}
            </button>
            {canManual && (
              <button onClick={() => run(true)} disabled={busy} data-testid="btn-axxess-pending-manual"
                className="px-4 py-2 rounded-xl text-xs font-semibold" style={{ background: "#FFFFFF", color: "#9A3412", border: "1.5px solid #FDBA74" }}>
                Registrar SSID sin Axxess
              </button>
            )}
          </div>
        </>
      ) : (
        <div className="text-[11px]" style={{ color: "#9A3412" }}>Pida a coordinación que configure el WiFi.</div>
      )}
    </div>
  );
}

function ProvisioningFase2Actions({ odooTaskId, ot, canEdit, onRefresh }) {
  const { user } = useAuth();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // v101.87.0 · «Desaprovisionar sin OLT» cuando la OLT falla al eliminar la ONT
  const [skipOlt, setSkipOlt] = useState(null);   // { detail } | null
  const [skipOltReason, setSkipOltReason] = useState("");
  const [skipOltConfirm, setSkipOltConfirm] = useState(false);
  // v101.17.0 · Cancelar aprovisionamiento: siempre desactivado hasta aprovisionar (todos los flujos)
  // (antes v100.2.0 solo bloqueaba flujo 3 / Cambio Equipo)
  const cpeSupplyDone = !!(ot?.provisioning_data?.cpe_supply_completed || ot?.has_successful_provision);
  const cancelBlocked = !cpeSupplyDone;
  const [showChangeIp, setShowChangeIp] = useState(false);
  const [freeIps, setFreeIps] = useState([]);
  const [freeIpsLoading, setFreeIpsLoading] = useState(false);
  const [selectedNewIp, setSelectedNewIp] = useState(null);
  const [changingIp, setChangingIp] = useState(false);
  const [ipSearch, setIpSearch] = useState("");

  const smart = ot?.smart || {};
  const isProvisioned = smart.internal_status === "provisioned" ||
    smart.internal_status === "on_site" ||
    smart.internal_status === "en_sitio";
  const hasProvision = ot?.network_infrastructure?.cpe_serial || ot?.provisioning_data?.cpe_supply_completed || ot?.has_successful_provision;
  // v95.1.2 · super_admin puede forzar cancelación aunque no haya aprovisionamiento completado
  const isSuperAdmin = user?.role === "super_admin";

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/provisioning/${odooTaskId}/status`);
      setStatusData(data);
    } catch { setStatusData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, [odooTaskId]); // eslint-disable-line

  const tickets = statusData?.tickets || [];
  const openTickets = tickets.filter(t => t.status === "abierto");
  // v101.89.0 · Botón visible «Desaprovisionar sin OLT» si hubo un delete-ont fallido (ticket abierto)
  const hasDeleteOntFail = openTickets.some(t => t.causa === "cancel_delete_ont_fail");

  // v101.50.3 · Si el último aprovisionamiento fue manual, cancelar no llama delete-ont
  const _MANUAL_PROV_EPS = ["manual", "traslado-manual", "cambio-equipo-manual", "radio_manual"];
  const _AUTO_OLT_EPS = ["set-ont", "traslado-set-ont"];
  const lastProvEp = (() => {
    const entries = statusData?.audit_entries || statusData?.timeline || [];
    for (const e of entries) {
      const ep = String(e.endpoint || e.action || "");
      if (_MANUAL_PROV_EPS.includes(ep) || _AUTO_OLT_EPS.includes(ep)) {
        if (e.success === false || e.status === "error") continue;
        return ep;
      }
    }
    return "";
  })();
  const wasManualProvision = _MANUAL_PROV_EPS.includes(lastProvEp)
    || (!lastProvEp && cpeSupplyDone); // sin set-ont en auditoría → tratar como manual

  // ── Cancelar aprovisionamiento ──
  const handleCancel = async () => {
    const msg = wasManualProvision
      ? "¿Cancelar el aprovisionamiento MANUAL?\n\n" +
        "No se llamará a la OLT ni a Axxess.\n" +
        "Solo se liberará la IP (si aplica) y se revertirá el estado en AlfaField.\n" +
        "El desaprovisionamiento en red queda a cargo del operador."
      : "¿Está seguro de cancelar el aprovisionamiento?\n\n" +
        "Esto intentará:\n" +
        "• Eliminar la ONT de la OLT\n" +
        "• Eliminar el router de Axxess\n" +
        "• Liberar la IP del cliente\n" +
        "• Revertir el estado de la OT";
    if (!window.confirm(msg)) return;
    await doCancel({});
  };

  const _FIELD_ROLES = ["technician", "tecnico", "contractor", "contratista"];
  const canSkipOlt = !_FIELD_ROLES.includes(String(user?.role || "").toLowerCase());

  const doCancel = async (extra) => {
    setCancelling(true);
    try {
      const { data } = await api.post("/v1/provisioning/provision/cancel", {
        odoo_task_id: odooTaskId,
        ...(isSuperAdmin ? { force: true } : {}),
        ...extra,
      });
      setSkipOlt(null);
      if (data?.skip_olt) {
        toast.warning("Desaprovisionado SIN eliminar la ONT en la OLT. Se abrió un ticket para que NOC confirme que la "
          + `ONT ya no está en la OLT${data.ip_freed ? ` · IP ${data.ip_freed} liberada` : ""}.`, { duration: 12000 });
      } else toast.success(
        data?.manual_cancel || data?.olt_delete_skipped
          ? `Aprovisionamiento manual cancelado en AlfaField${data.ip_freed ? ` · IP ${data.ip_freed} liberada` : ""} (sin API OLT)`
          : `Aprovisionamiento cancelado${data.ip_freed ? ` · IP ${data.ip_freed} liberada` : ""}`,
      );
      // v101.63.0 · Traslado / cambio de equipo: CPE y red restaurados desde Odoo (o aviso si no se pudo)
      if (data.message) (data.odoo_cpe_restored ? toast.info : toast.warning)(data.message, { duration: 9000 });
      fetchStatus();
      if (onRefresh) onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const txt = typeof detail === "string" ? detail : detail?.message || "Error al cancelar";
      // v101.87.0 · la OLT no pudo eliminar la ONT → ofrecer «Desaprovisionar sin OLT» (motivo + confirmación)
      // v101.89.0 · No depender del código HTTP (un proxy puede cambiar el 502): basta el texto o el código 409
      if (/Desaprovisionar sin OLT/i.test(txt) && canSkipOlt && !extra?.skip_olt) {
        setSkipOltReason(""); setSkipOltConfirm(false); setSkipOlt({ detail: txt });
      } else toast.error(txt, { duration: 10000 });
    } finally { setCancelling(false); }
  };

  // ── Cambiar IP: v92.9.0 · solo IPs libres de la MISMA VLAN de la OT, selección manual ──
  const handleOpenChangeIp = async (q = "") => {
    setShowChangeIp(true);
    setSelectedNewIp(null);
    if (!q) setIpSearch("");
    setFreeIpsLoading(true);
    try {
      // v93.9.0 · Máximo 100 IPs; la búsqueda consulta al servidor
      const { data } = await api.get(`/v1/provisioning/provision/change-ip/options/${odooTaskId}`, { params: { limit: 100, q } });
      setFreeIps((data.items || []).map(ip => ({ ...ip, vlan_name: data.vlan_name, vlan_id: data.vlan_id })));
      if (!(data.items || []).length) toast.info(`No hay IPs libres en ${data.vlan_name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron cargar las IPs disponibles");
      setFreeIps([]);
    } finally { setFreeIpsLoading(false); }
  };

  // v93.9.0 · Buscar IP en el servidor al escribir (con pausa)
  useEffect(() => {
    if (!showChangeIp) return;
    const t = setTimeout(() => { if (ipSearch.trim().length >= 3) handleOpenChangeIp(ipSearch.trim()); }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ipSearch]);

  // ── v92.9.0 · Envío manual a Odoo (pruebas controladas) ──
  const [odooSending, setOdooSending] = useState("");
  const [odooProvSent, setOdooProvSent] = useState(false);
  const [odooCloseSent, setOdooCloseSent] = useState(false);

  // v93.7.1 · Si ya fue enviado previamente, marcar como "Enviado" al cargar
  useEffect(() => {
    if (ot?.odoo_ws_event_status === "sync_ok") setOdooProvSent(true);
    if (ot?.odoo_sync_status === "sincronizada") setOdooCloseSent(true);
  }, [ot?.odoo_ws_event_status, ot?.odoo_sync_status]);

  const sendToOdoo = async (kind) => {
    setOdooSending(kind);
    try {
      const url = kind === "prov"
        ? `/v1/provisioning/odoo-sync/retry/${odooTaskId}`
        : `/v1/provisioning/admin/retry-sync/${odooTaskId}`;
      const { data } = await api.post(url);
      if (data?.ok === false) toast.error(data?.odoo_message || data?.error || "Odoo no aceptó el envío");
      else {
        toast.success(kind === "prov" ? "Aprovisionamiento enviado a Odoo" : "Cierre enviado a Odoo");
        if (kind === "prov") setOdooProvSent(true);
        else setOdooCloseSent(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar a Odoo");
    } finally { setOdooSending(""); }
  };

  const handleChangeIp = async () => {
    if (!selectedNewIp) return;
    setChangingIp(true);
    try {
      const { data } = await api.post("/v1/provisioning/provision/change-ip", {
        odoo_task_id: odooTaskId,
        new_ip: selectedNewIp.name,
      });
      toast.success(`IP cambiada: ${data.old_ip || "(ninguna)"} → ${data.new_ip}`);
      setShowChangeIp(false);
      fetchStatus();
      if (onRefresh) onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al cambiar IP");
    } finally { setChangingIp(false); }
  };

  const filteredIps = ipSearch.trim()
    ? freeIps.filter(ip => ip.name.includes(ipSearch) || (ip.vlan_name || "").toLowerCase().includes(ipSearch.toLowerCase()))
    : freeIps;

  const currentIp = statusData?.pe_client_ip || ot?.network_infrastructure?.client_ip || "";

  return (
    <div className="space-y-4" data-testid="fase2-actions">
      <AxxessPendingCard odooTaskId={odooTaskId} ot={ot} canEdit={canEdit} onRefresh={onRefresh} />
      {/* v101.87.0 · Desaprovisionar sin OLT (la OLT falló al eliminar la ONT) */}
      {skipOlt && !cancelling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="skip-olt-modal">
          <div className="bg-white rounded-2xl p-5 shadow-2xl w-full max-w-md mx-4 space-y-3">
            <div className="text-sm font-black" style={{ color: "#B91C1C" }}>La OLT no pudo eliminar la ONT</div>
            <div className="text-[12px] text-gray-700 whitespace-pre-line">{skipOlt.detail}</div>
            <div className="text-[12px] rounded-lg p-2" style={{ background: "#FEF3C7", color: "#92400E" }}>
              Use esta opción solo si la ONT YA fue retirada de la OLT por otra vía (NOC, consola de la OLT, etc.).
              AlfaField no volverá a llamar a la OLT: liberará la IP / puerto, revertirá la OT y abrirá un ticket
              para que NOC confirme.
            </div>
            <label className="block text-[12px] font-bold text-gray-700">Motivo (obligatorio)</label>
            <textarea
              className="w-full border rounded-lg p-2 text-sm" rows={3} maxLength={300}
              value={skipOltReason} onChange={(e) => setSkipOltReason(e.target.value)}
              placeholder="Ej.: ONT ya retirada por NOC desde la consola de la OLT"
              data-testid="skip-olt-reason"
            />
            <label className="flex items-start gap-2 text-[12px] text-gray-700">
              <input type="checkbox" checked={skipOltConfirm} onChange={(e) => setSkipOltConfirm(e.target.checked)}
                data-testid="skip-olt-confirm" />
              Confirmo que la ONT ya no está en la OLT.
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button className="px-3 py-1.5 rounded-lg text-sm border" onClick={() => setSkipOlt(null)}>Volver</button>
              <button
                className="px-3 py-1.5 rounded-lg text-sm font-bold text-white disabled:opacity-40"
                style={{ background: "#DC2626" }}
                disabled={skipOltReason.trim().length < 10 || !skipOltConfirm}
                onClick={() => doCancel({ skip_olt: true, skip_olt_reason: skipOltReason.trim() })}
                data-testid="skip-olt-submit"
              >Desaprovisionar sin OLT</button>
            </div>
          </div>
        </div>
      )}
      {/* v95.0.0 · Animación mientras se desaprovisiona */}
      {cancelling && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40" data-testid="deprovisioning-overlay">
          <div className="bg-white rounded-2xl px-8 py-6 flex flex-col items-center gap-3 shadow-2xl max-w-sm text-center">
            <Loader2 size={36} className="animate-spin" style={{ color: "#DC2626" }} />
            <div className="text-sm font-black" style={{ color: "#0A3747" }}>
              {wasManualProvision ? "Cancelando aprovisionamiento manual…" : "Desaprovisionando…"}
            </div>
            <div className="text-[11px] text-gray-500">
              {wasManualProvision
                ? "Limpiando datos en AlfaField (sin llamar a la OLT). No cierres esta ventana."
                : "Eliminando la ONT de la OLT y liberando la IP del cliente. No cierres esta ventana."}
            </div>
          </div>
        </div>
      )}
      {/* ── Tickets abiertos ── */}
      {openTickets.length > 0 && (
        <div
          className="rounded-2xl overflow-hidden"
          style={{ background: "#FFFBEB", border: "1.5px solid #FDE68A", boxShadow: NEU.shadowUp }}
          data-testid="provisioning-tickets-card"
        >
          <div className="flex items-center gap-2 px-5 py-3" style={{ borderBottom: "1px solid #FDE68A" }}>
            <AlertTriangle size={16} style={{ color: "#D97706" }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#92400E" }}>
              Tickets de Aprovisionamiento ({openTickets.length})
            </span>
          </div>
          <div className="px-5 py-3 space-y-2">
            {openTickets.map((tkt) => (
              <div
                key={tkt.id}
                className="flex items-start gap-3 p-3 rounded-xl"
                style={{ background: "#FEF3C7", border: "1px solid #FDE68A" }}
                data-testid={`ticket-${tkt.id}`}
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#F59E0B20" }}>
                  <AlertTriangle size={14} style={{ color: "#D97706" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold" style={{ color: "#92400E" }}>
                    {tkt.causa?.replace(/_/g, " ").toUpperCase()}
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: "#78350F" }}>
                    {tkt.detalle}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[9px]" style={{ color: "#A16207" }}>
                    <span>Creado: {tkt.created_at ? new Date(tkt.created_at).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" }) : "—"}</span>
                    {tkt.created_by && <span>por {tkt.created_by}</span>}
                    <span className="px-1.5 py-0.5 rounded font-bold text-[8px]" style={{ background: "#F59E0B", color: "#fff" }}>
                      ABIERTO
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Acciones: Cancelar (siempre visible si canEdit) + Cambiar IP ── */}
      {/* v101.17.0 · Cancelar visible y desactivado hasta que haya aprovisionamiento exitoso */}
      {canEdit && (
        <div
          className="rounded-2xl p-4"
          style={{ background: NEU.bgLight, border: `1.5px solid ${NEU.border}`, boxShadow: NEU.shadowUp }}
          data-testid="fase2-action-buttons"
        >
          <div className="flex items-center gap-1.5 mb-3">
            <Workflow size={12} style={{ color: NEU.accent }} />
            <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: NEU.textLabel }}>
              Acciones de Aprovisionamiento
            </span>
          </div>
          <div className="flex gap-3">
            {/* Cancelar aprovisionamiento — v101.17.0 · disabled hasta aprovisionar (flujos 1–6) */}
            <button
              onClick={handleCancel}
              disabled={cancelling || cancelBlocked}
              data-testid="btn-cancel-provision"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              title={cancelBlocked ? "Disponible solo después de aprovisionar el equipo" : undefined}
              style={{
                background: "#FEF2F2",
                color: "#DC2626",
                border: "1.5px solid #FECACA",
              }}
            >
              {cancelling ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
              {cancelling ? "Cancelando..." : "Cancelar Aprovisionamiento"}
            </button>

            {/* v101.89.0 · Desaprovisionar sin OLT: visible tras un delete-ont fallido (solo oficina) */}
            {hasDeleteOntFail && canSkipOlt && !cancelBlocked && (
              <button
                onClick={() => { setSkipOltReason(""); setSkipOltConfirm(false); setSkipOlt({
                  detail: "La OLT no pudo eliminar la ONT en un intento anterior (ver tickets). La OT sigue aprovisionada." }); }}
                disabled={cancelling}
                data-testid="btn-skip-olt"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40"
                style={{ background: "#FFF7ED", color: "#C2410C", border: "1.5px solid #FED7AA" }}
              >
                <XCircle size={14} />
                Desaprovisionar sin OLT
              </button>
            )}

            {/* Cambiar IP — solo con aprovisionamiento / infra */}
            <button
              onClick={() => handleOpenChangeIp()}
              disabled={!hasProvision && !isSuperAdmin}
              data-testid="btn-change-ip"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              title={!hasProvision && !isSuperAdmin ? "Disponible solo después de aprovisionar" : undefined}
              style={{
                background: "#EFF6FF",
                color: "#1D4ED8",
                border: "1.5px solid #BFDBFE",
              }}
            >
              <RefreshCw size={14} />
              Cambiar IP {currentIp ? `(${currentIp})` : ""}
            </button>
          </div>
          {/* v92.9.0 · Envío manual a Odoo (pruebas controladas, solo administradores) */}
          {["super_admin", "admin"].includes(user?.role) && (
            <div className="flex gap-3 mt-3">
              <button onClick={() => sendToOdoo("prov")} disabled={!!odooSending || odooProvSent} data-testid="btn-send-prov-odoo"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold disabled:opacity-40"
                style={{ background: odooProvSent ? "#E5E7EB" : "#F0FDF4", color: odooProvSent ? "#6B7280" : "#15803D", border: `1.5px solid ${odooProvSent ? "#D1D5DB" : "#BBF7D0"}` }}>
                {odooSending === "prov" ? <Loader2 size={13} className="animate-spin" /> : odooProvSent ? <Check size={13} /> : <RefreshCw size={13} />}
                {odooProvSent ? "Aprovisionamiento enviado" : "Enviar aprovisionamiento a Odoo"}
              </button>
              <button onClick={() => sendToOdoo("close")} disabled={!!odooSending || odooCloseSent} data-testid="btn-send-close-odoo"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-[11px] font-bold disabled:opacity-40"
                style={{ background: odooCloseSent ? "#E5E7EB" : "#F0FDF4", color: odooCloseSent ? "#6B7280" : "#15803D", border: `1.5px solid ${odooCloseSent ? "#D1D5DB" : "#BBF7D0"}` }}>
                {odooSending === "close" ? <Loader2 size={13} className="animate-spin" /> : odooCloseSent ? <Check size={13} /> : <RefreshCw size={13} />}
                {odooCloseSent ? "Cierre enviado" : "Enviar cierre a Odoo"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Modal: Cambiar IP ── */}
      {showChangeIp && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !changingIp && setShowChangeIp(false)}
          data-testid="change-ip-modal-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
            data-testid="change-ip-modal"
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between" style={{ background: "#EFF6FF", borderBottom: "1px solid #BFDBFE" }}>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#1E40AF" }}>
                  CAMBIAR IP DEL CLIENTE
                </div>
                <div className="text-sm font-bold" style={{ color: "#1E3A8A" }}>
                  OT #{odooTaskId} {currentIp ? `· IP actual: ${currentIp}` : ""}
                </div>
              </div>
              <button
                onClick={() => setShowChangeIp(false)}
                disabled={changingIp}
                className="text-slate-400 hover:text-slate-700 text-xl px-2"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-3">
              {/* Buscador */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar por IP o VLAN..."
                  value={ipSearch}
                  onChange={(e) => setIpSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 text-xs rounded-xl border outline-none"
                  style={{ borderColor: NEU.border }}
                  data-testid="change-ip-search"
                />
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
              </div>

              {/* Lista de IPs */}
              {freeIpsLoading ? (
                <div className="flex items-center justify-center gap-2 py-8">
                  <Loader2 size={16} className="animate-spin" style={{ color: NEU.accent }} />
                  <span className="text-xs" style={{ color: NEU.textMuted }}>Cargando IPs disponibles...</span>
                </div>
              ) : filteredIps.length === 0 ? (
                <div className="text-center py-8">
                  <Wifi size={28} className="mx-auto mb-2 opacity-30" style={{ color: NEU.textMuted }} />
                  <p className="text-xs" style={{ color: NEU.textMuted }}>
                    No se encontraron IPs libres{ipSearch ? ` para "${ipSearch}"` : ""}
                  </p>
                </div>
              ) : (
                <div className="max-h-[280px] overflow-y-auto rounded-xl border space-y-0" style={{ borderColor: NEU.border }}>
                  {filteredIps.map((ip) => (
                    <button
                      key={`${ip.vlan_id}-${ip.id}`}
                      onClick={() => setSelectedNewIp(ip)}
                      className="w-full text-left px-4 py-2.5 text-xs border-b last:border-b-0 transition-all hover:bg-blue-50 flex items-center justify-between"
                      style={{
                        borderColor: NEU.border,
                        background: selectedNewIp?.id === ip.id && selectedNewIp?.vlan_id === ip.vlan_id ? "#EFF6FF" : "transparent",
                      }}
                      data-testid={`change-ip-option-${ip.id}`}
                    >
                      <div>
                        <span className="font-mono font-bold" style={{ color: NEU.text }}>{ip.name}</span>
                        <span className="ml-2 text-[10px]" style={{ color: NEU.textMuted }}>VLAN: {ip.vlan_name || "—"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedNewIp?.id === ip.id && selectedNewIp?.vlan_id === ip.vlan_id && (
                          <CheckCircle2 size={14} style={{ color: "#059669" }} />
                        )}
                        <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "#DCFCE7", color: "#166534" }}>
                          Libre
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 flex gap-2" style={{ borderTop: `1px solid ${NEU.border}`, background: NEU.bgSurface }}>
              <button
                onClick={() => setShowChangeIp(false)}
                disabled={changingIp}
                className="px-4 py-2 border rounded-xl text-xs font-semibold hover:bg-white disabled:opacity-40"
                style={{ borderColor: NEU.border }}
              >
                Cancelar
              </button>
              <button
                onClick={handleChangeIp}
                disabled={!selectedNewIp || changingIp}
                data-testid="btn-confirm-change-ip"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40"
                style={{ background: changingIp ? NEU.textMuted : "#1D4ED8" }}
              >
                {changingIp ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                {changingIp
                  ? "Cambiando..."
                  : selectedNewIp
                    ? `Cambiar a ${selectedNewIp.name}`
                    : "Seleccione una IP"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════
   FASE 6B · RadioEnlaceWizard — Provisioning Radio Enlace (flujos 4/5/6)
   Nunca llama OLT ni Axxess.
   ═══════════════════════════════════════════════════════════════════════════ */
function RadioEnlaceWizard({ odooTaskId, canEdit, internalStatus, onDone, provisioned }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Flow detection
  const [flowData, setFlowData] = useState(null);
  const [infraList, setInfraList] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [freeIps, setFreeIps] = useState([]);
  const [vlans, setVlans] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Form fields
  const [antenaSn, setAntenaSn] = useState("");
  const [routerSn, setRouterSn] = useState("");
  // v101.83.0 · Axxess no aplica a Radio Enlace (solo fibra Residencial / Mi Negocio)
  const [conectadoAId, setConectadoAId] = useState("");
  const [vlanId, setVlanId] = useState("");
  const [clientIp, setClientIp] = useState("");
  const [ponEnlace, setPonEnlace] = useState("");
  const [cpeMac, setCpeMac] = useState("");          // v92.8.1 · MAC del equipo del cliente
  const [curRadio, setCurRadio] = useState(null);    // v101.138.0 · radio base / IP actuales del cliente
  const [conserva, setConserva] = useState(false);   // v101.203.0 · cambio de equipo: se conserva el aprovisionamiento
  // v92.6.2 · Puerto real de la radio base
  const [radioPorts, setRadioPorts] = useState([]);
  const [radioPortId, setRadioPortId] = useState("");
  const [radioPortsMsg, setRadioPortsMsg] = useState("");

  // Filters
  const [infraSearch, setInfraSearch] = useState("");
  const [infraFound, setInfraFound] = useState(null);   // v101.191.0 · resultados de la búsqueda en el servidor
  const [infraSearching, setInfraSearching] = useState(false);
  const [invSearch, setInvSearch] = useState("");

  // v101.191.0 · La búsqueda de radio base consulta al servidor (todas las bases, no solo las 300 más cercanas)
  useEffect(() => {
    const q = (infraSearch || "").trim();
    if (!q || !odooTaskId) { setInfraFound(null); setInfraSearching(false); return; }
    let alive = true;
    setInfraSearching(true);
    const t = setTimeout(() => {
      api.get("/v1/provisioning/radio/bases", { params: { task_id: odooTaskId, q } })
        .then(({ data }) => { if (alive) setInfraFound(data?.items || []); })
        .catch(() => { if (alive) setInfraFound(null); })
        .finally(() => { if (alive) setInfraSearching(false); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [infraSearch, odooTaskId]);

  const isOnSite = ["on_site", "en_sitio", "in_progress", "en_ejecucion"].includes(
    (internalStatus || "").toLowerCase()
  );
  const gate = useMaterialsGate(odooTaskId);   // v97.4.0 · materiales primero

  useEffect(() => {
    if (!odooTaskId) return;
    api.get(`/v1/provisioning/${odooTaskId}/status`)
      .then(({ data }) => {
        setFlowData(data?.flow);
        // Flujo 5 (traslado RE): precargar seriales actuales
        if (data?.flow?.flujo === 5) {
          setAntenaSn(data?.pe_cpe_serial || "");
        }
        // v101.135.0 · Traslado / cambio de equipo radio: se envía lo mismo que tiene el cliente (precarga)
        if ([5, 6].includes(data?.flow?.flujo)) {
          api.get(`/v1/provisioning/radio/datos-actuales/${odooTaskId}`).then(({ data: cur }) => {
            setCurRadio(cur || null);   // v101.138.0 · base e IP actuales del cliente
            if (!cur?.enabled) return;
            if (data?.flow?.flujo === 5 && cur.antena_serial) setAntenaSn(cur.antena_serial);
            if (cur.router_serial) setRouterSn(cur.router_serial);
            if (cur.cpe_mac) setCpeMac(cur.cpe_mac);
            // v101.203.0 · Cambio de equipo: radio base, puerto, VLAN e IP del cliente fijos; solo cambia el equipo
            if (data?.flow?.flujo === 6 && cur.cambio_conserva && cur.conserva) {
              setConserva(true);
              setConectadoAId(String(cur.conectado_a_id));
              setRadioPortId(String(cur.radio_port_id));
              setVlanId(String(cur.current_vlan_id));
              setClientIp(cur.current_ip);
            }
          }).catch(() => {});
        }
      })
      .catch(() => {});
  }, [odooTaskId]);

  const flujo = flowData?.flujo;
  const isRE = [4, 5, 6].includes(flujo);
  // v101.138.0 · Router obligatorio solo en instalación; en traslado / cambio se conserva el del cliente
  const routerRequired = flujo === 4;
  // v101.17.0 · Auto-descargo solo instalación radio (flujo 4)
  const occupyRadioSerial = async (serial, kind) => {
    const sn = String(serial || "").trim().toUpperCase();
    if (!sn || flujo !== 4) return;
    try {
      await api.post(`/v1/provisioning/occupy-material-serial/${odooTaskId}`, {
        serial: sn, category_hint: kind, kind,
      });
      gate.check();
    } catch (_) { /* no bloquear */ }
  };

  if (!isRE || !canEdit || !isOnSite || (provisioned && !open)) return null;   // v93.5.0

  const loadData = async () => {
    if (infraList.length > 0) return; // already loaded
    setDataLoading(true);
    try {
      // v101.19.0 · VLANs filtradas por OT (sucursal + plan + client_type residential|corporate)
      const [infraRes, invRes, vlansRes] = await Promise.all([
        api.get("/v1/provisioning/radio/bases", { params: { task_id: odooTaskId } }),
        api.get(`/v1/provisioning/radio/inventory/${odooTaskId}`),
        api.get("/v1/provisioning/vlans-disponibles", { params: { odoo_task_id: odooTaskId } }),
      ]);
      setInfraList(infraRes.data?.items || []);
      setInventory(invRes.data?.inventory || []);
      setVlans(vlansRes.data?.items || []);
    } catch { /* ignore */ }
    finally { setDataLoading(false); }
  };

  const loadIps = async (vid) => {
    if (!vid) return;
    try {
      const { data } = await api.get(`/v1/provisioning/ips-libres/${vid}`);
      setFreeIps(data.items || []);
    } catch { setFreeIps([]); }
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open) { loadData(); gate.check(); }
  };

  const handleVlanChange = (v) => {
    setVlanId(v);
    setClientIp("");
    loadIps(v);
  };

  // v92.6.2 · Al elegir la radio base se cargan sus puertos reales
  const handleSelectBase = async (baseId) => {
    setConectadoAId(String(baseId));
    setRadioPortId(""); setRadioPorts([]); setRadioPortsMsg("");
    try {
      const { data } = await api.get(`/v1/provisioning/radio/bases/${baseId}/ports`, { params: { task_id: odooTaskId } });   // v101.135.0
      const items = data?.items || [];
      setRadioPorts(items);
      if (!items.length) setRadioPortsMsg(data?.message || "La radio base no tiene puertos registrados en Odoo.");
    } catch (e) {
      setRadioPortsMsg(e?.response?.data?.detail || "No se pudieron cargar los puertos de la radio base.");
    }
  };

  // v101.138.0 · Misma radio base que hoy → se puede conservar la IP actual del cliente
  const _baseKey = (n) => String(n || "").split("|")[0].trim().toUpperCase();
  const selectedBaseName = ([...(infraFound || []), ...infraList].find(i => String(i.id) === String(conectadoAId)) || {}).name;
  const sameBase = [5, 6].includes(flujo) && !!curRadio && !!conectadoAId && (
    (curRadio.conectado_a_id != null && String(curRadio.conectado_a_id) === String(conectadoAId)) ||
    (curRadio.base_names || []).some(n => _baseKey(n) && _baseKey(n) === _baseKey(selectedBaseName)));
  const canKeepIp = sameBase && !!curRadio?.current_ip;
  const useCurrentIp = () => {
    if (!canKeepIp) return;
    const vid = curRadio.current_vlan_id != null ? String(curRadio.current_vlan_id) : vlanId;
    if (vid && vid !== vlanId) { setVlanId(vid); loadIps(vid); }
    setClientIp(curRadio.current_ip);
  };
  const vlanOptions = (canKeepIp && curRadio.current_vlan_id != null && !vlans.some(v => String(v.id) === String(curRadio.current_vlan_id)))
    ? [{ id: curRadio.current_vlan_id, name: "VLAN actual del cliente", free_ips: 0 }, ...vlans] : vlans;
  const ipOptions = (canKeepIp && String(vlanId) === String(curRadio.current_vlan_id) && !freeIps.some(i => i.name === curRadio.current_ip))
    ? [{ id: "actual", name: curRadio.current_ip, actual: true }, ...freeIps] : freeIps;

  const _infraLocal = infraList.filter(i =>
    !infraSearch || `${i.name || ""} ${i.ip_address || ""}`.toLowerCase().includes(infraSearch.trim().toLowerCase())
  );
  // v101.191.0 · Con texto: resultados del servidor (+ coincidencias locales); la base actual del cliente siempre arriba
  const filteredInfra = (infraSearch.trim() && infraFound)
    ? [...infraFound, ..._infraLocal.filter(i => !infraFound.some(f => String(f.id) === String(i.id)))]
    : _infraLocal;
  const filteredInv = inventory.filter(i =>
    !invSearch || (i.serial || "").toLowerCase().includes(invSearch.toLowerCase()) ||
    (i.product_name || "").toLowerCase().includes(invSearch.toLowerCase())
  );

  const handleProvision = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await api.post("/v1/provisioning/provision/radio", {
        odoo_task_id: odooTaskId,
        antena_serial: antenaSn,
        router_serial: routerSn,
        conectado_a_id: parseInt(conectadoAId),
        radio_port_id: radioPortId ? parseInt(radioPortId) : null,
        radio_port_number: radioPorts.find(p => String(p.id) === String(radioPortId))?.port_number ?? null,
        vlan_id: vlanId,
        client_ip: clientIp,
        pon_enlace: ponEnlace || null,
        cpe_mac: cpeMac.trim(),
      });
      setResult(data);
      toast.success(`Radio Enlace flujo ${data.flujo} completado`);
      if (onDone) onDone();
    } catch (err) {
      // v101.207.0 · Mostrar el motivo real (antes «Error en provisioning radio» sin detalle)
      const _d = err.response?.data?.detail;
      const msg = typeof _d === "string" ? _d
        : _d ? JSON.stringify(_d).slice(0, 300)
        : `Error en provisioning radio (${err.response?.status ? `HTTP ${err.response.status}` : "sin respuesta del servidor"}${typeof err.response?.data === "string" && err.response.data ? `: ${err.response.data.slice(0, 160)}` : ""})`;
      setError(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const flujoLabel = { 4: "Instalación RE", 5: "Traslado RE", 6: "Cambio Equipo RE" }[flujo] || "Radio";
  const isTraslado = flujo === 5;
  const isCambio = flujo === 6;

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: "#EFF6FF", border: "1.5px solid #BFDBFE", boxShadow: "0 2px 8px rgba(37,99,235,.08)" }}
      data-testid="radio-enlace-wizard"
    >
      <div
        className="flex items-center justify-between px-5 py-3 cursor-pointer"
        onClick={handleOpen}
        style={{ borderBottom: open ? "1px solid #BFDBFE" : "none" }}
      >
        <div className="flex items-center gap-2">
          <Wifi size={16} style={{ color: "#2563EB" }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#1E40AF" }}>
            {flujoLabel} (Flujo {flujo})
          </span>
        </div>
        <span style={{ color: "#2563EB" }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="px-5 py-4 space-y-3">
          {gate.blocked && <MaterialsBlockedBanner odooTaskId={odooTaskId} onReady={(d) => gate.setMc(d)} />}
          {dataLoading ? (
            <div className="flex justify-center py-4"><Loader2 size={16} className="animate-spin text-blue-500" /></div>
          ) : result ? (
            /* ── Resultado exitoso ── */
            <div className="p-3 rounded-xl space-y-2" style={{ background: "#ECFDF5", border: "1.5px solid #A7F3D0" }}
              data-testid="radio-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} style={{ color: "#059669" }} />
                <span className="text-xs font-bold" style={{ color: "#065F46" }}>{flujoLabel} completado</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[10px]">
                <div><span className="text-gray-500">Antena:</span> <b className="font-mono">{result.antena_serial}</b></div>
                <div><span className="text-gray-500">Router:</span> <b className="font-mono">{result.router_serial}</b></div>
                <div><span className="text-gray-500">Conectado a:</span> <b>{result.conectado_a}</b></div>
                <div><span className="text-gray-500">IP:</span> <b className="font-mono">{result.client_ip}</b></div>
                <div><span className="text-gray-500">VLAN:</span> <b>{result.vlan_tag}</b></div>
                {result.complete_cpe_dispatched && (
                  <div className="col-span-2 text-[9px] text-green-700">complete_cpe enviado</div>
                )}
                {result.rn8_warning && (
                  <div className="col-span-2 p-1.5 rounded bg-amber-50 text-amber-800 text-[10px]">
                    <AlertTriangle size={10} className="inline mr-1" /> {result.rn8_warning}
                  </div>
                )}
                {result.rn13_alert && (
                  <div className="col-span-2 p-1.5 rounded bg-red-50 text-red-800 text-[10px]">
                    <AlertTriangle size={10} className="inline mr-1" />
                    RN13: {result.rn13_alert.product_name} ({result.rn13_alert.serial})
                    — {result.rn13_alert.is_already_invoiced ? "Facturado (OK)" : "NO facturado — devolución"}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* ── Formulario ── */
            <>
              {/* Antena serial */}
              <div>
                <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>
                  Antena (serial) {isTraslado && "(bloqueado)"}
                </label>
                {isTraslado ? (
                  <input value={antenaSn} readOnly className="w-full px-3 py-2 rounded-xl text-xs border bg-gray-100 font-mono"
                    data-testid="input-antena-sn" />
                ) : (
                  <>
                    <input value={antenaSn} onChange={e => setAntenaSn(e.target.value)}
                      placeholder="Serial antena (del inventario)"
                      className="w-full px-3 py-2 rounded-xl text-xs border font-mono outline-none"
                      style={{ borderColor: "#BFDBFE" }}
                      data-testid="input-antena-sn" />
                    {/* Inventory picker for antenna */}
                    {inventory.filter(i => (i.category || "").toUpperCase().includes("MIKROTIK")).length > 0 && (
                      <div className="mt-1 max-h-[100px] overflow-y-auto rounded-lg border text-[10px]" style={{ borderColor: "#DBEAFE" }}>
                        {inventory.filter(i => (i.category || "").toUpperCase().includes("MIKROTIK")).map((item, idx) => (
                          <button key={idx} onClick={() => { setAntenaSn(item.serial); occupyRadioSerial(item.serial, "Antena"); }}
                            className={`w-full text-left px-2 py-1.5 border-b last:border-b-0 hover:bg-blue-50 ${antenaSn === item.serial ? 'bg-blue-50' : ''}`}
                            data-testid={`pick-antena-${idx}`}>
                            <span className="font-mono font-bold">{item.serial}</span>
                            <span className="ml-1 text-gray-400">{item.product_name?.slice(0, 25)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Router serial */}
              <div>
                <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>
                  Router (serial){!routerRequired && <span className="normal-case font-normal text-gray-500"> · opcional: en traslado / cambio se conserva el router del cliente</span>}
                </label>
                <input value={routerSn} onChange={e => setRouterSn(e.target.value)}
                  placeholder="Serial router WiFi (del inventario)"
                  className="w-full px-3 py-2 rounded-xl text-xs border font-mono outline-none"
                  style={{ borderColor: "#BFDBFE" }}
                  data-testid="input-router-sn" />
                {inventory.filter(i => (i.category || "").toUpperCase().includes("ROUTER")).length > 0 && (
                  <div className="mt-1 max-h-[100px] overflow-y-auto rounded-lg border text-[10px]" style={{ borderColor: "#DBEAFE" }}>
                    {inventory.filter(i => (i.category || "").toUpperCase().includes("ROUTER")).map((item, idx) => (
                      <button key={idx} onClick={() => { setRouterSn(item.serial); occupyRadioSerial(item.serial, "Router"); }}
                        className={`w-full text-left px-2 py-1.5 border-b last:border-b-0 hover:bg-blue-50 ${routerSn === item.serial ? 'bg-blue-50' : ''}`}
                        data-testid={`pick-router-${idx}`}>
                        <span className="font-mono font-bold">{item.serial}</span>
                        <span className="ml-1 text-gray-400">{item.product_name?.slice(0, 25)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>


              {/* v101.203.0 · Cambio de equipo: se conserva el aprovisionamiento del cliente */}
              {conserva ? (
                <div className="p-3 rounded-xl space-y-1.5" style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0" }}
                  data-testid="radio-cambio-conserva">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: "#166534" }}>
                    <CheckCircle2 size={13} /> Se conserva el aprovisionamiento del cliente — solo cambia el equipo
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]" style={{ color: "#14532D" }}>
                    <div><span className="text-gray-500">Radio base:</span> <b>{curRadio?.conectado_a_name || `#${curRadio?.conectado_a_id}`}</b></div>
                    <div><span className="text-gray-500">Puerto:</span> <b className="font-mono">{curRadio?.radio_port_number ?? curRadio?.radio_port_name ?? `#${curRadio?.radio_port_id}`}</b></div>
                    <div><span className="text-gray-500">VLAN:</span> <b>{curRadio?.current_vlan_name || curRadio?.current_vlan_id}</b></div>
                    <div><span className="text-gray-500">IP:</span> <b className="font-mono">{curRadio?.current_ip}</b></div>
                  </div>
                </div>
              ) : (
              <>
              {isCambio && curRadio?.cambio_conserva && !curRadio?.conserva && (
                <div className="p-2.5 rounded-xl text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }}
                  data-testid="radio-cambio-sin-datos">
                  No se encontraron completos los datos actuales del cliente (radio base, puerto, VLAN e IP).
                  Selecciónelos con los que tiene HOY el cliente: en el cambio de equipo solo cambia el equipo.
                </div>
              )}
              {/* Conectado a (infraestructura) */}
              <div>
                <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>
                  Radio base (conectado a)
                </label>
                <input value={infraSearch} onChange={e => setInfraSearch(e.target.value)}
                  placeholder="Buscar radio base..."
                  className="w-full px-3 py-2 rounded-xl text-xs border outline-none mb-1"
                  style={{ borderColor: "#BFDBFE" }}
                  data-testid="input-infra-search" />
                <div className="max-h-[120px] overflow-y-auto rounded-lg border text-[10px]" style={{ borderColor: "#DBEAFE" }}>
                  {filteredInfra.slice(0, 30).map(item => (
                    <button key={item.id}
                      onClick={() => handleSelectBase(item.id)}
                      className={`w-full text-left px-2 py-1.5 border-b last:border-b-0 hover:bg-blue-50 flex items-center justify-between ${String(conectadoAId) === String(item.id) ? 'bg-blue-100' : ''}`}
                      data-testid={`infra-${item.id}`}>
                      <div>
                        <span className="font-bold">{item.name}</span>
                        <span className="ml-1 text-gray-400">{item.equipment_type}</span>
                        {item.parent_name && <span className="ml-1 text-blue-400">← {item.parent_name}</span>}
                        {item.current && <span className="ml-1 px-1 rounded bg-emerald-100 text-emerald-700 font-bold">base actual del cliente</span>}
                        {item.warning && <span className="ml-1 text-amber-600">· {item.warning}</span>}
                      </div>
                      {String(conectadoAId) === String(item.id) && <CheckCircle2 size={12} className="text-blue-600" />}
                    </button>
                  ))}
                  {filteredInfra.length === 0 && <div className="px-2 py-3 text-gray-400 text-center">
                    {infraSearching ? "Buscando…"
                      : infraSearch.trim() ? `No se encontró una radio base con «${infraSearch.trim()}» en el espejo de Odoo (activa y de tipo radio).`
                      : "Sin radio bases en el espejo de Odoo."}
                  </div>}
                </div>
              </div>

              {/* v92.6.2 · Puerto real de la radio base */}
              {conectadoAId && (
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>
                    Puerto de la radio base
                  </label>
                  {radioPortsMsg && (
                    <div className="px-2 py-2 mb-1 text-[10px] rounded-lg" style={{ background: "#FEF3C7", color: "#92400E" }}
                      data-testid="radio-ports-msg">{radioPortsMsg}</div>
                  )}
                  <div className="flex flex-wrap gap-1">
                    {radioPorts.map(p => {
                      const disabled = !p.is_free && !(flujo === 5 || flujo === 6);
                      const sel = String(radioPortId) === String(p.id);
                      return (
                        <button key={p.id} type="button" disabled={disabled} title={p.name}
                          onClick={() => setRadioPortId(String(p.id))}
                          className={`px-2 py-1 rounded-lg border text-[10px] font-mono ${sel ? "bg-blue-600 text-white" : disabled ? "opacity-40 cursor-not-allowed" : "hover:bg-blue-50"}`}
                          data-testid={`radio-port-${p.id}`}>
                          {p.port_number ?? p.name}{!p.is_free ? " · ocupado" : ""}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* VLAN + IP */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>VLAN</label>
                  <select value={vlanId} onChange={e => handleVlanChange(e.target.value)}
                    className="w-full px-2 py-2 rounded-xl text-xs border outline-none"
                    style={{ borderColor: "#BFDBFE" }}
                    data-testid="select-vlan-re">
                    <option value="">Seleccionar VLAN...</option>
                    {vlanOptions.map(v => (
                      <option key={v.id} value={v.id}>{v.name} ({v.free_ips || 0} libres)</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>IP Cliente</label>
                  <select value={clientIp} onChange={e => setClientIp(e.target.value)}
                    className="w-full px-2 py-2 rounded-xl text-xs border outline-none"
                    style={{ borderColor: "#BFDBFE" }}
                    data-testid="select-ip-re"
                    disabled={!vlanId}>
                    <option value="">Seleccionar IP...</option>
                    {ipOptions.map(ip => (
                      <option key={ip.id || ip.name} value={ip.name}>{ip.actual ? `${ip.name} · IP actual del cliente` : ip.name}</option>
                    ))}
                  </select>
                  {canKeepIp && clientIp !== curRadio.current_ip && (
                    <button type="button" onClick={useCurrentIp} className="mt-1 text-[10px] text-blue-700 underline" data-testid="btn-keep-ip-re">
                      Misma radio base: usar la IP actual del cliente ({curRadio.current_ip})
                    </button>
                  )}
                </div>
              </div>
              </>
              )}

              {/* v92.8.1 · MAC del equipo del cliente (obligatoria, se envía a Odoo como PON/MAC) */}
              <div>
                <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>
                  MAC del equipo del cliente *
                </label>
                <input value={cpeMac} onChange={e => setCpeMac(e.target.value)}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  className="w-full px-3 py-2 rounded-xl text-xs border outline-none font-mono"
                  style={{ borderColor: cpeMac && cpeMac.replace(/[^0-9a-f]/gi, "").length !== 12 ? "#F87171" : "#BFDBFE" }}
                  data-testid="input-cpe-mac" />
              </div>

              {/* PON Enlace opcional */}
              <div>
                <label className="text-[9px] font-bold uppercase tracking-wider block mb-1" style={{ color: "#374151" }}>
                  PON / Enlace (opcional)
                </label>
                <input value={ponEnlace} onChange={e => setPonEnlace(e.target.value)}
                  placeholder="Enlace punto a punto (opcional)"
                  className="w-full px-3 py-2 rounded-xl text-xs border outline-none"
                  style={{ borderColor: "#BFDBFE" }}
                  data-testid="input-pon-enlace" />
              </div>

              {/* Error */}
              {error && (
                <div className="p-2.5 rounded-xl text-xs" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626" }}>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                onClick={handleProvision}
                disabled={loading || gate.blocked || !antenaSn || (routerRequired && !routerSn) || !conectadoAId || !radioPortId || !vlanId || !clientIp || cpeMac.replace(/[^0-9a-f]/gi, "").length !== 12}
                data-testid="btn-radio-provision"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40"
                style={{ background: loading ? "#9CA3AF" : "#2563EB" }}
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Wifi size={14} />}
                {loading ? "Aprovisionando..." : `Aprovisionar ${flujoLabel}`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   FASE 7 · VerificacionTecnica — RN15 Reconciliación de equipos
   ═══════════════════════════════════════════════════════════════════════════ */
function VerificacionTecnica({ odooTaskId, ot, canEdit, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [returningEq, setReturningEq] = useState(null);

  // RN7 equipment types the tech can check
  const EQUIPMENT_OPTIONS = ["ONT", "ONT-WiFi", "Router", "Cable_UTP", "Cable_Poder", "Splitter", "Roseta"];
  const [checked, setChecked] = useState({});

  const segment = (ot?.segment_name || "").toLowerCase();
  const isCorporate = segment.includes("empresa");
  const status = (ot?.smart?.internal_status || "").toLowerCase();
  const isOnSite = ["on_site", "en_sitio", "in_progress", "en_ejecucion"].includes(status);

  // Determine flow
  const [flow, setFlow] = useState(null);
  useEffect(() => {
    if (!odooTaskId) return;
    api.get(`/v1/provisioning/${odooTaskId}/status`)
      .then(({ data }) => setFlow(data?.flow))
      .catch(() => {});
  }, [odooTaskId]);

  const flowNum = flow?.flujo;
  // Show button: on_site + Residencial/Mi Negocio + flow == 7 + NOT corporate
  const showButton = canEdit && isOnSite && !isCorporate && flowNum === 7;
  // Also show as final step for F1-F6
  const showAsStep = canEdit && isOnSite && !isCorporate && flowNum >= 1 && flowNum <= 6;

  if (!showButton && !showAsStep) return null;

  const toggle = (eq) => setChecked(prev => ({ ...prev, [eq]: !prev[eq] }));

  const handleVerify = async () => {
    setLoading(true);
    const present = Object.keys(checked).filter(k => checked[k]);
    try {
      const { data } = await api.post("/v1/provisioning/provision/verify-equipment", {
        odoo_task_id: odooTaskId,
        equipment_present: present,
      });
      setResult(data);
      if (data.action === "sin_excedente") toast.success("Verificación OK — sin excedente");
      else toast.info(`Excedente detectado: ${data.excess.join(", ")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en verificación");
    } finally { setLoading(false); }
  };

  const handleReturn = async (eq) => {
    setReturningEq(eq);
    try {
      await api.post("/v1/provisioning/provision/verify-equipment/return", {
        odoo_task_id: odooTaskId, equipment: eq,
      });
      toast.success(`Devolución de ${eq} registrada`);
      setResult(prev => ({
        ...prev,
        excess_detail: prev.excess_detail.map(e =>
          e.equipment === eq ? { ...e, action_required: "devuelto" } : e
        ),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar devolución");
    } finally { setReturningEq(null); }
  };

  const handleTicket = async (eq) => {
    try {
      await api.post("/v1/provisioning/provision/verify-equipment/ticket", {
        odoo_task_id: odooTaskId, equipment: eq,
      });
      toast.info(`Ticket de regularización creado para ${eq}`);
      setResult(prev => ({
        ...prev,
        excess_detail: prev.excess_detail.map(e =>
          e.equipment === eq ? { ...e, action_required: "ticket_creado" } : e
        ),
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear ticket");
    }
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: "#F0FDF4", border: "1.5px solid #BBF7D0", boxShadow: "0 2px 8px rgba(22,163,74,.08)" }}
      data-testid="verificacion-tecnica"
    >
      <div
        className="flex items-center justify-between px-5 py-3 cursor-pointer"
        onClick={() => setOpen(!open)}
        style={{ borderBottom: open ? "1px solid #BBF7D0" : "none" }}
      >
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} style={{ color: "#16A34A" }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#166534" }}>
            {showButton ? "Verificación Técnica" : "RN15 · Reconciliación de Equipos"}
          </span>
        </div>
        <span style={{ color: "#16A34A" }}>{open ? "▲" : "▼"}</span>
      </div>

      {open && (
        <div className="px-5 py-4 space-y-4">
          {/* v96.3.0 · Equipos que el cliente tiene en su poder según Alfanet */}
          <ClientEquipmentCard odooTaskId={odooTaskId} autoLoad hint="Equipos registrados a nombre del cliente: verifique y retire los que correspondan." />
          {!result ? (
            <>
              <p className="text-xs" style={{ color: "#166534" }}>
                Marque los equipos que el técnico tiene físicamente presentes en sitio:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {EQUIPMENT_OPTIONS.map(eq => (
                  <label
                    key={eq}
                    className="flex items-center gap-2 p-2.5 rounded-xl cursor-pointer transition-all text-xs font-medium"
                    style={{
                      background: checked[eq] ? "#DCFCE7" : "#fff",
                      border: `1.5px solid ${checked[eq] ? "#4ADE80" : "#D1D5DB"}`,
                    }}
                    data-testid={`check-eq-${eq}`}
                  >
                    <input
                      type="checkbox"
                      checked={!!checked[eq]}
                      onChange={() => toggle(eq)}
                      className="w-4 h-4 rounded accent-green-600"
                    />
                    <span style={{ color: checked[eq] ? "#166534" : "#374151" }}>{eq.replace(/_/g, " ")}</span>
                  </label>
                ))}
              </div>
              <button
                onClick={handleVerify}
                disabled={loading || Object.values(checked).filter(Boolean).length === 0}
                data-testid="btn-verify-equipment"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40"
                style={{ background: loading ? "#9CA3AF" : "#16A34A" }}
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                {loading ? "Verificando..." : "Verificar Equipos"}
              </button>
            </>
          ) : (
            <>
              {/* Result */}
              <div className="p-3 rounded-xl" style={{
                background: result.action === "sin_excedente" ? "#DCFCE7" : "#FEF3C7",
                border: `1px solid ${result.action === "sin_excedente" ? "#A7F3D0" : "#FDE68A"}`,
              }}>
                <div className="flex items-center gap-2 text-xs font-bold" style={{
                  color: result.action === "sin_excedente" ? "#166534" : "#92400E"
                }}>
                  {result.action === "sin_excedente"
                    ? <><CheckCircle2 size={14} /> Sin excedente — todo OK</>
                    : <><AlertTriangle size={14} /> Excedente detectado</>
                  }
                </div>
                <div className="mt-1 text-[11px]" style={{ color: "#374151" }}>
                  <span className="font-medium">Esperado:</span> {result.expected.join(", ")} &nbsp;|&nbsp;
                  <span className="font-medium">Presente:</span> {result.present.join(", ")}
                  {result.excess.length > 0 && (
                    <> &nbsp;|&nbsp; <span className="font-bold text-amber-700">Excedente:</span> {result.excess.join(", ")}</>
                  )}
                </div>
              </div>

              {/* Excess detail with actions */}
              {result.excess_detail?.filter(e => e.action_required !== "ninguna" && e.action_required !== "devuelto" && e.action_required !== "ticket_creado").map(ex => (
                <div key={ex.equipment} className="p-3 rounded-xl" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
                  data-testid={`excess-${ex.equipment}`}>
                  <div className="text-xs font-bold" style={{ color: "#92400E" }}>
                    {ex.equipment} — {ex.is_already_invoiced === false ? "No facturado" : "Sin datos de facturación"}
                  </div>
                  {ex.is_already_invoiced !== true && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleReturn(ex.equipment)}
                        disabled={returningEq === ex.equipment}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold text-white"
                        style={{ background: returningEq === ex.equipment ? "#9CA3AF" : "#16A34A" }}
                        data-testid={`btn-return-${ex.equipment}`}
                      >
                        {returningEq === ex.equipment ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                        Devolver a bodega
                      </button>
                      <button
                        onClick={() => handleTicket(ex.equipment)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-bold"
                        style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A" }}
                        data-testid={`btn-ticket-${ex.equipment}`}
                      >
                        <AlertTriangle size={12} />
                        Ticket regularización
                      </button>
                    </div>
                  )}
                </div>
              ))}

              {/* Already handled items */}
              {result.excess_detail?.filter(e => e.action_required === "devuelto" || e.action_required === "ticket_creado").map(ex => (
                <div key={ex.equipment} className="p-2.5 rounded-xl flex items-center gap-2 text-xs"
                  style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                  <CheckCircle2 size={14} style={{ color: "#16A34A" }} />
                  <span style={{ color: "#166534" }}>{ex.equipment}: {ex.action_required === "devuelto" ? "Devolución registrada" : "Ticket creado"}</span>
                </div>
              ))}

              <button onClick={() => { setResult(null); setChecked({}); }}
                className="text-[11px] font-semibold px-3 py-1.5 rounded-lg"
                style={{ color: "#16A34A", background: "#F0FDF4" }}
                data-testid="btn-reset-verify"
              >
                Reiniciar verificación
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// v101.22.0 · Timeline historial suscripción (Odoo + cache IA) · scroll fijo bajo el mapa
function SubscriptionAiTimeline({ odooTaskId, subscriptionCode, canRebuild }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!odooTaskId || Number.isNaN(odooTaskId)) return;
    if (force) setRebuilding(true);
    else setLoading(true);
    setErr(null);
    try {
      const url = force
        ? `/v1/ot_smart/${odooTaskId}/subscription-timeline/rebuild`
        : `/v1/ot_smart/${odooTaskId}/subscription-timeline`;
      const { data: d } = force ? await api.post(url) : await api.get(url);
      setData(d);
      if (d && d.ok === false) setErr(d.message || d.error || "Sin timeline");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Error al cargar timeline");
      setData(null);
    } finally {
      setLoading(false);
      setRebuilding(false);
    }
  }, [odooTaskId]);

  useEffect(() => {
    load(false);
  }, [load]);

  const tl = data?.timeline || {};
  const items = Array.isArray(tl.items) ? tl.items : [];
  const subLabel = data?.subscription_code || subscriptionCode || "—";

  return (
    <NeuCard title="Historial suscripción" icon={History} testid="card-subscription-timeline">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[10px]" style={{ color: NEU.textMuted }}>
          Contrato <span className="font-mono font-semibold" style={{ color: NEU.text }}>{subLabel}</span>
          {data?.from_cache ? " · cache" : data?.source ? ` · ${data.source}` : ""}
          {data?.generated_at ? ` · ${new Date(data.generated_at).toLocaleString("es-EC")}` : ""}
        </div>
        <div className="flex items-center gap-1">
          {canRebuild && (
            <button
              type="button"
              onClick={() => load(true)}
              disabled={rebuilding || loading}
              className="text-[10px] px-2 py-1 rounded-lg font-semibold flex items-center gap-1"
              style={{ background: NEU.bgSurface, color: NEU.text, border: `1px solid ${NEU.border}` }}
              data-testid="btn-rebuild-sub-timeline"
              title="Regenerar timeline"
            >
              <RefreshCw size={12} className={rebuilding ? "animate-spin" : ""} />
              Regenerar
            </button>
          )}
        </div>
      </div>

      {/* v101.25.0 · Odoo sin respuesta: se muestra la última versión guardada con aviso */}
      {!loading && data?.stale && data?.warning && (
        <div className="text-[11px] rounded-xl p-2.5 mb-2" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="sub-timeline-stale">
          {data.warning}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-xs py-6 justify-center" style={{ color: NEU.textMuted }}>
          <Loader2 size={16} className="animate-spin" /> Construyendo / leyendo cache…
        </div>
      )}

      {!loading && err && (
        <div className="text-[11px] rounded-xl p-3" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="sub-timeline-error">
          {err}
        </div>
      )}

      {!loading && !err && data?.ok && (
        <>
          {tl.current_summary && (
            <div
              className="text-[11px] leading-relaxed rounded-xl p-2.5 mb-3"
              style={{ background: NEU.bgSurface, color: NEU.text }}
              data-testid="sub-timeline-summary"
            >
              {tl.current_summary}
            </div>
          )}

          {/* Scroll fijo: no estira la ventana */}
          <div
            className="relative overflow-y-auto pr-1"
            style={{
              maxHeight: 280,
              borderTop: `1px solid ${NEU.border}`,
              borderBottom: `1px solid ${NEU.border}`,
            }}
            data-testid="sub-timeline-scroll"
          >
            {items.length === 0 ? (
              <div className="text-[11px] italic py-4 text-center" style={{ color: NEU.textMuted }}>
                Sin actividades en Odoo para esta suscripción
              </div>
            ) : (
              <div className="pl-3 py-2 space-y-0">
                {items.map((it, idx) => {
                  const open = it.is_current || !it.closed;
                  return (
                    <div
                      key={it.odoo_task_id || idx}
                      className="relative flex gap-3 pb-3 last:pb-1"
                      data-testid={`sub-timeline-item-${idx}`}
                    >
                      <div
                        className="absolute left-[-10px] top-1.5 w-2.5 h-2.5 rounded-full z-10"
                        style={{
                          background: open ? "#0A3747" : "#94A3B8",
                          boxShadow: idx < items.length - 1 ? `0 12px 0 -4px ${NEU.border}` : undefined,
                        }}
                      />
                      {idx < items.length - 1 && (
                        <div
                          className="absolute left-[-6px] top-4 bottom-0 w-px"
                          style={{ background: NEU.border }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-bold" style={{ color: NEU.text }}>
                            {(it.date || "").slice(0, 10) || "—"}
                          </span>
                          {it.is_current && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold" style={{ background: "#E0E621", color: "#0A3747" }}>
                              ACTUAL
                            </span>
                          )}
                          <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold" style={{ background: NEU.bgSurface, color: NEU.textMuted }}>
                            {it.stage_label || (it.closed ? "Cerrada" : "Abierta")}
                          </span>
                          {it.activity && (
                            <span className="text-[9px] font-semibold" style={{ color: "#0A3747" }}>{it.activity}</span>
                          )}
                        </div>
                        <div className="text-[10px] font-mono mt-0.5" style={{ color: NEU.textMuted }}>
                          #{it.task_number || it.odoo_task_id}
                          {it.name ? ` · ${it.name}` : ""}
                        </div>
                        {it.issue && (
                          <div className="text-[11px] mt-1 leading-snug" style={{ color: NEU.text }}>{it.issue}</div>
                        )}
                        {it.tech_notes && (
                          <div className="text-[10px] mt-0.5" style={{ color: NEU.textMuted }}>
                            Obs.: {it.tech_notes}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px]" style={{ color: NEU.textMuted }}>
                          {(it.phones || []).length > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Phone size={10} /> {(it.phones || []).join(" · ")}
                            </span>
                          )}
                          {it.technician && (
                            <span className="inline-flex items-center gap-1">
                              <User size={10} /> {it.technician}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {tl.stats && (
            <div className="text-[9px] mt-2" style={{ color: NEU.textMuted }} data-testid="sub-timeline-stats">
              Odoo: {tl.stats.total_odoo ?? data?.total_odoo ?? "—"} · mostradas: {tl.stats.shown ?? items.length}
              {typeof tl.stats.closed === "number" ? ` · cerradas: ${tl.stats.closed}` : ""}
            </div>
          )}
        </>
      )}
    </NeuCard>
  );
}

function ProvisioningTimeline({ odooTaskId }) {
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v1/provisioning/${odooTaskId}/status`);
      setTimeline(data);
    } catch {
      setTimeline(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odooTaskId]);

  const items = timeline?.timeline || [];
  if (!timeline || (!loading && items.length === 0)) return null;

  const ACTION_META = {
    "discover":                { label: "Discovery ONTs",   color: "#2563EB", icon: Wifi },
    "set-ont":                 { label: "Aprovisionamiento", color: "#059669", icon: Zap },
    "webhook-complete_cpe":    { label: "Webhook Odoo",      color: "#7C3AED", icon: CheckCircle2 },
  };

  const getActionMeta = (action) => ACTION_META[action] || { label: action, color: "#64748B", icon: Clock };

  const formatTs = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("es-EC", { day: "2-digit", month: "short", year: "numeric" }) +
        " " + d.toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch { return iso; }
  };

  const visibleItems = expanded ? items : items.slice(0, 5);

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{
        background: NEU.bgLight,
        boxShadow: NEU.shadowDown,
        border: `1.5px solid ${NEU.border}`,
      }}
      data-testid="provisioning-timeline"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-2">
          <History size={16} style={{ color: NEU.accent }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: NEU.textLabel }}>
            Historial de Aprovisionamiento
          </span>
          {items.length > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-lg font-mono font-bold"
              style={{ background: `${NEU.accent}15`, color: NEU.accent }}>
              {items.length}
            </span>
          )}
        </div>
        <button
          onClick={fetchTimeline}
          disabled={loading}
          data-testid="btn-refresh-timeline"
          className="p-1.5 rounded-lg transition hover:opacity-70"
          style={{ color: NEU.textMuted }}
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
      </div>

      {/* Loading */}
      {loading && items.length === 0 && (
        <div className="flex items-center justify-center gap-2 py-6 px-5">
          <Loader2 size={16} className="animate-spin" style={{ color: NEU.accent }} />
          <span className="text-xs" style={{ color: NEU.textMuted }}>Cargando historial...</span>
        </div>
      )}

      {/* Timeline */}
      {items.length > 0 && (
        <div className="px-5 pb-4">
          <div className="relative pl-5">
            {/* Línea vertical */}
            <div
              className="absolute left-[7px] top-1 bottom-1 w-[2px] rounded-full"
              style={{ background: `${NEU.border}` }}
            />

            {visibleItems.map((item, idx) => {
              const meta = getActionMeta(item.action);
              const Icon = meta.icon;
              const isSuccess = item.status === "success" || item.status === "processed";
              const isError = item.status === "error";

              return (
                <div key={item.id || idx} className="relative flex gap-3 pb-4 last:pb-0" data-testid={`timeline-item-${idx}`}>
                  {/* Dot */}
                  <div
                    className="absolute left-[-13px] top-1 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                    style={{
                      background: isError ? "#FEE2E2" : isSuccess ? "#DCFCE7" : "#EFF6FF",
                      border: `2px solid ${isError ? "#DC2626" : isSuccess ? meta.color : "#94A3B8"}`,
                    }}
                  >
                    <Icon size={8} style={{ color: isError ? "#DC2626" : isSuccess ? meta.color : "#94A3B8" }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-bold" style={{ color: meta.color }}>
                        {meta.label}
                      </span>
                      <span
                        className="text-[9px] px-1.5 py-0.5 rounded font-semibold"
                        style={{
                          background: isError ? "#FEE2E2" : isSuccess ? "#DCFCE7" : "#FEF3C7",
                          color: isError ? "#DC2626" : isSuccess ? "#166534" : "#92400E",
                        }}
                      >
                        {isSuccess ? "OK" : isError ? "ERROR" : item.status?.toUpperCase()}
                      </span>
                      {item.olt_ip && (
                        <span className="text-[9px] font-mono" style={{ color: NEU.textMuted }}>
                          OLT: {item.olt_ip}
                        </span>
                      )}
                      {item.cpe_serial && (
                        <span className="text-[9px] font-mono" style={{ color: NEU.textMuted }}>
                          CPE: {item.cpe_serial}
                        </span>
                      )}
                    </div>
                    {item.error_message && (
                      <div className="text-[10px] mt-0.5 truncate max-w-[400px]" style={{ color: "#DC2626" }}>
                        {item.error_message.slice(0, 120)}{item.error_message.length > 120 ? "..." : ""}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-0.5 text-[9px]" style={{ color: NEU.textMuted }}>
                      <span>{formatTs(item.timestamp)}</span>
                      {item.created_by && <span>por {item.created_by}</span>}
                      {item.evento_id && <span className="font-mono">evt: {item.evento_id.slice(0, 8)}...</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Ver más / menos */}
          {items.length > 5 && (
            <button
              onClick={() => setExpanded(!expanded)}
              data-testid="btn-toggle-timeline"
              className="mt-2 w-full text-center text-[10px] font-semibold py-1.5 rounded-lg transition hover:opacity-80"
              style={{ color: NEU.accent, background: `${NEU.accent}08` }}
            >
              {expanded ? "Mostrar menos" : `Ver ${items.length - 5} eventos más`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
